/**
 * Copilot Studio agent tools.
 *
 * Copilot Studio has no dedicated authoring API. Agents ARE Dataverse rows:
 * the agent is a `bot`, and everything inside it — topics, instructions,
 * knowledge sources, settings, test cases — are `botcomponent` rows whose
 * `data` column holds the authoring source (YAML). These tools wrap that
 * reality so the model doesn't have to rediscover the schema, and so the
 * three traps below can't be stepped on:
 *
 *  1. SHARING DOES NOT CASCADE. Dataverse GrantAccess on the bot row shares
 *     the agent but none of its components — the recipient sees an agent
 *     with no topics. Every component needs its own grant (verified live:
 *     an agent shared bot-row-only showed 0 of 141 components).
 *  2. DELETE IS BOTTOM-UP unless you use the platform's own action. A plain
 *     row delete of a bot with children fails on the FK
 *     `botcomponent_parent_bot`; `PvaDeleteBot` cascades instead (verified
 *     live), so that is the path taken, with the manual order as fallback.
 *  3. PROVISIONING IS ASYNC. A newly created agent has no components for
 *     ~60-90s while the service adds the default topics.
 *
 * Everything here was verified against a live tenant before shipping — an
 * agent was created, authored, published, and then held a real Direct Line
 * conversation.
 *
 * The platform exposes eight UNDOCUMENTED bound actions on `bot`. All eight
 * were tested; these are the verdicts, so nobody has to repeat the work:
 *
 *   PvaPublish                 USED. Publishes. Returns HTTP 200 on failure
 *                              too, and its body
 *                              ({PublishedBotContentId, PublishBotJobResponse})
 *                              came back empty/null, so the real outcome must
 *                              be read from synchronizationstatus.
 *   PvaDeleteBot               USED. Cascades to every component. Requires
 *                              OWNERSHIP — a user the agent was merely shared
 *                              with (even read/write) gets HTTP 400.
 *   PvaGetDirectLineEndpoint   USED. Conversation endpoint. Requires a
 *                              Copilot Studio licence on the caller;
 *                              unlicensed accounts are refused with
 *                              "User Viral license is expired".
 *   PvaAuthorize               NOT USED. Looks like the native share action,
 *                              but an unshare returned 204 and revoked
 *                              nothing. Undocumented role semantics + a
 *                              silent no-op; explicit GrantAccess is used.
 *   PvaCreateBotComponents     NOT USED. Works, but ONLY when every component
 *                              carries `parentbotid@odata.bind` — without it
 *                              the call returns 204 and creates nothing. It
 *                              also returns no ids. Individual creates are
 *                              preferred: same result, per-component
 *                              feedback, no silent-failure mode.
 *   PvaPublishStatus           NOT USABLE. Wants a PublishBotJob id that
 *                              nothing in the Dataverse surface produces
 *                              (PvaPublish's job field came back null);
 *                              returns 204 with no body for any id tried.
 *   PvaProvision               Returns 204 on an already-provisioned agent,
 *                              with no observable effect. Not needed —
 *                              creating an agent provisions it.
 *   PvaCreateContentSnapshot   Returns 200 with {SnapshotId: null}. No
 *                              observable artifact; purpose unclear.
 *
 * The one thing Dataverse will NOT do is validate component content: `data`
 * is an opaque string to it, so a malformed topic saves happily and only
 * fails when the designer or publish reads it. Hence `validate_copilot_agent`
 * and the warnings the write tools carry.
 *
 * PUBLISH IS THE ONLY REAL VALIDATOR, AND ITS REACH IS UNEVEN. Publishing
 * writes a verdict to `bot.synchronizationstatus.lastFinishedPublishOperation`
 * — `status`, `operationEnd`, and a `diagnosticDetails` list that NAMES the
 * offending componentId. That makes it usable as an oracle, but only for the
 * types it actually compiles:
 *
 *   type 9  (topics AND tools)  VALIDATED, including the nested `action.kind`
 *                               inside a tool — an invented one fails with
 *                               `UnknownElementError`.
 *   type 16 (knowledge sources) VALIDATED, including the nested `source.kind`.
 *   type 17 (external triggers) NOT VALIDATED. Five components with five
 *                               different `kind:` values, three of them
 *                               invented outright, all published clean. So
 *                               publish cannot be used to discover what a
 *                               valid external trigger looks like, and this
 *                               server deliberately claims no knowledge of
 *                               type 17 content rather than guessing.
 *
 * Publish also does not resolve references: a `flowId` pointing at nothing
 * publishes happily. Back-to-back publishes coalesce, so read the verdict by
 * polling `operationEnd` for a CHANGE rather than by counting calls.
 */

import { z } from "zod";
import { isMap, isScalar, isSeq, parseDocument } from "yaml";
import { lintMcpToolInputDefaults } from "../utils/copilot-mcp-input-preflight.js";
import { conversationTestCaseInputSchema, handleConversationTestCase, lintConversationTestCaseSource } from "./copilot-conversation-test-case.js";
import { inflateSync } from "node:zlib";
import { createHash } from "node:crypto";
import type { DataverseApi } from "../api/dataverse-api.js";
import { CopilotComponentUpdateError } from "../api/dataverse-api.js";
import type { ConnectorMetadataApi } from "../api/connector-metadata.js";
import { logger } from "../utils/logger.js";
import { ApiError, PowerAutomateError, ValidationError } from "../utils/errors.js";
import { categorizeDataverseError } from "./dataverse-depth-tools.js";
import { GUID_PATTERN, escapeOData, sanitizeErrorMessage } from "../utils/security.js";

// =============================================================================
// Component types — the catalog, read from the live option set
// =============================================================================

/**
 * `botcomponent.componenttype` values. Read from the live picklist rather
 * than guessed; the ones an author actually touches are marked.
 */
export const COMPONENT_TYPES = {
  topic: 9, // "Topic (V2)" — the modern topic; type 0 is the legacy one
  customGpt: 15, // instructions / generative-AI settings live here
  knowledgeSource: 16,
  externalTrigger: 17,
  settings: 18,
  testCase: 19,
  customMetric: 20,
  variable: 12,
  entity: 11,
  skill: 13,
  translations: 10,
  fileAttachment: 14,
} as const;

const COMPONENT_TYPE_LABELS: Record<number, string> = {
  0: "Topic (legacy)",
  1: "Skill",
  2: "Bot variable",
  3: "Bot entity",
  4: "Dialog",
  5: "Trigger",
  6: "Language understanding",
  7: "Language generation",
  8: "Dialog schema",
  9: "Topic",
  10: "Translations",
  11: "Entity",
  12: "Variable",
  13: "Skill",
  14: "File attachment",
  15: "Instructions (Custom GPT)",
  16: "Knowledge source",
  17: "External trigger",
  18: "Settings",
  19: "Evaluation case or set",
  20: "Custom metric",
};

export function componentTypeLabel(value: number | null | undefined): string {
  if (value === null || value === undefined) return "unknown";
  return COMPONENT_TYPE_LABELS[value] ?? `type ${value}`;
}

/**
 * The `kind:` each component type's YAML may open with.
 *
 * Type 9 carries TWO different things despite the picklist calling it all
 * "Topic (V2)": a conversational topic (`AdaptiveDialog`, with `beginDialog:`)
 * and an agent TOOL (`TaskDialog`, with `action:`). Both were read off a real
 * agent, and both publish. Treating type 9 as topic-only reports correct tool
 * YAML as broken.
 */
const EXPECTED_KINDS: Record<number, readonly string[]> = {
  9: ["AdaptiveDialog", "TaskDialog"],
  15: ["GptComponentMetadata"],
  16: ["KnowledgeSourceConfiguration"],
  // MultiTurnEvaluationCase is also a TestCaseComponent in Microsoft's public
  // authoring schema and native maker UI; runtime compatibility is experimental.
  19: ["EvaluationData", "EvaluationSet", "MultiTurnEvaluationCase"],
};

/**
 * `action.kind` values inside a `TaskDialog`, i.e. what a tool actually calls.
 *
 * Confirmed against the publish validator: both of these publish, and an
 * invented one fails publish with `UnknownElementError` naming the component.
 */
export const KNOWN_TASK_ACTION_KINDS = [
  "InvokeConnectorTaskAction", // a connector operation, e.g. SharePoint GetItemAttachments
  "InvokeFlowTaskAction", // a Power Automate cloud flow
  "InvokeAgentTaskAction", // ANOTHER AGENT, called as a tool
  "InvokeSkillTaskAction", // a registered skill's action
  // An EXTERNAL agent or MCP server, over a connection. Read off a production
  // agent's Work IQ tools, whose operationDetails carry
  // `kind: ModelContextProtocolMetadata` + `operationId` — so MCP tools are
  // expressed through this kind, not a dedicated one (InvokeMcpTaskAction is
  // not recognised).
  "InvokeExternalAgentTaskAction",
] as const;

/**
 * Properties each action kind requires, mapped by probing the publish
 * validator: a real kind missing a property fails with "Missing required
 * property 'X'", while an invented kind fails with "Node is unknown to the
 * system". Both `InvokeAgentTaskAction` and `InvokeSkillTaskAction` were taken
 * all the way to a SUCCEEDED publish with the properties listed here.
 *
 * Tested and NOT recognised: InvokePromptTaskAction, InvokeMcpTaskAction,
 * InvokeRestApiTaskAction, SearchKnowledgeTaskAction, InvokeCustomApiTaskAction,
 * InvokeAIBuilderTaskAction.
 */
const TASK_ACTION_REQUIREMENTS: Record<string, readonly string[]> = {
  InvokeConnectorTaskAction: ["connectionReference", "operationId"],
  InvokeFlowTaskAction: ["flowId"],
  InvokeAgentTaskAction: ["gptComponentSchemaName"],
  InvokeSkillTaskAction: ["skillId", "actionId"],
  InvokeExternalAgentTaskAction: ["connectionReference", "operationDetails"],
};

/**
 * Action kinds whose tools carry no modelDescription in production — the
 * description comes from the external server (an MCP server describes its own
 * tools), so warning about its absence here would be a false positive.
 */
const ACTION_KINDS_WITHOUT_MODEL_DESCRIPTION = new Set(["InvokeExternalAgentTaskAction"]);

// =============================================================================
// Helpers
// =============================================================================

function assertGuid(value: string, what: string): string {
  if (!GUID_PATTERN.test(value)) {
    throw new ValidationError(`${what} must be a GUID — got ${JSON.stringify(value.slice(0, 60))}`);
  }
  return value;
}

async function withCategory<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (err) {
    throw categorizeDataverseError(err);
  }
}

function requireApi(api: DataverseApi | null): DataverseApi {
  if (!api) {
    throw new ValidationError(
      "Dataverse is not configured for this environment — Copilot Studio agents live in Dataverse, so this tool needs an environment that has it.",
    );
  }
  return api;
}

/** Row shape we read back from bot/botcomponent queries. */
type Row = Record<string, unknown>;

function str(row: Row, key: string): string | undefined {
  const v = row[key];
  return typeof v === "string" ? v : undefined;
}

function num(row: Row, key: string): number | undefined {
  const v = row[key];
  return typeof v === "number" ? v : undefined;
}

/**
 * The component's DOCUMENT kind — the top-level `kind:` line.
 *
 * Deliberately anchored at column 0: nested `kind:` keys are common and
 * mean something else entirely. A knowledge source reads
 *
 *   kind: KnowledgeSourceConfiguration
 *   source:
 *     kind: SharePointSearchSource
 *
 * and an indentation-tolerant match would report the inner source kind as
 * the document kind, so a correct component would be flagged as wrong.
 */
export function readYamlKind(data: string | undefined): string | undefined {
  if (!data) return undefined;
  const match = /^kind:[ \t]*(\S+)/m.exec(data);
  return match?.[1];
}

/**
 * Every cloud-flow id a topic invokes (`flowId:` under an InvokeFlowAction).
 *
 * These are the references that break publishes: the agent resolves each one
 * against the flows the environment actually has, and an unresolvable id
 * fails the whole publish with `NotFound: CloudFlow … not found`.
 */
/**
 * Read a scalar `key: value` from a component's YAML at any indent.
 *
 * Deliberately regex, not a YAML parser: `data` is authoring source that the
 * platform itself treats as an opaque string, so a strict parse would throw on
 * content the service accepts. A missed value degrades to "not reported"; a
 * throw would take the whole listing down.
 */
export function readYamlScalar(data: string | undefined, key: string): string | undefined {
  if (!data) return undefined;
  const match = new RegExp(`^[ \\t]*${key}:[ \\t]*(.+?)[ \\t]*$`, "m").exec(data);
  const value = match?.[1];
  if (value === undefined || value === "") return undefined;
  if (value.startsWith('"') && value.endsWith('"')) {
    try {
      const decoded: unknown = JSON.parse(value);
      if (typeof decoded === "string") return decoded;
    } catch {
      // Preserve the fail-soft reader behaviour for opaque platform YAML.
    }
  }
  return value.replace(/^["']/, "").replace(/["']$/, "");
}

/** Encode an arbitrary string as a YAML string scalar without allowing it to add keys or lines. */
function yamlStringScalar(value: string): string {
  // JSON strings are valid YAML double-quoted scalars. This deliberately
  // quotes every caller-supplied value: names such as "Jira: Search", a `#`,
  // or a newline otherwise change the YAML document rather than its value.
  return JSON.stringify(value);
}

/**
 * A tool's connectionReference is an opaque Dataverse logical name.
 * Its suffix is NOT the physical connection ID. Resolve the complete name
 * against connectionreferences before using connectorid or connectionid.
 */
const CONNECTION_REFERENCE_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_.-]{0,499}$(?![\s\S])/;

export function parseConnectionReference(
  reference: string | undefined,
): { referenceName: string } | undefined {
  return typeof reference === "string" && CONNECTION_REFERENCE_NAME_PATTERN.test(reference)
    ? { referenceName: reference } : undefined;
}

/**
 * True for a connection id whose GUID is all zeros, e.g.
 * `shared-a365memcp-00000000-0000-0000-0000-000000000000`.
 *
 * Legacy syntactic detector only. Never apply this to a logical-reference
 * suffix or use it as proof of an implicit binding or healthy connection.
 */
export function isImplicitConnectionId(connectionId: string | undefined): boolean {
  if (!connectionId) return false;
  return /0{8}-0{4}-0{4}-0{4}-0{12}$/.test(connectionId) || /^0+$/.test(connectionId);
}

/**
 * Every complete logical connection-reference name in the component.
 * Never infer a physical connection or implicit binding from a suffix.
 */
export function extractConnectionReferences(
  data: string | undefined,
): { referenceName: string }[] {
  if (!data) return [];
  const seen = new Map<string, { referenceName: string }>();
  const pattern = /^[ \t]*connectionReference:[ \t]*(?:"([^"\r\n]+)"|'([^'\r\n]+)'|([^\s#]+))(?:[ \t]+#[^\r\n]*)?[ \t]*\r?$/gm;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(data)) !== null) {
    let value = match[2] ?? match[3];
    if (match[1] !== undefined) {
      try { value = JSON.parse(`"${match[1]}"`) as string; } catch { continue; }
    }
    const parsed = parseConnectionReference(value);
    if (parsed !== undefined) {
      seen.set(parsed.referenceName.toLowerCase(), parsed);
    }
  }
  return [...seen.values()];
}

type ResolvedToolConnection = {
  state: "resolved";
  referenceName: string;
  referenceId: string;
  connectorId: string;
  connectionId: string | undefined;
} | {
  state: "missing" | "ambiguous" | "invalid" | "inactive" | "unavailable" | "not checked";
  referenceName: string;
};

function toolConnectorId(value: unknown): string | undefined {
  if (typeof value !== "string" || value !== value.trim()) return undefined;
  const leaf = value.replace(/^\/providers\/Microsoft\.PowerApps\/apis\//i, "");
  return leaf.length <= 200 && /^[A-Za-z][A-Za-z0-9]*_[A-Za-z0-9_-]+$/.test(leaf) ? leaf : undefined;
}

function toolPhysicalConnectionId(value: unknown, connectorId: string): string | undefined {
  if (typeof value !== "string" || value !== value.trim()) return undefined;
  let leaf: string;
  const qualified = /^\/providers\/Microsoft\.PowerApps\/apis\/([^/]+)\/connections\/([^/]+)$/i.exec(value);
  if (qualified) {
    if (qualified[1]?.toLowerCase() !== connectorId.toLowerCase()) return undefined;
    leaf = qualified[2] ?? "";
  } else {
    leaf = value.replace(/^\/providers\/Microsoft\.PowerApps\/connections\//i, "");
  }
  return /^[A-Za-z0-9][A-Za-z0-9_-]{0,199}$/.test(leaf) ? leaf : undefined;
}

/** Exact logical-name lookup; no suffix fallback or raw provider-error disclosure. */
async function resolveToolConnectionReference(
  api: DataverseApi,
  referenceName: string,
): Promise<ResolvedToolConnection> {
  if (!CONNECTION_REFERENCE_NAME_PATTERN.test(referenceName)) return { state: "invalid", referenceName };
  try {
    const page = await api.queryRows("connectionreferences", {
      select: "connectionreferenceid,connectionreferencelogicalname,connectorid,connectionid,statecode",
      filter: `connectionreferencelogicalname eq '${referenceName}'`,
      top: 2,
    });
    if (!Array.isArray(page?.value) || page.nextLink !== undefined || page.value.length > 1) {
      return { state: "ambiguous", referenceName };
    }
    if (page.value.length === 0) return { state: "missing", referenceName };
    const row = page.value[0];
    if (!row || typeof row !== "object" || Array.isArray(row)) return { state: "invalid", referenceName };
    const referenceId = str(row, "connectionreferenceid");
    const returnedName = str(row, "connectionreferencelogicalname");
    const connectorId = toolConnectorId(row.connectorid);
    if (row.statecode !== 0) return { state: row.statecode === 1 ? "inactive" : "invalid", referenceName };
    if (!referenceId || !GUID_PATTERN.test(referenceId) ||
        returnedName?.toLowerCase() !== referenceName.toLowerCase() || connectorId === undefined) {
      return { state: "invalid", referenceName };
    }
    const rawConnectionId = row.connectionid;
    const connectionId = toolPhysicalConnectionId(rawConnectionId, connectorId);
    if (rawConnectionId !== null && rawConnectionId !== undefined && rawConnectionId !== "" && connectionId === undefined) {
      return { state: "invalid", referenceName };
    }
    return { state: "resolved", referenceName: returnedName, referenceId, connectorId, connectionId };
  } catch {
    return { state: "unavailable", referenceName };
  }
}

/** Bound diagnostics to 50 distinct references; remaining names are explicitly unchecked. */
async function resolveToolConnectionReferences(api: DataverseApi, names: string[]): Promise<Map<string, ResolvedToolConnection>> {
  const results = new Map<string, ResolvedToolConnection>();
  for (const name of names) {
    const key = name.toLowerCase();
    if (results.has(key)) continue;
    results.set(key, results.size < 50
      ? await resolveToolConnectionReference(api, name)
      : { state: "not checked", referenceName: name });
  }
  return results;
}

async function toolConnectionDiagnosticLines(
  api: DataverseApi,
  connectorApi: ConnectorMetadataApi | null | undefined,
  names: string[],
  check: boolean,
): Promise<Map<string, string[]>> {
  const details = new Map<string, string[]>();
  const references = check ? await resolveToolConnectionReferences(api, names) : new Map<string, ResolvedToolConnection>();
  let live: Awaited<ReturnType<ConnectorMetadataApi["listConnections"]>> | undefined;
  if (check && connectorApi && [...references.values()].some((ref) => ref.state === "resolved" && ref.connectionId !== undefined)) {
    try { live = await connectorApi.listConnections(); } catch { /* Unavailable is not a broken connection. */ }
  }
  for (const name of names) {
    const key = name.toLowerCase();
    if (details.has(key)) continue;
    const lines = [`logical connection reference: ${name}`];
    const reference = references.get(key);
    if (reference?.state !== "resolved") {
      lines.push(`reference resolution: ${reference?.state ?? "not checked"}; physical connection not verified (no suffix inference).`);
    } else {
      lines.push(`connector metadata: ${reference.connectorId}`);
      if (reference.connectionId === undefined) {
        lines.push("physical connection metadata: unbound or not reported; no runtime verification.");
      } else {
        lines.push(`physical connection metadata: ${reference.connectionId}`);
        if (live === undefined || !Array.isArray(live) || live.length > 5000 ||
            live.some((connection) => connection === null || typeof connection !== "object" ||
              Array.isArray(connection) || typeof connection.name !== "string")) {
          lines.push("connection status metadata: not checked or unavailable.");
        } else {
          const matches = live.filter((connection) => connection.name?.toLowerCase() === reference.connectionId?.toLowerCase());
          const found = matches.length === 1 ? matches[0] : undefined;
          if (matches.length === 0) {
            lines.push("connection status metadata: not returned by the current visible connection listing; not proof of deletion.");
          } else if (!found || toolConnectorId(found.properties?.apiId)?.toLowerCase() !== reference.connectorId.toLowerCase()) {
            lines.push("connection status metadata: identity ambiguous or connector not verified.");
          } else {
            const statuses = Array.isArray(found.properties.statuses) && found.properties.statuses.length <= 100
              ? found.properties.statuses : [];
            const allowed = new Set(["Connected", "Error", "NotConnected", "Disconnected", "Disabled"]);
            const values = statuses.map((entry) => entry !== null && typeof entry === "object" &&
              !Array.isArray(entry) && typeof entry.status === "string" ? entry.status : undefined);
            const status = values.length > 0 && values.every((value) => typeof value === "string" && allowed.has(value))
              ? values.find((value) => value !== "Connected") ?? "Connected" : "Unknown";
            lines.push(`connection status metadata: ${status}; not proof of runtime access or the Invoker's binding.`);
          }
        }
      }
      lines.push("Component/reference association was not checked by this metadata diagnostic.");
    }
    details.set(key, lines);
  }
  return details;
}



/**
 * True when a component is a TOOL rather than a conversational topic. Both are
 * `componenttype` 9; the content's `kind` is authoritative (`TaskDialog` for a
 * tool, `AdaptiveDialog` for a topic) and the designer's `<agent>.action.<Name>`
 * schema name is the fallback when content was not selected.
 */
export function isAgentToolComponent(args: {
  componentType: number | null | undefined;
  data?: string | undefined;
  schemaName?: string | undefined;
}): boolean {
  if (args.componentType !== COMPONENT_TYPES.topic) return false;
  if (args.data !== undefined && args.data !== "") return readYamlKind(args.data) === "TaskDialog";
  return args.schemaName !== undefined && args.schemaName.includes(".action.");
}

/**
 * What one tool is and what it calls. Everything here is read from the
 * component's own content — nothing is inferred from the display name, which
 * an author can set to anything.
 */
export interface AgentToolSummary {
  actionKind: string | undefined;
  /** The name the ORCHESTRATOR sees, which is what it matches against. */
  modelDisplayName: string | undefined;
  /** The text the model uses to decide whether to call this tool. */
  modelDescription: string | undefined;
  operationId: string | undefined;
  flowId: string | undefined;
  connectionReferenceName: string | undefined;
  /** Whose credentials run the call — `Invoker` (signed-in user) or `Maker`. */
  connectionMode: string | undefined;
  isMcp: boolean;
}

export function summarizeAgentTool(data: string | undefined): AgentToolSummary {
  const connection = extractConnectionReferences(data)[0];
  return {
    actionKind: readTaskActionKind(data),
    modelDisplayName: readYamlScalar(data, "modelDisplayName"),
    modelDescription: readYamlScalar(data, "modelDescription"),
    operationId: readYamlScalar(data, "operationId"),
    flowId: readYamlScalar(data, "flowId"),
    connectionReferenceName: connection?.referenceName,
    connectionMode: readYamlScalar(data, "mode"),
    isMcp: data !== undefined && /kind:\s*ModelContextProtocolMetadata/.test(data),
  };
}

export function extractFlowReferences(data: string | undefined): string[] {
  if (!data) return [];
  const ids = new Set<string>();
  const pattern = /^\s*flowId:\s*["']?([0-9a-fA-F-]{36})["']?\s*$/gm;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(data)) !== null) {
    const id = match[1];
    if (id !== undefined && GUID_PATTERN.test(id)) ids.add(id.toLowerCase());
  }
  return [...ids];
}

/**
 * Knowledge-source `source.kind` values that publish accepts.
 *
 * `SharePointSearchSource` and `PublicSiteSearchSource` were both confirmed
 * live — an agent published successfully with each. `GraphConnectorSearchSource`
 * is documented but untested here, so it is allowed rather than flagged.
 * A kind outside this set fails publish with the unhelpful "Node is unknown
 * to the system", which is worth catching before that point.
 */
/**
 * Knowledge-source kinds the publish validator accepts, and the properties it
 * demands for each.
 *
 * Established by probing publish rather than from documentation: an unknown
 * kind fails with "Node is unknown to the system", while a REAL kind missing a
 * property fails with "Missing required property 'X'" — so the second message
 * is itself proof the kind is valid, and it names what is missing. Six other
 * plausible kinds (DataverseSearchSource, WebSearchSource, FileSearchSource,
 * OneDriveSearchSource, SalesforceSearchSource, CustomSearchSource) were tested
 * and are NOT recognised.
 */
const KNOWLEDGE_SOURCE_REQUIREMENTS: Record<string, readonly string[]> = {
  SharePointSearchSource: [],
  PublicSiteSearchSource: [],
  AzureAISearchSource: ["indexName"],
  // Publish accepts connectionId and connectionName, then asks for a
  // 'SchemaName' whose YAML spelling is still unknown — schemaName,
  // connectionSchemaName, itemSchemaName and schema were all rejected. So this
  // kind is real but cannot yet be completed from here.
  GraphConnectorSearchSource: ["connectionId", "connectionName"],
};

const KNOWLEDGE_SOURCE_KINDS_NEEDING_UNKNOWN_PROPERTY = new Set(["GraphConnectorSearchSource"]);

const KNOWN_KNOWLEDGE_SOURCE_KINDS = Object.keys(KNOWLEDGE_SOURCE_REQUIREMENTS);

/** The nested `source.kind` of a knowledge-source component, if present. */
export function readKnowledgeSourceKind(data: string | undefined): string | undefined {
  if (!data) return undefined;
  const match = /^\s+kind:[ \t]*(\S+)/m.exec(data);
  return match?.[1];
}

/**
 * The `kind:` nested under a TaskDialog's `action:` — what the tool calls.
 *
 * This cannot reuse `readKnowledgeSourceKind`'s "first indented kind", because
 * a tool's `inputs:` list has its own `- kind: ManualTaskInput` entries above
 * `action:`. So anchor on the `action:` key and read the kind inside its block.
 */
export function readTaskActionKind(data: string | undefined): string | undefined {
  if (!data) return undefined;
  const start = /^action:[ \t]*$/m.exec(data);
  if (start === null) return undefined;
  const rest = data.slice(start.index + start[0].length);
  for (const line of rest.split("\n")) {
    if (line.trim() === "") continue;
    // A non-indented line ends the action block.
    if (!/^[ \t]/.test(line)) return undefined;
    const kind = /^[ \t]+kind:[ \t]*(\S+)/.exec(line);
    if (kind !== null) return kind[1];
  }
  return undefined;
}

/**
 * Type 9 is both topics and tools. Callers listing components should say which,
 * because "Topic" on a connector tool is actively misleading.
 *
 * Content is authoritative when we have it. Schema-name conventions cannot
 * classify metadata-only rows: native MCP tools also use `<agent>.topic.*`.
 * Report an unclassified type-9 bucket without fetching additional content.
 */
export function describeComponentKind(args: {
  componentType: number | null | undefined;
  data?: string | undefined;
  schemaName?: string | undefined;
}): string {
  if (args.componentType === COMPONENT_TYPES.testCase) {
    if (args.data === undefined || args.data === "")
      return "Evaluation case or set (source not inspected)";
    const kind = readEvaluationComponentKind(args.data);
    if (kind === "EvaluationSet") return "Evaluation set";
    if (kind === "EvaluationData") return "Test case";
    if (kind === "MultiTurnEvaluationCase") return "Conversation test case";
    return "Evaluation case or set (source kind unrecognized)";
  }
  if (args.componentType !== COMPONENT_TYPES.topic) return componentTypeLabel(args.componentType);
  if (args.data !== undefined && args.data !== "") {
    const kind = readYamlKind(args.data);
    if (kind === "AdaptiveDialog") return componentTypeLabel(args.componentType);
    if (kind !== "TaskDialog") return "Topic/tool (source kind unrecognized)";
    const actionKind = readTaskActionKind(args.data);
    if (actionKind === "InvokeFlowTaskAction") return "Tool (flow)";
    if (actionKind === "InvokeAgentTaskAction") return "Tool (agent)";
    if (actionKind === "InvokeExternalAgentTaskAction") {
      return /kind:\s*ModelContextProtocolMetadata/.test(args.data)
        ? "Tool (MCP server)"
        : "Tool (external agent)";
    }
    if (actionKind === "InvokeConnectorTaskAction") return "Tool (connector)";
    return "Tool";
  }
  return "Topic/tool (source not inspected)";
}

/**
 * Structural check on component content. This is deliberately a LINT, not a
 * validator: only the Copilot Studio designer and publish know the full
 * schema, so we report what is checkable and say plainly what is not.
 */
export function lintComponent(componentType: number, data: string): string[] {
  const problems: string[] = [];
  if (data.trim() === "") {
    problems.push("content is empty");
    return problems;
  }

  const kind = readYamlKind(data);
  const expected = EXPECTED_KINDS[componentType];
  if (expected !== undefined) {
    const wanted = expected.map((k) => `'kind: ${k}'`).join(" or ");
    if (kind === undefined) {
      problems.push(
        `no 'kind:' line — ${componentTypeLabel(componentType)} content must start with ${wanted}`,
      );
    } else if (!expected.includes(kind)) {
      problems.push(
        `'kind: ${kind}' but ${componentTypeLabel(componentType)} content must be ${wanted}`,
      );
    }
  }

  if (data.includes("\t")) {
    problems.push("contains a TAB character — YAML indentation must use spaces");
  }
  if (componentType === COMPONENT_TYPES.testCase && kind === "MultiTurnEvaluationCase") {
    problems.push(...lintConversationTestCaseSource(data));
  }
  if (componentType === COMPONENT_TYPES.customGpt && !/^\s*instructions:/m.test(data)) {
    problems.push("no 'instructions:' key — the agent's instructions would be empty");
  }
  if (componentType === COMPONENT_TYPES.topic) {
    problems.push(...lintMcpToolInputDefaults(data));
    if (kind === "TaskDialog") {
      // A tool, not a topic. Different shape entirely.
      const actionKind = readTaskActionKind(data);
      if (actionKind === undefined) {
        problems.push(
          `no 'action.kind' — a tool needs one, e.g. ${KNOWN_TASK_ACTION_KINDS.join(" or ")}`,
        );
      } else if (!(KNOWN_TASK_ACTION_KINDS as readonly string[]).includes(actionKind)) {
        problems.push(
          `action kind '${actionKind}' is not one this server has verified (${KNOWN_TASK_ACTION_KINDS.join(", ")}). A real kind will publish; an invented one fails publish with the unhelpful "Node is unknown to the system"`,
        );
      } else {
        // Catch a missing property here rather than letting publish fail
        // minutes later with "Missing required property 'X'".
        for (const required of TASK_ACTION_REQUIREMENTS[actionKind] ?? []) {
          if (!new RegExp(`^\\s+${required}:`, "m").test(data)) {
            problems.push(
              `${actionKind} requires '${required}' under action — publish rejects it otherwise`,
            );
          }
        }
      }
      if (
        !/^\s*modelDescription:/m.test(data) &&
        !(actionKind !== undefined && ACTION_KINDS_WITHOUT_MODEL_DESCRIPTION.has(actionKind))
      ) {
        problems.push(
          "no 'modelDescription:' — that text is how the model decides whether to call this tool, so without it the tool is unlikely to ever fire",
        );
      }
    } else if (!/^\s*beginDialog:/m.test(data)) {
      problems.push("no 'beginDialog:' key — a topic needs one");
    } else {
      problems.push(...lintTopicNodes(data));
    }
  }
  if (
    componentType === COMPONENT_TYPES.testCase &&
    kind === "EvaluationSet" &&
    !/^\s*graders:/m.test(data)
  ) {
    problems.push(
      "no 'graders:' — an EvaluationSet names the graders that score its cases (e.g. GeneralQualityGrader)",
    );
  }
  if (componentType === COMPONENT_TYPES.knowledgeSource) {
    const sourceKind = readKnowledgeSourceKind(data);
    if (sourceKind === undefined) {
      problems.push(
        "no nested 'source.kind' — a knowledge source needs one, e.g. SharePointSearchSource",
      );
    } else if (!KNOWN_KNOWLEDGE_SOURCE_KINDS.includes(sourceKind)) {
      problems.push(
        `source kind '${sourceKind}' is not one this server has seen work (${KNOWN_KNOWLEDGE_SOURCE_KINDS.join(", ")}). An unrecognised kind fails publish with the unhelpful "Node is unknown to the system"`,
      );
    } else {
      // Catch a missing required property here rather than letting publish
      // fail minutes later with "Missing required property 'X'".
      for (const required of KNOWLEDGE_SOURCE_REQUIREMENTS[sourceKind] ?? []) {
        if (!new RegExp(`^\\s+${required}:`, "m").test(data)) {
          problems.push(
            `${sourceKind} requires '${required}' under source — publish rejects it otherwise`,
          );
        }
      }
      if (KNOWLEDGE_SOURCE_KINDS_NEEDING_UNKNOWN_PROPERTY.has(sourceKind)) {
        problems.push(
          `${sourceKind} also requires a 'SchemaName' property whose YAML spelling this server has not identified — publish will reject it. Configure this source in Copilot Studio instead`,
        );
      }
    }
  }
  return problems;
}

// =============================================================================
// list_copilot_agents
// =============================================================================

export const listCopilotAgentsInputSchema = z.object({
  search: z.string().max(200).optional(),
  includeComponentCounts: z.boolean().optional(),
  limit: z.number().int().min(1).max(100).optional().default(50),
});

export const listCopilotAgentsTool = {
  name: "list_copilot_agents",
  description:
    "List Microsoft Copilot Studio agents in the current environment. Agents are Dataverse 'bot' rows, so you only see agents you own or that were shared with you — an empty result does not prove the environment has no agents (use get_copilot_agent_inventory with an appropriate tenant inventory reader role).",
  inputSchema: {
    type: "object" as const,
    properties: {
      search: { type: "string", description: "Case-insensitive substring match on the agent name" },
      includeComponentCounts: {
        type: "boolean",
        description:
          "Also count up to 1,000 components per agent by type (one extra query per agent; limit cannot exceed 50)",
      },
      limit: {
        type: "integer",
        minimum: 1,
        maximum: 100,
        default: 50,
        description: "Maximum agents to return (default 50, maximum 100)",
      },
    },
  },
};

export async function handleListCopilotAgents(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = listCopilotAgentsInputSchema.parse(input);
  if (parsed.includeComponentCounts === true && parsed.limit > 50) {
    throw new ValidationError(
      "includeComponentCounts performs one Dataverse query per agent, so limit cannot exceed 50 when it is enabled.",
    );
  }
  const api = requireApi(context.api);
  logger.info({ search: parsed.search, limit: parsed.limit }, "Executing list_copilot_agents");

  const options: { select: string; orderBy: string; filter?: string; top: number } = {
    select: "botid,name,schemaname,statecode,statuscode,publishedon,createdon,modifiedon,template",
    orderBy: "modifiedon desc",
    top: parsed.limit,
  };
  if (parsed.search) {
    options.filter = `contains(name,'${parsed.search.replace(/'/g, "''")}')`;
  }

  const page = await withCategory(() => api.queryRows("bots", options));
  const rows = page.value.slice(0, parsed.limit);
  const hasMore = page.nextLink !== undefined || page.value.length > parsed.limit;
  if (rows.length === 0) {
    return parsed.search
      ? `No Copilot Studio agents matching ${JSON.stringify(parsed.search)} are visible to you in this environment.`
      : "No Copilot Studio agents are visible to you in this environment. Note this reflects Dataverse row security — agents owned by other users are not listed unless shared with you.";
  }

  const lines = [
    !hasMore
      ? `Copilot Studio agents (${rows.length}):`
      : `Copilot Studio agents (showing the first ${rows.length}; more are available):`,
    "",
  ];
  for (const row of rows) {
    const botid = str(row, "botid") ?? "?";
    lines.push(`  ${str(row, "name") ?? "(unnamed)"}`);
    lines.push(`    id: ${botid}`);
    const schema = str(row, "schemaname");
    if (schema) lines.push(`    schema name: ${schema}`);
    const published = str(row, "publishedon");
    lines.push(`    published: ${published ?? "never"}`);
    const modified = str(row, "modifiedon");
    if (modified) lines.push(`    modified: ${modified}`);

    if (parsed.includeComponentCounts && GUID_PATTERN.test(botid)) {
      const componentPage = await withCategory(() =>
        api.queryRows("botcomponents", {
          select: "componenttype",
          filter: `_parentbotid_value eq ${botid}`,
          top: 1000,
        }),
      );
      const comps = componentPage.value;
      const counts = new Map<number, number>();
      for (const c of comps) {
        const t = num(c, "componenttype");
        if (t !== undefined) counts.set(t, (counts.get(t) ?? 0) + 1);
      }
      const summary = [...counts.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([t, n]) => `${componentTypeLabel(t)} ${n}`)
        .join(", ");
      lines.push(
        `    components: ${comps.length}${componentPage.nextLink === undefined ? "" : "+"}${summary ? ` (${summary})` : ""}`,
      );
    } else if (parsed.includeComponentCounts) {
      lines.push("    components: unavailable because the row did not contain a valid agent id");
    }
    lines.push("");
  }
  if (hasMore) {
    lines.push(
      "Only the first Dataverse page is shown. Use search to narrow the list; get_copilot_agent_inventory provides tenant inventory independently of row sharing.",
    );
  }
  return lines.join("\n").trimEnd();
}

/**
 * Feature flags an agent carries outside its components.
 *
 * `bot.configuration` holds the generative/voice switches — notably
 * `settings.GenerativeActionsEnabled`, which is generative orchestration —
 * and `bot.applicationmanifestinformation` records which channels the agent
 * has been registered with. Neither is visible from the component list, and
 * both answer questions people actually ask ("is generative orchestration on
 * for this agent?", "is this published to Teams?").
 */
/**
 * How `bot.authenticationmode` is spelled inside `synchronizationstatus`.
 * Verified live: mode 1 publishes as "None", mode 2 as "Integrated". The
 * other modes have not been observed there, so they are deliberately absent
 * and produce no drift claim.
 */
const AUTH_MODE_PUBLISHED_NAMES: Record<number, string> = {
  1: "None",
  2: "Integrated",
};

/**
 * What the PUBLISHED agent is, as opposed to what the authoring record says.
 *
 * `bot.authenticationmode` and the component rows describe the draft; a
 * setting only reaches Teams and the web channel at publish. The publish
 * verdict, the synchronization state and the authentication mode the last
 * publish actually captured all live in `bot.synchronizationstatus`, and
 * reading the draft alone reports a change as live the moment it is saved.
 *
 * Deliberately does NOT report `botRegistration.isAppAvailableInTenant`:
 * measured across four live agents it is `true` for every published one
 * regardless of channel setup, so it does not distinguish an agent that is
 * reachable in Teams from one that is not. Reporting it as Teams
 * availability would be a confident wrong answer.
 */
export function describeSynchronizationStatus(
  raw: unknown,
  currentAuthMode: number | undefined,
): string[] {
  let value: unknown = raw;
  if (typeof raw === "string") {
    try {
      value = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (value === null || typeof value !== "object" || Array.isArray(value)) return [];
  const status = value as Record<string, unknown>;
  const lines: string[] = [];

  const asRecord = (input: unknown): Record<string, unknown> | undefined =>
    input !== null && typeof input === "object" && !Array.isArray(input)
      ? (input as Record<string, unknown>)
      : undefined;

  const lastPublish = asRecord(status["lastFinishedPublishOperation"]);
  const publishStatus = lastPublish === undefined ? undefined : str(lastPublish, "status");
  if (publishStatus !== undefined) {
    const ended = str(lastPublish!, "operationEnd");
    const when = ended !== undefined ? ` (${ended})` : "";
    lines.push(
      publishStatus === "Succeeded"
        ? `  last publish: Succeeded${when}`
        : `  ⚠ last publish: ${publishStatus}${when} — the published agent is whatever the previous successful publish left`,
    );
  }

  const syncState = asRecord(status["currentSynchronizationState"]);
  const state = syncState === undefined ? undefined : str(syncState, "state");
  if (state !== undefined && state !== "Synchronized") {
    lines.push(
      `  ⚠ synchronization state: ${state} — the published agent may not match what is authored`,
    );
  }

  // The mode the last publish captured is what the live agent enforces. A
  // draft change to authentication does not take effect until republish, and
  // authentication decides both whether the agent can be tested and whether
  // its Invoker-mode tools have a user to run as.
  const publishedDetails = asRecord(status["lastPublishedDetails"]);
  const publishedAuth =
    publishedDetails === undefined ? undefined : str(publishedDetails, "authenticationMode");
  if (publishedAuth !== undefined && currentAuthMode !== undefined) {
    // The platform's own spelling inside synchronizationstatus, which is NOT
    // the first word of this server's display label — mode 1 prints as "No
    // authentication" and publishes as "None". Only modes seen live are
    // mapped; an unmapped mode makes no drift claim at all rather than
    // comparing against a guess.
    const currentShort = AUTH_MODE_PUBLISHED_NAMES[currentAuthMode];
    if (currentShort !== undefined && currentShort !== publishedAuth) {
      lines.push(
        `  ⚠ published with authentication '${publishedAuth}', but the agent is now set to '${currentShort}' — the live agent keeps '${publishedAuth}' until you publish again`,
      );
    }
  }

  return lines;
}

export function describeAgentConfiguration(configuration: unknown, manifest: unknown): string[] {
  const lines: string[] = [];
  const parse = (raw: unknown): Record<string, unknown> | undefined => {
    let value: unknown = raw;
    if (typeof raw === "string") {
      try {
        value = JSON.parse(raw);
      } catch {
        return undefined;
      }
    }
    return value !== null && typeof value === "object"
      ? (value as Record<string, unknown>)
      : undefined;
  };

  const config = parse(configuration);
  if (config !== undefined) {
    const settings = config["settings"];
    const generative =
      settings !== null && typeof settings === "object"
        ? (settings as Record<string, unknown>)["GenerativeActionsEnabled"] === true
        : false;
    lines.push(`  generative orchestration: ${generative ? "on" : "off"}`);
    if (config["isTelephonyEnabled"] === true) lines.push("  telephony: enabled");
  }

  const manifestRecord = parse(manifest);
  if (manifestRecord !== undefined) {
    const channels = Object.keys(manifestRecord).filter((k) => !k.startsWith("$"));
    if (channels.length > 0) lines.push(`  channel registrations: ${channels.join(", ")}`);
  }
  return lines;
}

// =============================================================================
// get_copilot_agent
// =============================================================================

export const getCopilotAgentInputSchema = z.object({
  agentId: z.string().min(1),
  includeInstructions: z.boolean().optional().default(true),
});

export const getCopilotAgentTool = {
  name: "get_copilot_agent",
  description:
    "Get one Copilot Studio agent: owner, primary/supported languages, settings, publish/provisioning state, a breakdown of its components by type, and (by default) its full instructions text.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: { type: "string", description: "The agent's botid (GUID)" },
      includeInstructions: {
        type: "boolean",
        description: "Include the agent's instructions text (default true)",
      },
    },
    required: ["agentId"],
  },
};

export async function handleGetCopilotAgent(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = getCopilotAgentInputSchema.parse(input);
  const api = requireApi(context.api);
  const agentId = assertGuid(parsed.agentId, "agentId");
  logger.info({ agentId }, "Executing get_copilot_agent");

  const bot = await withCategory(() =>
    api.getRow(
      "bots",
      agentId,
      "botid,name,schemaname,language,supportedlanguages,_ownerid_value,statecode,statuscode,publishedon,createdon,modifiedon,template,authenticationmode,accesscontrolpolicy,synchronizationstatus,configuration,applicationmanifestinformation",
    ),
  );

  const componentPage = await withCategory(() =>
    api.queryRows("botcomponents", {
      select: "botcomponentid,name,componenttype,schemaname,modifiedon",
      filter: `_parentbotid_value eq ${agentId}`,
      top: 5000,
    }),
  );
  const components = componentPage.value;
  const componentsIncomplete = componentPage.nextLink !== undefined;

  const lines = [`Agent: ${str(bot, "name") ?? "(unnamed)"}`, ""];
  lines.push(`  id: ${agentId}`);
  const schema = str(bot, "schemaname");
  if (schema) lines.push(`  schema name: ${schema}`);
  const primaryLanguage = num(bot, "language");
  if (primaryLanguage !== undefined) {
    const languageLabel = str(bot, "language@OData.Community.Display.V1.FormattedValue");
    lines.push(
      `  primary language: ${primaryLanguage}${languageLabel ? ` (${languageLabel})` : ""}`,
    );
  }
  const supportedLanguages = str(bot, "supportedlanguages");
  if (supportedLanguages) lines.push(`  supported languages: ${supportedLanguages}`);
  const ownerId = str(bot, "_ownerid_value");
  if (ownerId) {
    const ownerLabel = str(bot, "_ownerid_value@OData.Community.Display.V1.FormattedValue");
    lines.push(`  owner: ${ownerLabel ? `${ownerLabel} (${ownerId})` : ownerId}`);
  }
  lines.push(`  published: ${str(bot, "publishedon") ?? "never"}`);
  const statusLabel = str(bot, "statuscode@OData.Community.Display.V1.FormattedValue");
  if (statusLabel) lines.push(`  status: ${statusLabel}`);
  const template = str(bot, "template");
  if (template) lines.push(`  template: ${template}`);
  lines.push(
    `  created: ${str(bot, "createdon") ?? "?"}   modified: ${str(bot, "modifiedon") ?? "?"}`,
  );
  // Authentication decides whether the agent can be TESTED from here at all:
  // Direct Line carries no signed-in user, so an Integrated-auth agent
  // refuses the conversation. Surfacing it next to the rest saves finding
  // that out from a runtime error code.
  const authMode = num(bot, "authenticationmode");
  if (authMode !== undefined) {
    lines.push(`  authentication: ${AUTH_MODE_LABELS[authMode] ?? `mode ${authMode}`}`);
  }
  const accessPolicy = num(bot, "accesscontrolpolicy");
  if (accessPolicy !== undefined) {
    lines.push(
      `  who may use it: ${ACCESS_POLICY_LABELS[accessPolicy] ?? `policy ${accessPolicy}`}`,
    );
  }
  lines.push(
    ...describeAgentConfiguration(bot["configuration"], bot["applicationmanifestinformation"]),
  );
  lines.push(...describeSynchronizationStatus(bot["synchronizationstatus"], authMode));

  const byType = new Map<number, Row[]>();
  for (const c of components) {
    const t = num(c, "componenttype");
    if (t === undefined) continue;
    const list = byType.get(t) ?? [];
    list.push(c);
    byType.set(t, list);
  }

  // Type 9 includes topics and tools. This metadata-only query does not
  // establish their source kinds; native tools can have ".topic." names too.
  // Do not invent a tool/topic split or fetch extra content to imply one.
  const byKind = new Map<string, { type: number; rows: Row[] }>();
  for (const c of components) {
    const t = num(c, "componenttype");
    if (t === undefined) continue;
    const label = describeComponentKind({
      componentType: t,
      data: t === COMPONENT_TYPES.testCase ? undefined : str(c, "data"),
      schemaName: str(c, "schemaname"),
    });
    const entry = byKind.get(label) ?? { type: t, rows: [] };
    entry.rows.push(c);
    byKind.set(label, entry);
  }

  lines.push(
    "",
    componentsIncomplete
      ? `  Components (first ${components.length}; more are available):`
      : `  Components (${components.length}):`,
  );
  for (const [label, entry] of [...byKind.entries()].sort(
    (a, b) => a[1].type - b[1].type || a[0].localeCompare(b[0]),
  )) {
    const list = entry.rows;
    lines.push(`    ${label} — ${list.length}`);
    for (const c of list.slice(0, 12)) {
      lines.push(`      ${str(c, "name") ?? "(unnamed)"}  [${str(c, "botcomponentid") ?? "?"}]`);
    }
    if (list.length > 12) lines.push(`      … and ${list.length - 12} more`);
  }
  if ([...byKind.keys()].some((label) => label.startsWith("Topic/tool ("))) {
    lines.push("    Type 9 includes both topics and tools; schema names do not prove which. Use get_copilot_agent_component or list_copilot_agent_components with includeContent=true to inspect source kinds.");
  }

  // Changes made since the last publish are not in the published version.
  // Earlier versions of these components may still be present in Teams/the
  // web channel; modifiedon alone cannot show whether a component is new.
  // componentstate describes the solution layer, not agent publication.
  const publishedOn = str(bot, "publishedon");
  if (publishedOn !== undefined) {
    const stale = components
      .filter((c) => {
        const modified = str(c, "modifiedon");
        return modified !== undefined && modified > publishedOn;
      })
      .map((c) => str(c, "name") ?? "(unnamed)");
    if (stale.length > 0) {
      lines.push(
        "",
        `  ⚠ ${stale.length} component(s) changed since the last publish; their latest draft changes are not published:`,
        ...stale.slice(0, 8).map((name) => `      ${name}`),
        ...(stale.length > 8 ? [`      … and ${stale.length - 8} more`] : []),
        "    Earlier published versions may still be present in Teams and the web channel. Review and test the draft changes before publishing with publish_copilot_agent.",
      );
    }
  }

  if (parsed.includeInstructions) {
    const gpt = byType.get(COMPONENT_TYPES.customGpt)?.[0];
    if (gpt) {
      const full = await withCategory(() =>
        api.getRow("botcomponents", str(gpt, "botcomponentid") ?? "", "data"),
      );
      const data = str(full, "data");
      if (data) {
        lines.push("", "  Instructions:", ...data.split("\n").map((l) => `    ${l}`));
      }
    } else if (componentsIncomplete) {
      lines.push(
        "",
        "  Instructions: not found in the first component page; the component set is incomplete",
      );
    } else {
      lines.push("", "  Instructions: none set (no Custom GPT component on this agent)");
    }
  }

  if (componentsIncomplete) {
    lines.push(
      "",
      "  ⚠ Dataverse returned a component continuation. Counts, changed-since-publish checks, and instruction discovery above cover only the first 5,000 components and are not a complete-agent verdict.",
    );
  }

  return lines.join("\n");
}

// =============================================================================
// get_copilot_agent_component / list / delete
// =============================================================================

export const getCopilotAgentComponentInputSchema = z.object({
  agentId: z.string().regex(GUID_PATTERN, "agentId must be a GUID"),
  componentId: z.string().regex(GUID_PATTERN, "componentId must be a GUID"),
  includeContent: z.boolean().optional().default(true),
  maxContentChars: z.number().int().min(100).max(200_000).optional().default(50_000),
});

export const getCopilotAgentComponentTool = {
  name: "get_copilot_agent_component",
  description:
    "Read one Copilot Studio component by id and verify that it belongs to the supplied agent. Returns its kind, schema name, modified time, and by default its YAML authoring source. With includeContent=true, reports SHA-256 of the exact full stored UTF-8 source when the returned component ID matches; display truncation does not change that hash. Otherwise the source hash is unavailable. Content is bounded to keep the MCP response usable; raise maxContentChars when a larger component must be inspected.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: {
        type: "string",
        pattern: GUID_PATTERN.source,
        description: "The parent agent's botid (GUID)",
      },
      componentId: {
        type: "string",
        pattern: GUID_PATTERN.source,
        description: "The component's botcomponentid (GUID)",
      },
      includeContent: {
        type: "boolean",
        description: "Include YAML authoring content (default true)",
        default: true,
      },
      maxContentChars: {
        type: "integer",
        minimum: 100,
        maximum: 200_000,
        description: "Maximum content characters to return (default 50000)",
        default: 50_000,
      },
    },
    required: ["agentId", "componentId"],
  },
};

function assertComponentBelongsToAgent(row: Row, componentId: string, agentId: string): void {
  const parentAgentId = str(row, "_parentbotid_value");
  if (parentAgentId === undefined || parentAgentId.toLowerCase() !== agentId.toLowerCase()) {
    throw new ValidationError(
      `Component ${componentId} does not belong to agent ${agentId} — refusing to operate on a component through a different agent.`,
    );
  }
}

export async function handleGetCopilotAgentComponent(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = getCopilotAgentComponentInputSchema.parse(input);
  const api = requireApi(context.api);
  const agentId = assertGuid(parsed.agentId, "agentId");
  const componentId = assertGuid(parsed.componentId, "componentId");
  const select = [
    "botcomponentid",
    "name",
    "description",
    "componenttype",
    "schemaname",
    "modifiedon",
    "_parentbotid_value",
    ...(parsed.includeContent ? ["data"] : []),
  ].join(",");
  const row = await withCategory(() => api.getRow("botcomponents", componentId, select));
  assertComponentBelongsToAgent(row, componentId, agentId);

  const componentType = num(row, "componenttype");
  const lines = [
    `${str(row, "name") ?? "(unnamed)"} — ${describeComponentKind({
      componentType,
      data:
        componentType === COMPONENT_TYPES.testCase && !parsed.includeContent
          ? undefined
          : str(row, "data"),
      schemaName: str(row, "schemaname"),
    })}`,
    `  id: ${componentId}`,
    `  agent id: ${agentId}`,
  ];
  const schemaName = str(row, "schemaname");
  if (schemaName !== undefined) lines.push(`  schema name: ${schemaName}`);
  const description = str(row, "description");
  if (description !== undefined && description !== "") lines.push(`  description: ${description}`);
  const modifiedOn = str(row, "modifiedon");
  if (modifiedOn !== undefined) lines.push(`  modified: ${modifiedOn}`);
  const source = parsed.includeContent && str(row, "botcomponentid")?.toLowerCase() === componentId.toLowerCase()
    ? str(row, "data") : undefined;
  lines.push(`  source sha256: ${source === undefined ? "unavailable" : sourceSha256(source)}`);

  if (parsed.includeContent) {
    const content = str(row, "data") ?? "";
    const returned = content.slice(0, parsed.maxContentChars);
    lines.push(
      "",
      `  content (${content.length} chars${returned.length < content.length ? `; first ${returned.length} shown` : ""}):`,
      returned
        .split("\n")
        .map((line) => `    ${line}`)
        .join("\n"),
    );
    if (returned.length < content.length) {
      lines.push(
        "",
        `  ⚠ Content was truncated. Re-run with maxContentChars up to 200000 to request more.`,
      );
    }
  }
  return lines.join("\n");
}

export const deleteCopilotAgentComponentInputSchema = z.object({
  agentId: z.string().regex(GUID_PATTERN, "agentId must be a GUID"),
  componentId: z.string().regex(GUID_PATTERN, "componentId must be a GUID"),
  confirmDelete: z.boolean().optional(),
});

export const deleteCopilotAgentComponentTool = {
  name: "delete_copilot_agent_component",
  description:
    "Preview or delete one Copilot Studio component (topic, tool, knowledge source, evaluation case/set, or settings row). The component is read first and must belong to agentId. Refuses any component with visible linked child components: deleting a parent can cascade-delete its children, including an EvaluationSet's cases. There is no cascade override. The target and child lookup are checked again before deletion, but these checks are not atomic and caller-visible reads cannot prove that no hidden children exist. Stop concurrent editing before confirming. Omit confirmDelete for a no-change preview; deletion requires confirmDelete=true and the agent must be published afterwards.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: {
        type: "string",
        pattern: GUID_PATTERN.source,
        description: "The parent agent's botid (GUID)",
      },
      componentId: {
        type: "string",
        pattern: GUID_PATTERN.source,
        description: "The component's botcomponentid (GUID)",
      },
      confirmDelete: { type: "boolean", description: "Must be true to perform the deletion" },
    },
    required: ["agentId", "componentId"],
  },
};

export async function handleDeleteCopilotAgentComponent(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = deleteCopilotAgentComponentInputSchema.parse(input);
  const api = requireApi(context.api);
  const agentId = assertGuid(parsed.agentId, "agentId");
  const componentId = assertGuid(parsed.componentId, "componentId");
  const readTarget = async (): Promise<Row> => {
    const target = await withCategory(() =>
      api.getRow(
        "botcomponents",
        componentId,
        "botcomponentid,name,componenttype,schemaname,_parentbotid_value,modifiedon,versionnumber",
      ),
    );
    if (str(target, "botcomponentid")?.toLowerCase() !== componentId.toLowerCase()) {
      throw new ValidationError("The component response did not match the requested ID; refusing deletion.");
    }
    assertComponentBelongsToAgent(target, componentId, agentId);
    return target;
  };
  const assertNoLinkedChildren = async (): Promise<void> => {
    // Do not restrict children by agent: a cross-agent child is also at risk
    // when Dataverse applies the parent relationship's cascade behavior.
    const children = await withCategory(() =>
      api.queryRows("botcomponents", {
        select: "botcomponentid",
        filter: `_parentbotcomponentid_value eq ${componentId}`,
        top: 1,
      }),
    );
    if (!Array.isArray(children?.value)) {
      throw new ValidationError("Could not verify the component's child lookup; refusing deletion.");
    }
    if (children.value.length > 0) {
      throw new ValidationError(
        `Refusing to delete component ${componentId}: at least one linked child component is visible. ` +
          "Deleting a parent can cascade-delete its children, including an EvaluationSet's test cases. " +
          "No deletion was performed. Inspect and deliberately preserve or move the children first; this tool has no cascade override.",
      );
    }
    if (children.nextLink !== undefined) {
      throw new ValidationError("The component's child lookup was incomplete; refusing deletion.");
    }
  };
  const row = await readTarget();
  await assertNoLinkedChildren();
  const componentType = num(row, "componenttype");
  const kindLabel = componentType === COMPONENT_TYPES.testCase
    ? "Evaluation case or set (type 19)"
    : componentTypeLabel(componentType);
  const label = `${str(row, "name") ?? "(unnamed)"} — ${kindLabel}`;

  if (parsed.confirmDelete !== true) {
    return [
      `Delete preview: ${label}`,
      `  component id: ${componentId}`,
      `  agent id: ${agentId}`,
      str(row, "schemaname") !== undefined ? `  schema name: ${str(row, "schemaname")}` : "",
      "  No linked children were visible to this caller; this is not proof that hidden children do not exist.",
      "",
      "No deletion was performed. Re-run with confirmDelete=true to permanently remove this component.",
      "Stop concurrent editing before confirming: the target and child checks are not an atomic delete precondition.",
    ]
      .filter((line) => line !== "")
      .join("\n");
  }

  const current = await readTarget();
  const snapshotFields = [
    "botcomponentid", "name", "componenttype", "schemaname", "_parentbotid_value",
    "modifiedon", "versionnumber", "@odata.etag",
  ];
  if (snapshotFields.some((field) => row[field] !== current[field])) {
    throw new ValidationError("The component changed during deletion checks; refusing deletion. Preview it again.");
  }
  await assertNoLinkedChildren();
  await withCategory(() => api.deleteRow("botcomponents", componentId));
  return [
    `Deleted ${label} (${componentId}) from agent ${agentId}.`,
    "Publish the agent for this removal to reach its channels.",
  ].join("\n");
}

export const listCopilotAgentComponentsInputSchema = z.object({
  agentId: z.string().min(1),
  componentType: z.number().int().min(0).max(20).optional(),
  includeContent: z.boolean().optional(),
  limit: z.number().int().min(1).max(200).optional().default(50),
});

export const listCopilotAgentComponentsTool = {
  name: "list_copilot_agent_components",
  description:
    "List a bounded page of an agent's components (topics, instructions, knowledge sources, settings, test cases), optionally filtered by type and including a bounded preview of YAML authoring source. Use get_copilot_agent_component when one component's complete content is needed.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: { type: "string", description: "The agent's botid (GUID)" },
      componentType: {
        type: "integer",
        description:
          "Filter by componenttype: 9=topic, 15=instructions, 16=knowledge source, 18=settings, 19=test case",
      },
      includeContent: { type: "boolean", description: "Include each component's YAML content" },
      limit: {
        type: "integer",
        minimum: 1,
        maximum: 200,
        default: 50,
        description: "Maximum components to return (default 50, maximum 200)",
      },
    },
    required: ["agentId"],
  },
};

/**
 * Read the agent row purely to prove it EXISTS.
 *
 * A component query filtered by `_parentbotid_value` returns an empty list for
 * an agent id that does not exist, exactly as it does for a real agent with no
 * components — so "no components" was reported for a typo'd GUID, together
 * with advice to wait for provisioning that would never finish. Deliberately
 * unguarded: the Dataverse error ("Entity 'bot' With Id = … Does Not Exist")
 * is already clear, and it is the same error the other readers surface.
 */
async function assertAgentExists(api: DataverseApi, agentId: string): Promise<void> {
  await withCategory(() => api.getRow("bots", agentId, "botid"));
}

export async function handleListCopilotAgentComponents(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = listCopilotAgentComponentsInputSchema.parse(input);
  const api = requireApi(context.api);
  const agentId = assertGuid(parsed.agentId, "agentId");

  let filter = `_parentbotid_value eq ${agentId}`;
  if (parsed.componentType !== undefined) filter += ` and componenttype eq ${parsed.componentType}`;

  const select = parsed.includeContent
    ? "botcomponentid,name,componenttype,schemaname,modifiedon,data"
    : "botcomponentid,name,componenttype,schemaname,modifiedon";

  const page = await withCategory(() =>
    api.queryRows("botcomponents", { select, filter, top: parsed.limit }),
  );
  const rows = page.value.slice(0, parsed.limit);
  const incomplete = page.nextLink !== undefined || page.value.length > parsed.limit;

  if (rows.length === 0) {
    await assertAgentExists(api, agentId);
    if (incomplete) {
      return "The first Dataverse component page was empty but a continuation was returned. The component list is incomplete; narrow the componentType filter and try again.";
    }
    return parsed.componentType !== undefined
      ? `No ${componentTypeLabel(parsed.componentType)} components on this agent.`
      : "No components on this agent. If it was just created, provisioning is asynchronous — the default topics appear within about a minute.";
  }

  const lines = [
    incomplete
      ? `Components (showing the first ${rows.length}; more are available):`
      : `Components (${rows.length}):`,
    "",
  ];
  // A botcomponent `data` column can be large. Keep the aggregate MCP text
  // bounded and direct callers to the single-component reader for the rest.
  let remainingContentCharacters = 200_000;
  let contentWasTruncated = false;
  for (const row of rows) {
    lines.push(
      `  ${str(row, "name") ?? "(unnamed)"}  — ${describeComponentKind({
        componentType: num(row, "componenttype"),
        data:
          num(row, "componenttype") === COMPONENT_TYPES.testCase && !parsed.includeContent
            ? undefined
            : str(row, "data"),
        schemaName: str(row, "schemaname"),
      })}`,
    );
    lines.push(`    id: ${str(row, "botcomponentid") ?? "?"}`);
    const schema = str(row, "schemaname");
    if (schema) lines.push(`    schema name: ${schema}`);
    if (parsed.includeContent) {
      const data = str(row, "data");
      const full = data ?? "(empty)";
      const returned = full.slice(0, remainingContentCharacters);
      lines.push(
        "    content:",
        returned
          .split("\n")
          .map((line) => `      ${line}`)
          .join("\n"),
      );
      remainingContentCharacters -= returned.length;
      if (returned.length < full.length) {
        contentWasTruncated = true;
        lines.push("      … content truncated; use get_copilot_agent_component for this row");
      }
    }
    lines.push("");
  }
  if (incomplete) {
    lines.push(
      "⚠ This is not the complete component set. Dataverse returned a continuation; narrow componentType or inspect a known component with get_copilot_agent_component.",
    );
  }
  if (contentWasTruncated) {
    lines.push(
      "⚠ Component content hit the 200,000-character aggregate response bound. Metadata above is still complete for the displayed rows.",
    );
  }
  return lines.join("\n").trimEnd();
}

// =============================================================================
// create_copilot_agent
// =============================================================================

/** Copilot Studio product limits (stricter than the underlying Dataverse columns). */
const COPILOT_AGENT_NAME_MAX_LENGTH = 42;
const COPILOT_AGENT_INSTRUCTIONS_MAX_LENGTH = 8_000;
const copilotAgentNameSchema = z
  .string()
  .min(1)
  .max(COPILOT_AGENT_NAME_MAX_LENGTH)
  .refine((value) => !/[<>]/.test(value), { message: "Agent names cannot contain '<' or '>'." });

export const createCopilotAgentInputSchema = z.object({
  name: copilotAgentNameSchema,
  schemaName: z.string().min(1).max(100).optional(),
  language: z.number().int().optional().default(1033),
  instructions: z.string().max(COPILOT_AGENT_INSTRUCTIONS_MAX_LENGTH).optional(),
  solutionUniqueName: z.string().min(1).max(200).optional(),
});

export const createCopilotAgentTool = {
  name: "create_copilot_agent",
  description:
    "Create a Copilot Studio agent. Provisioning is ASYNCHRONOUS — the service adds the default topics about a minute later, so a component list taken immediately after will look empty. Optionally sets the agent's instructions and adds the agent to a solution in the same call. If schemaName is omitted it is derived from the name using the environment's publisher prefix.",
  inputSchema: {
    type: "object" as const,
    properties: {
      name: {
        type: "string",
        minLength: 1,
        maxLength: COPILOT_AGENT_NAME_MAX_LENGTH,
        pattern: "^[^<>]+$",
        description: "Display name (maximum 42 characters; cannot contain < or >)",
      },
      schemaName: {
        type: "string",
        description:
          "Unique schema name including the publisher prefix, e.g. 'cr866_hrAssistant'. All component schema names derive from it. Omit to derive one from the name and the publisher prefix.",
      },
      language: { type: "number", description: "LCID, default 1033 (English US)" },
      instructions: {
        type: "string",
        maxLength: COPILOT_AGENT_INSTRUCTIONS_MAX_LENGTH,
        description:
          "The agent's instructions, up to 8,000 characters (plain text; wrapped into the required YAML automatically)",
      },
      solutionUniqueName: {
        type: "string",
        description:
          "Add the new agent to this unmanaged solution. Without it the agent lands in the environment's default solution, which is not what you want for anything you intend to ship elsewhere.",
      },
    },
    required: ["name"],
  },
};

/** Solution component type for a Copilot Studio agent (`bot`). */
export const SOLUTION_COMPONENT_TYPE_BOT = 10110;

/**
 * Turn a display name into the schema-name segment after the publisher prefix.
 *
 * Lowercases the whole LEADING RUN of capitals, not just the first letter, so
 * "ZZ Helpdesk Agent" becomes `zzHelpdeskAgent` rather than `zZHelpdeskAgent`.
 * That matches how existing agents in a real environment are named, and the
 * naive first-character rule produces something that reads like a typo.
 */
export function agentSchemaSegment(name: string): string {
  const cleaned = name.replace(/[^A-Za-z0-9]/g, "");
  if (cleaned === "") return "agent";
  const leadingCaps = /^[A-Z]+/.exec(cleaned)?.[0] ?? "";
  if (leadingCaps.length <= 1) return cleaned.charAt(0).toLowerCase() + cleaned.slice(1);
  // Keep the last capital when it starts the next word: "HRChatbot" -> "hrChatbot".
  const keep = leadingCaps.length < cleaned.length ? leadingCaps.length - 1 : leadingCaps.length;
  return cleaned.slice(0, keep).toLowerCase() + cleaned.slice(keep);
}

/**
 * Find the publisher prefix to build a schema name from.
 *
 * Prefers the publisher of the solution the agent is being created in, since
 * that is the prefix that solution's components must carry. Falls back to the
 * prefix already in use in this environment, read from an existing agent —
 * which is more reliable than picking a publisher, because an environment can
 * have several and only one of them is the one agents actually use here.
 */
export async function resolvePublisherPrefix(
  api: DataverseApi,
  solutionUniqueName?: string,
): Promise<string | undefined> {
  if (solutionUniqueName !== undefined) {
    // Two plain queries rather than one $expand: queryRows' OData validation
    // rejects the parentheses in `publisherid($select=...)`.
    const solutions = (
      await api.queryRows("solutions", {
        select: "solutionid,_publisherid_value",
        filter: `uniquename eq '${solutionUniqueName.replace(/'/g, "''")}'`,
        top: 1,
      })
    ).value;
    const solutionRow = solutions[0];
    const publisherId =
      solutionRow === undefined ? undefined : str(solutionRow, "_publisherid_value");
    if (publisherId !== undefined && publisherId !== "") {
      const publishers = (
        await api.queryRows("publishers", {
          select: "customizationprefix",
          filter: `publisherid eq ${publisherId}`,
          top: 1,
        })
      ).value;
      const publisherRow = publishers[0];
      const prefix =
        publisherRow === undefined ? undefined : str(publisherRow, "customizationprefix");
      if (prefix !== undefined && prefix !== "") return prefix;
    }
  }

  const existing = (await api.queryRows("bots", { select: "schemaname", top: 20 })).value;
  for (const row of existing) {
    const schema = str(row, "schemaname");
    const underscore = schema?.indexOf("_") ?? -1;
    if (schema !== undefined && underscore > 0) return schema.slice(0, underscore);
  }
  return undefined;
}

/** Build the YAML a Custom GPT (instructions) component requires. */
export function buildInstructionsYaml(instructions: string): string {
  const indented = instructions
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) => (line === "" ? "" : `  ${line}`))
    .join("\n");
  return `kind: GptComponentMetadata\ninstructions: |-\n${indented}`;
}

export async function handleCreateCopilotAgent(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = createCopilotAgentInputSchema.parse(input);
  const api = requireApi(context.api);

  let schemaName = parsed.schemaName;
  let derivedNote: string | undefined;
  if (schemaName === undefined) {
    const prefix = await withCategory(() => resolvePublisherPrefix(api, parsed.solutionUniqueName));
    if (prefix === undefined) {
      throw new ValidationError(
        "Could not work out a publisher prefix for this environment, so schemaName cannot be derived — pass it explicitly, e.g. 'cr866_helpdeskAgent'.",
      );
    }
    schemaName = `${prefix}_${agentSchemaSegment(parsed.name)}`;
    derivedNote = `Derived schema name: ${schemaName}`;
  }

  // The special Default solution already contains every component. Confirm
  // its unique name from metadata before skipping a redundant attachment;
  // a display name, prefix match, or missing read must not be treated as proof.
  let systemDefaultSolution = false;
  if (parsed.solutionUniqueName?.toLowerCase() === "default") {
    const solutionPage = await withCategory(() =>
      api.queryRows("solutions", {
        select: "solutionid,uniquename",
        filter: `uniquename eq '${parsed.solutionUniqueName!.replace(/'/g, "''")}'`,
        top: 2,
      }),
    );
    if (
      solutionPage.nextLink ||
      solutionPage.value.length !== 1 ||
      str(solutionPage.value[0] as Row, "uniquename") !== "Default"
    ) {
      throw new ValidationError(
        "Could not confirm the special Default solution from its unique-name metadata. No agent was created; use a verified solution unique name or omit solutionUniqueName.",
      );
    }
    systemDefaultSolution = true;
  }

  logger.info({ name: parsed.name, schemaName }, "Executing create_copilot_agent");

  const created = await withCategory(() =>
    api.createRow("bots", {
      name: parsed.name,
      schemaname: schemaName,
      language: parsed.language,
    }),
  );

  const botid = str(created, "botid");
  if (!botid) {
    return `Created "${parsed.name}", but the service did not return its id — list agents to find it.`;
  }

  const lines = [`Created agent "${parsed.name}" (${botid}).`];
  if (derivedNote !== undefined) lines.push(derivedNote);

  if (parsed.instructions !== undefined) {
    await withCategory(() =>
      api.createRow("botcomponents", {
        name: parsed.name,
        componenttype: COMPONENT_TYPES.customGpt,
        language: parsed.language,
        schemaname: `${schemaName}.gpt.default`,
        data: buildInstructionsYaml(parsed.instructions ?? ""),
        "parentbotid@odata.bind": `/bots(${botid})`,
      }),
    );
    lines.push("Instructions set.");
  }

  if (systemDefaultSolution) {
    lines.push(
      'The agent is already included in the system "Default" solution. No separate solution attachment is needed.',
    );
  } else if (parsed.solutionUniqueName !== undefined) {
    // Adding the agent pulls its components in too, but they do not exist yet
    // — provisioning is async. Say so, rather than letting a caller believe
    // the solution already holds a complete agent.
    try {
      await withCategory(() =>
        api.executeUnboundAction(
          "AddSolutionComponent",
          {
            SolutionUniqueName: parsed.solutionUniqueName,
            ComponentId: botid,
            ComponentType: SOLUTION_COMPONENT_TYPE_BOT,
            AddRequiredComponents: true,
          },
          { context: "Add agent to solution" },
        ),
      );
      lines.push(
        `Added to solution "${parsed.solutionUniqueName}" — but its components are still provisioning, so add the agent again once they exist to bring them into the solution too.`,
      );
    } catch (err) {
      // The agent EXISTS at this point. Failing the whole call would leave a
      // real agent behind while reporting failure, which is worse than saying
      // exactly which part did not happen.
      const message = err instanceof Error ? err.message : String(err);
      lines.push(
        `⚠ The agent was created, but adding it to solution "${parsed.solutionUniqueName}" failed: ${message}`,
      );
    }
  }

  lines.push(
    "",
    "Provisioning runs asynchronously: the default topics (Greeting, Fallback, Escalate, …) appear about a minute from now.",
    "The agent is NOT published yet — call publish_copilot_agent when its content is ready.",
  );
  return lines.join("\n");
}

// =============================================================================
// clone_copilot_agent
// =============================================================================

/**
 * Component schema names are `<agentSchema>.<kind>.<Name>` — e.g.
 * `cr866_HRChatbot.topic.Greeting`. Cloning is therefore a prefix rewrite,
 * and it must be anchored: a blind string replace would also rewrite the
 * prefix where it appears inside component CONTENT (topics reference each
 * other as `System.Bot.Components.Topics.'<agentSchema>.topic.X'`), which is
 * wanted there too — but only as a whole-token match, never as a substring
 * of a longer name.
 */
export function rewriteSchemaName(
  schemaName: string,
  fromPrefix: string,
  toPrefix: string,
): string {
  if (schemaName === fromPrefix) return toPrefix;
  if (schemaName.startsWith(`${fromPrefix}.`)) {
    return `${toPrefix}${schemaName.slice(fromPrefix.length)}`;
  }
  return schemaName;
}

/** Rewrite cross-references to the source agent inside component content. */
export function rewriteComponentContent(
  content: string,
  fromPrefix: string,
  toPrefix: string,
): string {
  if (fromPrefix === "") return content;
  const escaped = fromPrefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return content.replace(new RegExp(`${escaped}(?=[.'"\\s]|$)`, "g"), toPrefix);
}

export const cloneCopilotAgentInputSchema = z.object({
  agentId: z.string().min(1),
  name: copilotAgentNameSchema,
  schemaName: z.string().min(1).max(100).optional(),
});

export const cloneCopilotAgentTool = {
  name: "clone_copilot_agent",
  description:
    "Copy a Copilot Studio agent — its instructions, topics, knowledge sources and test cases — into a new agent. Useful for getting a safe sandbox copy of a real agent before changing it. The copy is NOT published; publish it when you are happy with it.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: { type: "string", description: "The agent to copy (botid GUID)" },
      name: {
        type: "string",
        minLength: 1,
        maxLength: COPILOT_AGENT_NAME_MAX_LENGTH,
        pattern: "^[^<>]+$",
        description: "Display name for the copy (maximum 42 characters; cannot contain < or >)",
      },
      schemaName: {
        type: "string",
        description:
          "Unique schema name for the copy including publisher prefix, e.g. 'cr866_hrAssistantSandbox'",
      },
    },
    required: ["agentId", "name"],
  },
};

const CLONE_PROVISIONING_POLL_INTERVAL_MS = 5_000;
const CLONE_PROVISIONING_MIN_WAIT_MS = 60_000;
const CLONE_PROVISIONING_TIMEOUT_MS = 150_000;
const CLONE_PROVISIONING_STABLE_SNAPSHOTS = 3;

export async function handleCloneCopilotAgent(
  context: { api: DataverseApi | null; sleep?: (milliseconds: number) => Promise<void> },
  input: unknown,
): Promise<string> {
  const parsed = cloneCopilotAgentInputSchema.parse(input);
  const api = requireApi(context.api);
  const agentId = assertGuid(parsed.agentId, "agentId");
  // Same convenience create_copilot_agent has: derive the schema name from
  // the display name and the environment's publisher prefix when omitted,
  // rather than making the caller know the prefix.
  let schemaName = parsed.schemaName;
  let derivedNote: string | undefined;
  if (schemaName === undefined) {
    const prefix = await withCategory(() => resolvePublisherPrefix(api));
    if (prefix === undefined) {
      throw new ValidationError(
        "Could not work out a publisher prefix for this environment, so schemaName cannot be derived — pass it explicitly, e.g. 'cr866_helpdeskCopy'.",
      );
    }
    schemaName = `${prefix}_${agentSchemaSegment(parsed.name)}`;
    derivedNote = `Derived schema name: ${schemaName}`;
  }
  logger.info({ agentId, name: parsed.name, schemaName }, "Executing clone_copilot_agent");

  const source = await withCategory(() => api.getRow("bots", agentId, "name,schemaname,language"));
  const sourcePrefix = str(source, "schemaname");
  if (sourcePrefix === undefined) {
    throw new ValidationError(
      "The source agent has no schema name — cannot work out how to rewrite its components.",
    );
  }
  const language = num(source, "language") ?? 1033;

  const sourceComponentPage = await withCategory(() =>
    api.queryRows("botcomponents", {
      select: "botcomponentid,name,componenttype,schemaname,data,description",
      filter: `_parentbotid_value eq ${agentId}`,
      top: 5000,
    }),
  );
  if (sourceComponentPage.nextLink !== undefined) {
    throw new ValidationError(
      "The source agent has more than 5,000 components. Clone was refused before creating a target because a one-page copy would be incomplete.",
    );
  }
  const components = sourceComponentPage.value;

  const created = await withCategory(() =>
    api.createRow("bots", { name: parsed.name, schemaname: schemaName, language }),
  );
  const newBotId = str(created, "botid");
  if (newBotId === undefined) {
    throw new ValidationError(
      "The new agent was created but the service returned no id — find it with list_copilot_agents.",
    );
  }

  // Creating an agent provisions default topics ASYNCHRONOUSLY. Copying
  // before they land would race them: the service would then add its own
  // Fallback/Greeting/etc. on top of the copies, colliding on schema name.
  // Wait for provisioning, then upsert BY SCHEMA NAME so a default that
  // already exists is updated rather than duplicated.
  const sleep =
    context.sleep ??
    ((milliseconds: number) => new Promise<void>((resolve) => setTimeout(resolve, milliseconds)));
  let elapsedMilliseconds = 0;
  let targetComponents: Row[];
  let previousSnapshot: string | undefined;
  let stableSnapshots = 0;
  let stableSinceMilliseconds: number | undefined;
  let provisioningSettled = false;
  for (;;) {
    const targetPage = await withCategory(() =>
      api.queryRows("botcomponents", {
        select: "botcomponentid,schemaname,componenttype,name,modifiedon",
        filter: `_parentbotid_value eq ${newBotId}`,
        top: 5000,
      }),
    );
    if (targetPage.nextLink !== undefined) {
      throw new ValidationError(
        `Target agent ${newBotId} was created, but its provisioned component set exceeds one Dataverse page. No source components were copied and no target defaults were deleted.`,
      );
    }
    targetComponents = targetPage.value;
    const snapshot = targetComponents
      .map(
        (component) =>
          `${str(component, "botcomponentid") ?? "?"}|${str(component, "schemaname") ?? "?"}|${num(component, "componenttype") ?? "?"}|${str(component, "name") ?? "?"}|${str(component, "modifiedon") ?? "?"}`,
      )
      .sort()
      .join("\n");
    if (targetComponents.length === 0) {
      stableSnapshots = 0;
      stableSinceMilliseconds = undefined;
    } else if (snapshot === previousSnapshot) {
      stableSnapshots += 1;
    } else {
      stableSnapshots = 1;
      stableSinceMilliseconds = elapsedMilliseconds;
    }
    previousSnapshot = snapshot;

    if (
      elapsedMilliseconds >= CLONE_PROVISIONING_MIN_WAIT_MS &&
      stableSinceMilliseconds !== undefined &&
      elapsedMilliseconds - stableSinceMilliseconds >= CLONE_PROVISIONING_MIN_WAIT_MS &&
      stableSnapshots >= CLONE_PROVISIONING_STABLE_SNAPSHOTS
    ) {
      provisioningSettled = true;
      break;
    }
    if (elapsedMilliseconds >= CLONE_PROVISIONING_TIMEOUT_MS) break;
    const delay = Math.min(
      CLONE_PROVISIONING_POLL_INTERVAL_MS,
      CLONE_PROVISIONING_TIMEOUT_MS - elapsedMilliseconds,
    );
    await sleep(delay);
    elapsedMilliseconds += delay;
  }

  if (!provisioningSettled) {
    throw new ValidationError(
      `Target agent ${newBotId} was created, but its asynchronous default-component provisioning did not settle within 150 seconds. No source components were copied and no target defaults were deleted; inspect the target before retrying or removing it.`,
    );
  }

  const existingBySchema = new Map<string, string>();
  for (const component of targetComponents) {
    const schema = str(component, "schemaname");
    const id = str(component, "botcomponentid");
    if (schema !== undefined && id !== undefined) existingBySchema.set(schema, id);
  }

  let copied = 0;
  let updated = 0;
  const failures: string[] = [];
  const copiedSchemas = new Set<string>();
  for (const component of components) {
    const sourceSchema = str(component, "schemaname");
    const type = num(component, "componenttype");
    if (sourceSchema === undefined || type === undefined) continue;
    const targetSchema = rewriteSchemaName(sourceSchema, sourcePrefix, schemaName);
    const data = rewriteComponentContent(str(component, "data") ?? "", sourcePrefix, schemaName);
    // The instructions component is displayed under the AGENT's name; keeping
    // the source's name there would label the copy's instructions "Source Agent"
    // inside "Cloned Agent".
    const sourceName = str(component, "name");
    const name =
      type === COMPONENT_TYPES.customGpt &&
      sourceName !== undefined &&
      sourceName === str(source, "name")
        ? parsed.name
        : (sourceName ?? targetSchema);
    copiedSchemas.add(targetSchema);

    try {
      const existingId = existingBySchema.get(targetSchema);
      if (existingId !== undefined) {
        await api.updateRow("botcomponents", existingId, { data });
        updated += 1;
      } else {
        const row: Record<string, unknown> = {
          name,
          componenttype: type,
          language,
          schemaname: targetSchema,
          data,
          "parentbotid@odata.bind": `/bots(${newBotId})`,
        };
        const description = str(component, "description");
        if (description !== undefined) row["description"] = description;
        await api.createRow("botcomponents", row);
        copied += 1;
      }
    } catch (err) {
      failures.push(`${name}: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  // A faithful copy has the SOURCE's topic set. Provisioning gives every new
  // agent the same defaults (Greeting, Fallback, the three Lessons, ...); if the
  // source had removed some of them, the copy must not grow them back.
  let removedDefaults = 0;
  for (const component of targetComponents) {
    const schema = str(component, "schemaname");
    const id = str(component, "botcomponentid");
    if (schema === undefined || id === undefined || copiedSchemas.has(schema)) continue;
    if (num(component, "componenttype") !== COMPONENT_TYPES.topic) continue;
    try {
      await api.deleteRow("botcomponents", id);
      removedDefaults += 1;
    } catch (err) {
      failures.push(
        `remove default ${str(component, "name") ?? schema}: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  const lines = [
    `Cloned "${str(source, "name") ?? agentId}" into "${parsed.name}" (${newBotId}).`,
    ...(derivedNote !== undefined ? [derivedNote] : []),
    `  ${copied} components created, ${updated} provisioned defaults overwritten with the source's version${removedDefaults > 0 ? `, ${removedDefaults} provisioned default topic${removedDefaults === 1 ? "" : "s"} removed because the source does not have ${removedDefaults === 1 ? "it" : "them"}` : ""}.`,
  ];
  if (failures.length > 0) {
    lines.push("", `⚠ ${failures.length} components did not copy:`);
    failures.slice(0, 5).forEach((f) => lines.push(`  • ${f.slice(0, 160)}`));
    if (failures.length > 5) lines.push(`  … and ${failures.length - 5} more`);
  }
  lines.push(
    "",
    "The copy is NOT published. Settings that live on the agent itself — authentication, access policy, generative orchestration, channel registrations — are NOT copied; set those on the copy if it needs them.",
  );
  return lines.join("\n");
}

// =============================================================================
// set_copilot_agent_instructions
// =============================================================================

const SOURCE_SHA256_PATTERN = /^[a-fA-F0-9]{64}$/;
const GUARDED_COMPONENT_SELECT = "botcomponentid,componenttype,_parentbotid_value,data,name,description";
const MCP_BINDING_REVIEW_NOTE = "MCP source authoring may require end-user connection binding or renewed review. A physical connection marked Connected does not prove the end user's current binding is reviewed. No reauthorization, grants or agent rerun were performed.";

function isMcpTaskDialogSource(source: string): boolean {
  try {
    const document = parseDocument(source, { uniqueKeys: true, strict: true });
    if (document.errors.length !== 0 || !isMap(document.contents)) return false;
    const action = document.contents.get("action", true);
    if (!isMap(action)) return false;
    const details = action.get("operationDetails", true);
    if (!isMap(details)) return false;
    const rootKind: unknown = document.contents.get("kind", true);
    const actionKind: unknown = action.get("kind", true);
    const detailsKind: unknown = details.get("kind", true);
    return isScalar(rootKind) && rootKind.value === "TaskDialog" &&
      isScalar(actionKind) && actionKind.value === "InvokeExternalAgentTaskAction" &&
      isScalar(detailsKind) && detailsKind.value === "ModelContextProtocolMetadata";
  } catch {
    // This optional source-specific note never changes authoring acceptance.
    return false;
  }
}

function sourceSha256(source: string): string {
  return createHash("sha256").update(source, "utf8").digest("hex");
}

function guardedComponentIdentity(row: Row, componentId: string, agentId: string, expectedType?: number): number {
  if (row === null || typeof row !== "object" || Array.isArray(row) ||
      str(row, "botcomponentid")?.toLowerCase() !== componentId.toLowerCase() ||
      str(row, "_parentbotid_value")?.toLowerCase() !== agentId.toLowerCase()) {
    throw new ValidationError("Guarded update requires the exact component and agent identity; no write sent.");
  }
  const type = num(row, "componenttype");
  if (type === undefined || !Number.isInteger(type) || type < 0 || type > 20 ||
      (expectedType !== undefined && type !== expectedType)) {
    throw new ValidationError("Guarded update requires the expected component type; no write sent.");
  }
  return type;
}

function guardedComponentPreflight(row: Row, componentId: string, agentId: string, expectedHash: string, expectedType?: number) {
  const componentType = guardedComponentIdentity(row, componentId, agentId, expectedType);
  const source = str(row, "data");
  if (source === undefined || source.length > 500_000) {
    throw new ValidationError("Guarded update requires complete source within the 500000-character bound; no write sent.");
  }
  if (sourceSha256(source) !== expectedHash.toLowerCase()) {
    throw new ValidationError("Component source changed since the preview; refresh and review the source before updating. No write sent.");
  }
  const etag = str(row, "@odata.etag");
  if (etag === undefined || !/^W\/"[0-9]{1,20}"$/.test(etag)) {
    throw new ValidationError("Guarded update requires a supported current Dataverse row ETag; no write sent.");
  }
  return { componentType, etag };
}

async function guardedRead<T>(read: () => Promise<T>): Promise<T> {
  try {
    return await read();
  } catch (error) {
    const status = error instanceof ApiError && error.statusCode !== undefined
      ? error.statusCode : 502;
    throw new CopilotComponentUpdateError("preflight", status);
  }
}

async function guardedComponentWrite(
  api: DataverseApi,
  componentId: string,
  agentId: string,
  componentType: number,
  data: { data: string; name?: string; description?: string },
  etag: string,
): Promise<string> {
  // The row precondition is enforced by Dataverse, not a check-then-unconditional write.
  try {
    await api.updateCopilotComponentIfUnchanged(componentId, data, etag);
  } catch (error) {
    if (error instanceof CopilotComponentUpdateError) throw error;
    throw new CopilotComponentUpdateError("transport");
  }
  let verified = false;
  try {
    const after = await api.getRow("botcomponents", componentId, GUARDED_COMPONENT_SELECT);
    guardedComponentIdentity(after, componentId, agentId, componentType);
    verified = after.data === data.data &&
      (data.name === undefined || after.name === data.name) &&
      (data.description === undefined || after.description === data.description);
  } catch {
    // A failed readback does not erase an accepted write or justify resubmission.
    // Never disclose returned source or provider errors in this result.
  }
  return [
    `Guarded update accepted for component ${componentId}.`,
    verified
      ? "Readback verified: exact source and requested metadata matched at observation time."
      : "Readback not verified: the returned identity/source/metadata did not match or the read was unavailable. Do not retry the write automatically; inspect the component.",
    "This does not verify publication, service compilation or runtime behavior. Publish the agent separately for draft changes to reach users.",
  ].join("\n");
}

export const setCopilotAgentInstructionsInputSchema = z.object({
  agentId: z.string().min(1),
  instructions: z.string().min(1).max(COPILOT_AGENT_INSTRUCTIONS_MAX_LENGTH),
  expectedSourceSha256: z.string().regex(SOURCE_SHA256_PATTERN).optional(),
});

export const setCopilotAgentInstructionsTool = {
  name: "set_copilot_agent_instructions",
  description:
    "Set (or replace) a Copilot Studio agent's instructions — the system prompt shown on the agent's Overview page. Creates the instructions component if absent. Optional expectedSourceSha256 guards an existing update using the exact stored source hash and a conditional row ETag; guarded creation and ambiguous targets are refused. Readback is reported separately from write acceptance. Does not publish or prove runtime behavior.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: { type: "string", description: "The agent's botid (GUID)" },
      expectedSourceSha256: {
        type: "string", pattern: SOURCE_SHA256_PATTERN.source,
        description: "Optional SHA-256 of the existing component's full stored UTF-8 source, from get_copilot_agent_component with includeContent=true. Not the instructions text alone. Refuses stale, missing or ambiguous targets; no guarded creation.",
      },
      instructions: {
        type: "string",
        minLength: 1,
        maxLength: COPILOT_AGENT_INSTRUCTIONS_MAX_LENGTH,
        description:
          "The full instructions text, up to 8,000 characters (replaces any existing text)",
      },
    },
    required: ["agentId", "instructions"],
  },
};

export async function handleSetCopilotAgentInstructions(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = setCopilotAgentInstructionsInputSchema.parse(input);
  const api = requireApi(context.api);
  const agentId = assertGuid(parsed.agentId, "agentId");
  logger.info({ agentId }, "Executing set_copilot_agent_instructions");

  if (parsed.expectedSourceSha256 !== undefined) {
    const found = await guardedRead(() => api.queryRows("botcomponents", {
      select: "botcomponentid",
      filter: `_parentbotid_value eq ${agentId} and componenttype eq ${COMPONENT_TYPES.customGpt}`,
      top: 2,
    }));
    if (found === null || typeof found !== "object" || !Array.isArray(found.value) || found.value.length !== 1 || found.nextLink !== undefined) {
      throw new ValidationError("Guarded instructions update requires exactly one existing instructions component and a complete result; no write or create sent.");
    }
    const selected = found.value[0];
    const id = selected !== null && typeof selected === "object" ? str(selected, "botcomponentid") : undefined;
    if (id === undefined || !GUID_PATTERN.test(id)) {
      throw new ValidationError("Guarded instructions update requires a valid existing component ID; no write sent.");
    }
    const current = await guardedRead(() => api.getRow("botcomponents", id, GUARDED_COMPONENT_SELECT));
    const { etag } = guardedComponentPreflight(current, id, agentId, parsed.expectedSourceSha256, COMPONENT_TYPES.customGpt);
    return guardedComponentWrite(api, id, agentId, COMPONENT_TYPES.customGpt, { data: buildInstructionsYaml(parsed.instructions) }, etag);
  }

  const existing = (
    await withCategory(() =>
      api.queryRows("botcomponents", {
        select: "botcomponentid,name",
        filter: `_parentbotid_value eq ${agentId} and componenttype eq ${COMPONENT_TYPES.customGpt}`,
        top: 5,
      }),
    )
  ).value;

  const yaml = buildInstructionsYaml(parsed.instructions);

  if (existing.length > 0) {
    const id = str(existing[0] as Row, "botcomponentid");
    if (!id) throw new ValidationError("Instructions component has no id — cannot update it.");
    await withCategory(() => api.updateRow("botcomponents", id, { data: yaml }));
    return [
      "Instructions updated.",
      "",
      "Publish the agent for this to reach users (publish_copilot_agent).",
    ].join("\n");
  }

  const bot = await withCategory(() => api.getRow("bots", agentId, "name,schemaname,language"));
  const schemaName = str(bot, "schemaname");
  if (!schemaName)
    throw new ValidationError(
      "Agent has no schema name — cannot derive the component schema name.",
    );

  await withCategory(() =>
    api.createRow("botcomponents", {
      name: str(bot, "name") ?? "Instructions",
      componenttype: COMPONENT_TYPES.customGpt,
      language: num(bot, "language") ?? 1033,
      schemaname: `${schemaName}.gpt.default`,
      data: yaml,
      "parentbotid@odata.bind": `/bots(${agentId})`,
    }),
  );
  return [
    "Instructions created and set.",
    "",
    "Publish the agent for this to reach users (publish_copilot_agent).",
  ].join("\n");
}

// =============================================================================
// upsert_copilot_agent_component (topics, knowledge, test cases)
// =============================================================================

export const upsertCopilotAgentComponentInputSchema = z.object({
  agentId: z.string().min(1),
  componentId: z.string().optional(),
  name: z.string().min(1).max(200).optional(),
  componentType: z.number().int().min(0).max(20).optional(),
  schemaName: z.string().min(1).max(200).optional(),
  content: z.string().min(1).max(500_000),
  description: z.string().max(2000).optional(),
  expectedSourceSha256: z.string().regex(SOURCE_SHA256_PATTERN).optional(),
});

export const upsertCopilotAgentComponentTool = {
  name: "upsert_copilot_agent_component",
  description:
    "Create or update one agent component (topic, knowledge source, test case, settings) from its YAML authoring source. Pass componentId to update, or name+componentType+schemaName to create. Optional expectedSourceSha256 guards existing updates using exact stored source and a conditional row ETag; guarded creation is refused. Readback is reported separately from write acceptance. Dataverse does not validate source: lint is advisory, and saved content still needs designer/publish validation.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: { type: "string", description: "The agent's botid (GUID)" },
      componentId: {
        type: "string",
        description: "Existing botcomponentid to UPDATE (omit to create)",
      },
      expectedSourceSha256: {
        type: "string", pattern: SOURCE_SHA256_PATTERN.source,
        description: "Optional SHA-256 of full stored UTF-8 source from get_copilot_agent_component with includeContent=true. Requires componentId; stale source or unsupported ETag refuses the write. No automatic retry.",
      },
      name: { type: "string", description: "Display name (required when creating)" },
      componentType: {
        type: "number",
        description:
          "9=topic, 16=knowledge source, 18=settings, 19=test case (required when creating)",
      },
      schemaName: {
        type: "string",
        description:
          "Unique schema name (required when creating), e.g. '<agentSchema>.topic.MyTopic'",
      },
      content: { type: "string", description: "The component's YAML authoring source" },
      description: { type: "string", description: "Optional description" },
    },
    required: ["agentId", "content"],
  },
};

export async function handleUpsertCopilotAgentComponent(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = upsertCopilotAgentComponentInputSchema.parse(input);
  const api = requireApi(context.api);
  const agentId = assertGuid(parsed.agentId, "agentId");

  if (parsed.componentId === undefined) {
    if (parsed.expectedSourceSha256 !== undefined) {
      throw new ValidationError("expectedSourceSha256 requires an existing componentId; guarded creation is not supported.");
    }
    const missing = (["name", "componentType", "schemaName"] as const).filter(
      (k) => parsed[k] === undefined,
    );
    if (missing.length > 0) {
      throw new ValidationError(
        `Creating a component needs ${missing.join(", ")}. To update an existing one, pass componentId instead.`,
      );
    }
  }

  const componentId =
    parsed.componentId === undefined ? undefined : assertGuid(parsed.componentId, "componentId");
  let componentType = parsed.componentType;
  let guardedEtag: string | undefined;
  let guardedSourceChanged = false;
  if (componentId !== undefined) {
    const current = parsed.expectedSourceSha256 === undefined
      ? await withCategory(() => api.getRow("botcomponents", componentId, "componenttype,_parentbotid_value"))
      : await guardedRead(() => api.getRow("botcomponents", componentId, GUARDED_COMPONENT_SELECT));
    if (parsed.expectedSourceSha256 !== undefined) {
      const guard = guardedComponentPreflight(current, componentId, agentId, parsed.expectedSourceSha256, componentType);
      componentType = guard.componentType;
      guardedEtag = guard.etag;
      guardedSourceChanged = current.data !== parsed.content;
    } else {
      assertComponentBelongsToAgent(current, componentId, agentId);
      componentType ??= num(current, "componenttype");
    }
  }

  const problems = componentType === undefined ? [] : lintComponent(componentType, parsed.content);
  const warning =
    problems.length > 0
      ? [
          "",
          "⚠ Content problems found (saved anyway — Dataverse does not validate, so this is advisory):",
          ...problems.map((p) => `  • ${p}`),
        ]
      : [];

  if (componentId !== undefined) {
    const data: { data: string; name?: string; description?: string } = { data: parsed.content };
    if (parsed.name !== undefined) data["name"] = parsed.name;
    if (parsed.description !== undefined) data["description"] = parsed.description;
    if (guardedEtag !== undefined) {
      const result = await guardedComponentWrite(api, componentId, agentId, componentType!, data, guardedEtag);
      return [
        result,
        ...(problems.length === 0 ? [] : [`Advisory lint found ${problems.length} source problem(s); saved source still needs designer validation.`]),
        ...(guardedSourceChanged && componentType === COMPONENT_TYPES.topic && isMcpTaskDialogSource(parsed.content)
          ? [MCP_BINDING_REVIEW_NOTE] : []),
      ].join("\n");
    }
    await withCategory(() => api.updateRow("botcomponents", componentId, data));
    return [
      `Updated component ${componentId}.`,
      ...warning,
      "",
      "Open the agent in Copilot Studio to confirm it renders, then publish.",
    ].join("\n");
  }

  const row: Record<string, unknown> = {
    name: parsed.name,
    componenttype: componentType,
    schemaname: parsed.schemaName,
    data: parsed.content,
    "parentbotid@odata.bind": `/bots(${agentId})`,
  };
  if (parsed.description !== undefined) row["description"] = parsed.description;

  const created = await withCategory(() => api.createRow("botcomponents", row));
  return [
    `Created ${componentTypeLabel(componentType)} "${parsed.name ?? ""}" (${str(created, "botcomponentid") ?? "?"}).`,
    ...warning,
    "",
    "Open the agent in Copilot Studio to confirm it renders, then publish.",
  ].join("\n");
}

// =============================================================================
// configure_copilot_agent
// =============================================================================

/**
 * Settings that live inside `bot.configuration`, keyed by where they sit in
 * that JSON. Every one of these was read off a REAL configured agent — none is
 * invented — and the blob also carries telephony, speech and recognizer
 * sections that this tool must never disturb.
 */
const AGENT_CONFIG_FLAGS: Record<
  string,
  { path: readonly string[]; kind?: string; description: string }
> = {
  generativeOrchestration: {
    path: ["settings", "GenerativeActionsEnabled"],
    description:
      "Let the agent plan across topics, knowledge and tools instead of matching one topic",
  },
  connectableByOtherAgents: {
    path: ["isAgentConnectable"],
    description: "Allow other agents to connect to this one",
  },
  useModelKnowledge: {
    path: ["aISettings", "useModelKnowledge"],
    kind: "AISettings",
    description:
      "Let the model answer from its own general knowledge, not only your knowledge sources",
  },
  fileAnalysis: {
    path: ["aISettings", "isFileAnalysisEnabled"],
    kind: "AISettings",
    description: "Allow the agent to analyse files supplied in a conversation",
  },
  semanticSearch: {
    path: ["aISettings", "isSemanticSearchEnabled"],
    kind: "AISettings",
    description:
      "Store aISettings.isSemanticSearchEnabled. This stored flag alone does not prove effective tenant graph grounding: that feature also requires Authenticate with Microsoft, generative orchestration, and tenant licensing/semantic-index availability. No authentication, licensing or tenant setting is changed here.",
  },
  useLatestModels: {
    path: ["aISettings", "optInUseLatestModels"],
    kind: "AISettings",
    description: "Opt in to newer models as Microsoft ships them",
  },
};

/**
 * Topic node kinds (`beginDialog.actions[].kind`) and the property publish
 * demands first for each. Mapped by probing the publish validator with one
 * topic per kind: a real kind missing something fails "Missing required
 * property 'X'", an invented kind fails "Node is unknown to the system". All
 * twelve below are real. Three (EndConversation, TransferConversationV2,
 * LogCustomTelemetryEvent) publish with nothing but kind and id.
 *
 * Property names in the YAML are camelCase; the validator's messages spell
 * them in PascalCase. Do not copy the message casing into the YAML.
 */
export const TOPIC_NODE_REQUIREMENTS: Record<string, readonly string[]> = {
  SendActivity: ["activity"],
  Question: ["variable"],
  SetVariable: ["variable"],
  ConditionGroup: ["conditions"],
  InvokeFlowAction: ["flowId"],
  InvokeConnectorAction: ["connectionReference"],
  // Experimental Microsoft authoring-schema evidence, not a tenant publish probe:
  // https://github.com/microsoft/skills-for-copilot-studio/blob/1838950fa95f1d2f8365e826acd8a600ff593bd9/reference/bot.schema.yaml-authoring.json
  // InvokeMcpToolAction is a topic DialogAction, not a TaskDialog action.
  InvokeMcpToolAction: ["serverSchemaName", "toolName"],
  SearchAndSummarizeContent: ["userInput"],
  HttpRequestAction: ["url"],
  GotoAction: ["actionId"],
  EndConversation: [],
  TransferConversationV2: [],
  LogCustomTelemetryEvent: [],
  // Second probe batch, plus kinds first seen on a production agent. The
  // minimums come from the validator, NOT from what that agent happened to
  // set: ClearAllVariables and CSATQuestion publish with kind and id alone,
  // even though production topics give them `variables` / `conversationOutcome`.
  BeginDialog: ["dialog"],
  // Passed publish with `dialog` (production uses it to hand off to Fallback).
  // Whether `dialog` is REQUIRED was not probed, so no requirement is asserted.
  ReplaceDialog: [],
  ParseValue: ["variable"],
  Foreach: ["items"],
  AdaptiveCardPrompt: ["card"],
  SetTextVariable: ["variable"],
  EditTable: ["itemsVariable"],
  RepeatDialog: [],
  CreateSearchQuery: [],
  ClearAllVariables: [],
  CancelAllDialogs: [],
  CSATQuestion: [],
  // Real, but it fails publish with "Element is invalid:
  // AuthenticationInputNotEnabled" unless the AGENT has authentication
  // configured — a constraint on the agent, not on the node's content, so it
  // cannot be checked here. `SignIn` is NOT a kind; the sign-in node is this one.
  OAuthInput: [],
};

/** Node kinds that publish only when the agent itself has authentication configured. */
export const NODE_KINDS_NEEDING_AGENT_AUTH = new Set(["OAuthInput"]);

/**
 * Walk a topic's YAML and lint each `- kind:` node against what publish
 * requires. This is a LINE-BASED read of the indentation, not a YAML parser,
 * so it checks only that a required key appears somewhere below the node at
 * a deeper indent than the `- kind:` line — enough to catch the omissions
 * that fail publish, without claiming to understand the whole schema.
 */
export function lintTopicNodes(data: string): string[] {
  const problems: string[] = [];
  const lines = data.split("\n");
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i] ?? "";
    const m = /^(\s*)-\s+kind:\s*(\S+)/.exec(line);
    if (m === null) continue;
    const indent = (m[1] ?? "").length;
    const kind = m[2] ?? "";
    // Only nodes inside an actions list — the top-level `beginDialog: kind:`
    // is a trigger, and `- kind: ManualTaskInput` in a tool is not a topic node.
    if (kind.endsWith("TaskInput")) continue;
    if (NODE_KINDS_NEEDING_AGENT_AUTH.has(kind)) {
      problems.push(
        `${kind} node publishes only if the agent has authentication configured — otherwise publish fails with "AuthenticationInputNotEnabled". Check the agent's authentication mode, not this topic`,
      );
    }
    const required = TOPIC_NODE_REQUIREMENTS[kind];
    if (required === undefined) {
      if (/^[A-Z]/.test(kind) && !/Intent|Dialog|Trigger/.test(kind)) {
        problems.push(
          `node kind '${kind}' is not one this server has verified (${Object.keys(TOPIC_NODE_REQUIREMENTS).filter((knownKind) => knownKind !== "InvokeMcpToolAction").join(", ")}). A real kind will publish; an invented one fails publish with "Node is unknown to the system"`,
        );
      }
      continue;
    }
    // Collect the keys belonging to this node: subsequent lines indented deeper
    // than the dash, stopping at the next sibling or parent.
    const keys = new Set<string>();
    for (let j = i + 1; j < lines.length; j += 1) {
      const next = lines[j] ?? "";
      if (next.trim() === "") continue;
      const nextIndent = next.length - next.trimStart().length;
      if (nextIndent <= indent) break;
      const key = /^\s*(\w+):/.exec(next);
      if (key !== null && nextIndent === indent + 2) keys.add(key[1] ?? "");
    }
    for (const req of required) {
      if (!keys.has(req)) {
        problems.push(
          kind === "InvokeMcpToolAction"
            ? `${kind} node needs '${req}' per the experimental Microsoft authoring schema; publication and runtime behavior are not verified`
            : `${kind} node needs '${req}' — publish rejects it with "Missing required property"`,
        );
      }
    }
  }
  return problems;
}

const CONTENT_MODERATION_PATH = ["aISettings", "contentModeration"] as const;
const CONTENT_MODERATION_LEVELS = ["Low", "Medium", "High"] as const;

function parseConfiguration(raw: unknown): Record<string, unknown> {
  if (typeof raw === "string" && raw.trim() !== "") {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (parsed !== null && typeof parsed === "object") return parsed as Record<string, unknown>;
    } catch {
      // Fall through: a blob we cannot parse must not be silently replaced.
      throw new ValidationError(
        "This agent's configuration is not valid JSON, so changing one setting would mean overwriting the whole thing. Read it with get_copilot_agent and fix it before using this tool.",
      );
    }
  }
  if (raw !== null && typeof raw === "object") return raw as Record<string, unknown>;
  return { $kind: "BotConfiguration" };
}

/**
 * Set one value deep in the configuration, creating only the containers it
 * needs and leaving every sibling alone. Replacing the blob wholesale would
 * silently drop an agent's telephony, speech and recognizer settings.
 */
export function applyConfigValue(
  config: Record<string, unknown>,
  path: readonly string[],
  value: unknown,
  containerKind?: string,
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...config };
  let cursor = next;
  for (let i = 0; i < path.length - 1; i += 1) {
    const key = path[i] as string;
    const existing = cursor[key];
    const child: Record<string, unknown> =
      existing !== null && typeof existing === "object"
        ? { ...(existing as Record<string, unknown>) }
        : {};
    if (containerKind !== undefined && child["$kind"] === undefined) child["$kind"] = containerKind;
    cursor[key] = child;
    cursor = child;
  }
  cursor[path[path.length - 1] as string] = value;
  return next;
}

export function readConfigValue(config: Record<string, unknown>, path: readonly string[]): unknown {
  let cursor: unknown = config;
  for (const key of path) {
    if (cursor === null || typeof cursor !== "object") return undefined;
    cursor = (cursor as Record<string, unknown>)[key];
  }
  return cursor;
}

export const configureCopilotAgentInputSchema = z.object({
  agentId: z.string().min(1),
  generativeOrchestration: z.boolean().optional(),
  connectableByOtherAgents: z.boolean().optional(),
  useModelKnowledge: z.boolean().optional(),
  fileAnalysis: z.boolean().optional(),
  semanticSearch: z.boolean().optional(),
  useLatestModels: z.boolean().optional(),
  contentModeration: z.enum(CONTENT_MODERATION_LEVELS).optional(),
});

export const configureCopilotAgentTool = {
  name: "configure_copilot_agent",
  description:
    "Read or change a Copilot Studio agent's stored behaviour settings — generative orchestration, whether other agents may connect to it, whether it may answer from the model's own knowledge, file analysis, semantic search, content moderation, and opting in to newer models. Call with only agentId to see stored values without changing anything. Stored semanticSearch alone does not verify effective tenant graph grounding; Authenticate with Microsoft and tenant feature availability are also required. Settings are MERGED into the agent's existing configuration, never replacing it: the same blob also holds telephony, speech and recognizer sections that a wholesale write would destroy. Publish the agent afterwards for changes to reach the running agent.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: { type: "string", description: "The agent's botid (GUID)" },
      generativeOrchestration: {
        type: "boolean",
        description: AGENT_CONFIG_FLAGS["generativeOrchestration"]?.description,
      },
      connectableByOtherAgents: {
        type: "boolean",
        description: AGENT_CONFIG_FLAGS["connectableByOtherAgents"]?.description,
      },
      useModelKnowledge: {
        type: "boolean",
        description: AGENT_CONFIG_FLAGS["useModelKnowledge"]?.description,
      },
      fileAnalysis: {
        type: "boolean",
        description: AGENT_CONFIG_FLAGS["fileAnalysis"]?.description,
      },
      semanticSearch: {
        type: "boolean",
        description: AGENT_CONFIG_FLAGS["semanticSearch"]?.description,
      },
      useLatestModels: {
        type: "boolean",
        description: AGENT_CONFIG_FLAGS["useLatestModels"]?.description,
      },
      contentModeration: {
        type: "string",
        enum: [...CONTENT_MODERATION_LEVELS],
        description: "How aggressively the agent filters content",
      },
    },
    required: ["agentId"],
  },
};

export async function handleConfigureCopilotAgent(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = configureCopilotAgentInputSchema.parse(input);
  const api = requireApi(context.api);
  const agentId = assertGuid(parsed.agentId, "agentId");

  const bot = await withCategory(() => api.getRow("bots", agentId, "name,configuration"));
  const current = parseConfiguration(bot["configuration"]);

  const requested: { label: string; path: readonly string[]; value: unknown; kind?: string }[] = [];
  for (const [label, flag] of Object.entries(AGENT_CONFIG_FLAGS)) {
    const value = (parsed as Record<string, unknown>)[label];
    if (typeof value === "boolean") {
      requested.push({
        label,
        path: flag.path,
        value,
        ...(flag.kind !== undefined ? { kind: flag.kind } : {}),
      });
    }
  }
  if (parsed.contentModeration !== undefined) {
    requested.push({
      label: "contentModeration",
      path: CONTENT_MODERATION_PATH,
      value: parsed.contentModeration,
      kind: "AISettings",
    });
  }

  if (requested.length === 0) {
    const lines = [`Configuration for "${str(bot, "name") ?? agentId}":`, ""];
    for (const [label, flag] of Object.entries(AGENT_CONFIG_FLAGS)) {
      const value = readConfigValue(current, flag.path);
      lines.push(
        `  ${label}: ${value === undefined ? "not set (platform default)" : String(value)}`,
      );
    }
    const moderation = readConfigValue(current, CONTENT_MODERATION_PATH);
    lines.push(
      `  contentModeration: ${moderation === undefined ? "not set (platform default)" : String(moderation)}`,
    );
    lines.push(
      "",
      "Nothing was changed. A setting reported as 'not set' is absent from the agent's configuration, so the platform's default applies — the value shown in Copilot Studio for such a setting is that default, not a stored choice.",
    );
    return lines.join("\n");
  }

  let next = current;
  const changes: string[] = [];
  for (const change of requested) {
    const before = readConfigValue(next, change.path);
    next = applyConfigValue(next, change.path, change.value, change.kind);
    changes.push(
      `  ${change.label}: ${before === undefined ? "not set" : String(before)} -> ${String(change.value)}`,
    );
  }

  await withCategory(() => api.updateRow("bots", agentId, { configuration: JSON.stringify(next) }));

  return [
    `Updated configuration for "${str(bot, "name") ?? agentId}".`,
    ...changes,
    "",
    "Other sections of the configuration (telephony, speech, recognizer) were left untouched.",
    "Publish the agent for this to reach the running agent.",
  ].join("\n");
}

// =============================================================================
// set_copilot_agent_access
// =============================================================================

/**
 * `bot.accesscontrolpolicy` option values, read from the live picklist:
 *   0 Any · 1 Agent readers · 2 Group membership · 3 Any (multi-tenant)
 */
const ACCESS_POLICY_VALUES = {
  anyone: 0,
  agentReaders: 1,
  securityGroups: 2,
  anyoneMultiTenant: 3,
} as const;

export const setCopilotAgentAccessInputSchema = z.object({
  agentId: z.string().min(1),
  policy: z.enum(["anyone", "agentReaders", "securityGroups", "anyoneMultiTenant"]).optional(),
  securityGroupIds: z.array(z.string().min(1)).max(20).optional(),
  confirmOpenAccess: z.boolean().optional(),
});

export const setCopilotAgentAccessTool = {
  name: "set_copilot_agent_access",
  description:
    "Control WHO may use a Copilot Studio agent: restrict it to named Entra security groups, to users the agent has been shared with, or open it to anyone. Opening it to anyone is refused unless confirmOpenAccess=true, because combined with no authentication that makes the agent reachable by anybody who finds its endpoint. Both columns were verified writable: the policy takes the platform's option values and the group list is comma-separated GUIDs. Publish afterwards for the change to take effect.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: { type: "string", description: "The agent's botid (GUID)" },
      policy: {
        type: "string",
        enum: ["anyone", "agentReaders", "securityGroups", "anyoneMultiTenant"],
        description:
          "anyone = no restriction; agentReaders = only people the agent is shared with; securityGroups = only members of securityGroupIds; anyoneMultiTenant = anyone, including other tenants",
      },
      securityGroupIds: {
        type: "array",
        maxItems: 20,
        items: { type: "string" },
        description:
          "Up to 20 Entra security group object IDs (GUIDs), the platform's 739-character comma-separated limit. Replaces the current list. Meaningful only with policy=securityGroups.",
      },
      confirmOpenAccess: {
        type: "boolean",
        description: "Required to set policy=anyone or anyoneMultiTenant",
      },
    },
    required: ["agentId"],
  },
};

export async function handleSetCopilotAgentAccess(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = setCopilotAgentAccessInputSchema.parse(input);
  const api = requireApi(context.api);
  const agentId = assertGuid(parsed.agentId, "agentId");

  if (parsed.policy === undefined && parsed.securityGroupIds === undefined) {
    throw new ValidationError(
      "Nothing to change — pass policy, securityGroupIds, or both. To inspect access, use audit_copilot_agent_access.",
    );
  }

  const bot = await withCategory(() =>
    api.getRow(
      "bots",
      agentId,
      "name,accesscontrolpolicy,authorizedsecuritygroupids,authenticationmode",
    ),
  );
  const name = str(bot, "name") ?? agentId;

  const update: Record<string, unknown> = {};
  const lines: string[] = [];

  if (parsed.policy !== undefined) {
    const opening = parsed.policy === "anyone" || parsed.policy === "anyoneMultiTenant";
    if (opening && parsed.confirmOpenAccess !== true) {
      const unauthenticated = num(bot, "authenticationmode") === 1;
      throw new ValidationError(
        [
          `Refusing to open "${name}" to ${parsed.policy === "anyoneMultiTenant" ? "anyone in any tenant" : "anyone"} without confirmOpenAccess=true.`,
          unauthenticated
            ? "This agent also has NO authentication, so with open access anybody who reaches its endpoint can hold a conversation with it."
            : "Set confirmOpenAccess=true if that is really the intent.",
        ].join(" "),
      );
    }
    update["accesscontrolpolicy"] = ACCESS_POLICY_VALUES[parsed.policy];
    lines.push(`  policy: ${parsed.policy}`);
  }

  if (parsed.securityGroupIds !== undefined) {
    const ids = parsed.securityGroupIds.map((id) =>
      assertGuid(id.trim(), "securityGroupIds entry"),
    );
    update["authorizedsecuritygroupids"] = ids.join(",");
    lines.push(`  security groups: ${ids.length === 0 ? "(none)" : ids.join(", ")}`);
  }

  await withCategory(() => api.updateRow("bots", agentId, update));

  // The combination that means "restricted to groups" but names none is the
  // one that quietly restricts nobody — call it out rather than let it pass.
  const effectivePolicy =
    parsed.policy !== undefined
      ? ACCESS_POLICY_VALUES[parsed.policy]
      : num(bot, "accesscontrolpolicy");
  const effectiveGroups =
    parsed.securityGroupIds !== undefined
      ? parsed.securityGroupIds.length
      : (str(bot, "authorizedsecuritygroupids") ?? "").split(",").filter((g) => g.trim() !== "")
          .length;
  const warnings: string[] = [];
  // Found by bisecting a live agent that returned AuthenticationNotConfigured
  // with no knowledge source present: "Agent readers" means only people the
  // agent is shared with, and the platform cannot know WHO the user is without
  // authentication — so on an unauthenticated agent every conversation fails,
  // with an error that blames knowledge rather than access. Flipping the
  // policy back made the same agent answer immediately.
  if (
    effectivePolicy === ACCESS_POLICY_VALUES.agentReaders &&
    num(bot, "authenticationmode") === 1
  ) {
    warnings.push(
      "⚠ Policy is 'agent readers' but this agent has NO authentication. The platform cannot tell who the user is, so every conversation will fail with AuthenticationNotConfigured — an error that reads as a knowledge problem. Either configure authentication or use a different policy.",
    );
  }
  if (effectivePolicy === ACCESS_POLICY_VALUES.securityGroups && effectiveGroups === 0) {
    warnings.push(
      "⚠ Policy is 'security groups' but no groups are listed — check in Copilot Studio which groups actually apply, because as written this restricts access to nobody in particular.",
    );
  }

  return [
    `Updated access for "${name}".`,
    ...lines,
    ...warnings,
    "",
    "Publish the agent for this to take effect.",
  ].join("\n");
}

// =============================================================================
// set_copilot_agent_icon
// =============================================================================

const PNG_SIGNATURE = Buffer.from([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);
/** Copilot Studio requires the icon to be strictly smaller than 72 KB. */
const MAX_ICON_BYTES = 72 * 1024 - 1;
const MAX_ICON_DIMENSION = 192;
const MAX_ICON_BASE64_LENGTH = Math.ceil(MAX_ICON_BYTES / 3) * 4 + "data:image/png;base64,".length;
const BASE64_PATTERN = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

const copilotAgentIconBase64Schema = z
  .string()
  .min(1)
  .max(MAX_ICON_BASE64_LENGTH)
  .superRefine((value, context) => {
    const raw = value.replace(/^data:image\/png;base64,/i, "").trim();
    if (!BASE64_PATTERN.test(raw)) {
      context.addIssue({
        code: "custom",
        message: "pngBase64 must contain valid standard base64.",
      });
      return;
    }
    const byteLength = Buffer.from(raw, "base64").length;
    if (byteLength > MAX_ICON_BYTES) {
      context.addIssue({
        code: "custom",
        message: `The icon is ${byteLength} bytes; Copilot Studio requires it to be smaller than 72 KB (${72 * 1024} bytes).`,
      });
    }
  });

/**
 * Check that bytes are a real PNG and read its dimensions from the IHDR chunk.
 *
 * Dataverse accepts ANY string into `bot.iconbase64` — it does not look at it.
 * The check happens at PUBLISH, where a bad icon fails the whole agent with
 * `MalformedBotChannelRegistrationIconException ... BCR Icon must be a valid
 * PNG` as a fatal agent-level error. Verified live: an 8-byte PNG header
 * saved fine and then broke every subsequent publish until it was cleared.
 * So the validation has to happen here, before the write.
 */
export function inspectPng(bytes: Buffer): { width: number; height: number } {
  if (bytes.length < 45 || !bytes.subarray(0, 8).equals(PNG_SIGNATURE)) {
    throw new ValidationError(
      "The icon is not a PNG — it does not start with the PNG signature. Copilot Studio requires a real PNG file.",
    );
  }

  let offset = PNG_SIGNATURE.length;
  let width: number | undefined;
  let height: number | undefined;
  let scanlineLayout: Array<{ rows: number; rowBytes: number }> = [];
  let sawIdat = false;
  let sawIend = false;
  const idatChunks: Buffer[] = [];

  while (offset + 12 <= bytes.length) {
    const length = bytes.readUInt32BE(offset);
    const typeStart = offset + 4;
    const dataStart = offset + 8;
    const dataEnd = dataStart + length;
    const chunkEnd = dataEnd + 4;
    if (dataEnd < dataStart || chunkEnd > bytes.length) {
      throw new ValidationError("The icon has a truncated PNG chunk.");
    }

    const type = bytes.subarray(typeStart, dataStart).toString("ascii");
    const storedCrc = bytes.readUInt32BE(dataEnd);
    const calculatedCrc = pngCrc32(bytes.subarray(typeStart, dataEnd));
    if (storedCrc !== calculatedCrc) {
      throw new ValidationError(
        `The icon has a bad ${type || "unknown"} chunk checksum, so it is not a valid PNG.`,
      );
    }

    if (offset === PNG_SIGNATURE.length) {
      if (type !== "IHDR" || length !== 13) {
        throw new ValidationError(
          "The icon's first chunk is not a valid IHDR, so it is not a well-formed PNG.",
        );
      }
      width = bytes.readUInt32BE(dataStart);
      height = bytes.readUInt32BE(dataStart + 4);
      if (width === 0 || height === 0) {
        throw new ValidationError(
          "The icon's IHDR reports a zero dimension, so it is not a usable PNG.",
        );
      }
      if (width > MAX_ICON_DIMENSION || height > MAX_ICON_DIMENSION) {
        throw new ValidationError(
          `The icon is ${width}x${height}; Copilot Studio allows at most ${MAX_ICON_DIMENSION}x${MAX_ICON_DIMENSION}.`,
        );
      }
      const bitDepth = bytes[dataStart + 8];
      const colorType = bytes[dataStart + 9];
      const compressionMethod = bytes[dataStart + 10];
      const filterMethod = bytes[dataStart + 11];
      const interlaceMethod = bytes[dataStart + 12];
      const allowedBitDepths: Record<number, readonly number[]> = {
        0: [1, 2, 4, 8, 16],
        2: [8, 16],
        3: [1, 2, 4, 8],
        4: [8, 16],
        6: [8, 16],
      };
      const channels: Record<number, number> = { 0: 1, 2: 3, 3: 1, 4: 2, 6: 4 };
      if (
        bitDepth === undefined ||
        colorType === undefined ||
        !allowedBitDepths[colorType]?.includes(bitDepth) ||
        compressionMethod !== 0 ||
        filterMethod !== 0 ||
        (interlaceMethod !== 0 && interlaceMethod !== 1)
      ) {
        throw new ValidationError("The icon's IHDR contains an unsupported or invalid PNG format.");
      }
      const bitsPerPixel = (channels[colorType] ?? 0) * bitDepth;
      scanlineLayout = pngScanlineLayout(width, height, bitsPerPixel, interlaceMethod);
    } else if (type === "IDAT") {
      sawIdat = true;
      idatChunks.push(bytes.subarray(dataStart, dataEnd));
    } else if (type === "IEND") {
      if (length !== 0 || chunkEnd !== bytes.length) {
        throw new ValidationError("The icon has an invalid IEND chunk or trailing data.");
      }
      sawIend = true;
      break;
    }

    offset = chunkEnd;
  }

  if (width === undefined || height === undefined || !sawIdat || !sawIend) {
    throw new ValidationError(
      "The icon is missing a required IHDR, IDAT, or IEND chunk, so it is not a complete PNG.",
    );
  }
  const expectedInflatedBytes = scanlineLayout.reduce(
    (total, pass) => total + pass.rows * (pass.rowBytes + 1),
    0,
  );
  let inflated: Buffer;
  try {
    inflated = inflateSync(Buffer.concat(idatChunks), {
      // Prevent a tiny, highly-compressed IDAT from expanding without bound.
      maxOutputLength: expectedInflatedBytes + 1,
    });
  } catch {
    throw new ValidationError("The icon's PNG image data is corrupt and cannot be decompressed.");
  }
  if (inflated.length !== expectedInflatedBytes) {
    throw new ValidationError(
      `The icon's decompressed PNG data is ${inflated.length} bytes, but its dimensions require ${expectedInflatedBytes}.`,
    );
  }
  let scanlineOffset = 0;
  for (const pass of scanlineLayout) {
    for (let row = 0; row < pass.rows; row += 1) {
      if ((inflated[scanlineOffset] ?? 255) > 4) {
        throw new ValidationError("The icon contains an invalid PNG scanline filter.");
      }
      scanlineOffset += pass.rowBytes + 1;
    }
  }
  return { width, height };
}

/** Expected scanline layout for non-interlaced or Adam7-interlaced PNG image data. */
function pngScanlineLayout(
  width: number,
  height: number,
  bitsPerPixel: number,
  interlaceMethod: number,
): Array<{ rows: number; rowBytes: number }> {
  if (interlaceMethod === 0) {
    return [{ rows: height, rowBytes: Math.ceil((width * bitsPerPixel) / 8) }];
  }
  const passes = [
    [0, 0, 8, 8],
    [4, 0, 8, 8],
    [0, 4, 4, 8],
    [2, 0, 4, 4],
    [0, 2, 2, 4],
    [1, 0, 2, 2],
    [0, 1, 1, 2],
  ] as const;
  return passes.flatMap(([xStart, yStart, xStep, yStep]) => {
    if (width <= xStart || height <= yStart) return [];
    const passWidth = Math.ceil((width - xStart) / xStep);
    const rows = Math.ceil((height - yStart) / yStep);
    return [{ rows, rowBytes: Math.ceil((passWidth * bitsPerPixel) / 8) }];
  });
}

/** CRC-32 used by PNG chunks. Icons are bounded below 72 KB, so this compact implementation is sufficient. */
function pngCrc32(bytes: Buffer): number {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = (crc >>> 1) ^ (crc & 1 ? 0xedb88320 : 0);
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}

export const setCopilotAgentIconInputSchema = z.object({
  agentId: z.string().min(1),
  pngBase64: copilotAgentIconBase64Schema.optional(),
  clear: z.boolean().optional(),
});

export const setCopilotAgentIconTool = {
  name: "set_copilot_agent_icon",
  description:
    "Set or clear a Copilot Studio agent's icon. The icon must be a PNG smaller than 72 KB and no larger than 192x192. Dataverse stores malformed image data without checking it, and a malformed icon then fails every publish until it is cleared — verified live. The bytes and dimensions are checked here before anything is written. Pass pngBase64 (the raw file, base64-encoded; a data: prefix is accepted and stripped) or clear=true.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: { type: "string", description: "The agent's botid (GUID)" },
      pngBase64: {
        type: "string",
        minLength: 1,
        maxLength: MAX_ICON_BASE64_LENGTH,
        description:
          "PNG file contents, base64-encoded; smaller than 72 KB and at most 192x192. A data:image/png;base64, prefix is accepted.",
      },
      clear: { type: "boolean", description: "Remove the icon instead of setting one" },
    },
    required: ["agentId"],
  },
};

export async function handleSetCopilotAgentIcon(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = setCopilotAgentIconInputSchema.parse(input);
  const api = requireApi(context.api);
  const agentId = assertGuid(parsed.agentId, "agentId");

  if (parsed.clear === true && parsed.pngBase64 !== undefined) {
    throw new ValidationError("Pass either pngBase64 or clear=true, not both.");
  }
  if (parsed.clear !== true && parsed.pngBase64 === undefined) {
    throw new ValidationError(
      "Nothing to do — pass pngBase64 to set an icon or clear=true to remove one.",
    );
  }

  const bot = await withCategory(() => api.getRow("bots", agentId, "name"));
  const name = str(bot, "name") ?? agentId;

  if (parsed.clear === true) {
    await withCategory(() => api.updateRow("bots", agentId, { iconbase64: null }));
    return `Cleared the icon on "${name}". Publish the agent for this to take effect.`;
  }

  const raw = (parsed.pngBase64 ?? "").replace(/^data:image\/png;base64,/i, "").trim();
  const bytes = Buffer.from(raw, "base64");
  if (bytes.length === 0)
    throw new ValidationError("pngBase64 did not decode to any bytes — check that it is base64.");
  if (bytes.length > MAX_ICON_BYTES) {
    throw new ValidationError(
      `The icon is ${bytes.length} bytes; Copilot Studio requires it to be smaller than 72 KB (${72 * 1024} bytes).`,
    );
  }
  const { width, height } = inspectPng(bytes);

  // RAW base64 — no data: URI prefix. Verified live with one 32x32 PNG:
  // written with the "data:image/png;base64," prefix it failed publish with
  // "BCR Icon must be a valid PNG"; written as bare base64 it published. The
  // platform stores the string verbatim and decodes it as-is, so the prefix
  // is read as part of the image and makes a perfectly good PNG "invalid".
  await withCategory(() =>
    api.updateRow("bots", agentId, { iconbase64: bytes.toString("base64") }),
  );
  return [
    `Set the icon on "${name}" — PNG ${width}x${height}, ${bytes.length} bytes.`,
    width !== height
      ? "  note: the icon is not square; Copilot Studio renders icons in a square frame."
      : "",
    "Publish the agent for this to take effect.",
  ]
    .filter((l) => l !== "")
    .join("\n");
}

// =============================================================================
// add_copilot_agent_knowledge
// =============================================================================

export const addCopilotAgentKnowledgeInputSchema = z.object({
  agentId: z.string().min(1),
  name: z.string().min(1).max(100),
  expectedSourceSha256: z.string().length(64).regex(SOURCE_SHA256_PATTERN).optional(),
  kind: z.enum(["sharepoint", "publicSite", "azureAISearch"]),
  siteUrl: z
    .string()
    .url()
    .refine((value) => new URL(value).protocol === "https:", {
      message: "siteUrl must use HTTPS",
    })
    .optional(),
  indexName: z.string().min(1).max(200).optional(),
  schemaName: z.string().min(1).max(200).optional(),
});

export const addCopilotAgentKnowledgeTool = {
  name: "add_copilot_agent_knowledge",
  description:
    "Add a knowledge source to a Copilot Studio agent without hand-writing YAML: a SharePoint site (sharepoint + siteUrl), a public website (publicSite + siteUrl), or an Azure AI Search index (azureAISearch + indexName). Existing replacement requires expectedSourceSha256 from the inspected full source and uses a conditional row update; ambiguous targets and guarded creation are refused. Readback is reported separately from acceptance. These are the three kinds the publish validator is known to accept; the shapes were read off production agents. Public website knowledge does not inherently require end-user sign-in. SharePoint depends on supported user authentication and site permissions; Azure AI Search connection/index credentials are not verified by authoring. Saved source does not prove indexing or runtime retrieval.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: { type: "string", description: "The agent's botid (GUID)" },
      name: { type: "string", description: "Display name for the knowledge source" },
      kind: { type: "string", enum: ["sharepoint", "publicSite", "azureAISearch"] },
      expectedSourceSha256: {
        type: "string",
        minLength: 64,
        maxLength: 64,
        pattern: SOURCE_SHA256_PATTERN.source,
        description: "Required to replace an existing source: exact SHA-256 of its full stored UTF-8 source, as returned by get_copilot_agent_component with content. Uppercase hexadecimal is accepted. Does not permit creation or bypass stale-source checks.",
      },
      siteUrl: {
        type: "string",
        format: "uri",
        pattern: "^[Hh][Tt][Tt][Pp][Ss]://",
        description: "HTTPS site URL (sharepoint / publicSite)",
      },
      indexName: { type: "string", description: "Index name (azureAISearch)" },
      schemaName: {
        type: "string",
        description: "Override the generated '<agentSchema>.topic.<Name>' schema name",
      },
    },
    required: ["agentId", "name", "kind"],
  },
};

export function buildKnowledgeYaml(args: {
  kind: "sharepoint" | "publicSite" | "azureAISearch";
  siteUrl?: string;
  indexName?: string;
}): string {
  const lines = ["kind: KnowledgeSourceConfiguration", "source:"];
  if (args.kind === "sharepoint")
    lines.push("  kind: SharePointSearchSource", `  site: ${yamlStringScalar(args.siteUrl ?? "")}`);
  else if (args.kind === "publicSite")
    lines.push("  kind: PublicSiteSearchSource", `  site: ${yamlStringScalar(args.siteUrl ?? "")}`);
  else
    lines.push(
      "  kind: AzureAISearchSource",
      `  indexName: ${yamlStringScalar(args.indexName ?? "")}`,
    );
  return lines.join("\n") + "\n";
}

export async function handleAddCopilotAgentKnowledge(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = addCopilotAgentKnowledgeInputSchema.parse(input);
  const api = requireApi(context.api);
  const agentId = assertGuid(parsed.agentId, "agentId");
  if (parsed.kind !== "azureAISearch" && parsed.siteUrl === undefined) {
    throw new ValidationError(`A ${parsed.kind} knowledge source needs siteUrl.`);
  }
  if (parsed.kind === "azureAISearch" && parsed.indexName === undefined) {
    throw new ValidationError(
      "An azureAISearch knowledge source needs indexName — publish rejects it otherwise.",
    );
  }
  const agent = await withCategory(() =>
    api.getRow("bots", agentId, "name,schemaname,authenticationmode"),
  );
  const agentSchema = str(agent, "schemaname");
  if (!agentSchema) throw new ValidationError(`Agent ${agentId} has no schema name.`);
  // Knowledge sources use the ".topic." infix on production agents, not ".knowledge.".
  const schemaName = parsed.schemaName ?? `${agentSchema}.topic.${toolSchemaSegment(parsed.name)}`;
  const content = buildKnowledgeYaml({
    kind: parsed.kind,
    ...(parsed.siteUrl !== undefined ? { siteUrl: parsed.siteUrl } : {}),
    ...(parsed.indexName !== undefined ? { indexName: parsed.indexName } : {}),
  });

  const existingPage = (
    await withCategory(() =>
      api.queryRows("botcomponents", {
        select: "botcomponentid",
        filter: `_parentbotid_value eq ${agentId} and schemaname eq '${schemaName.replace(/'/g, "''")}'`,
        top: 2,
      }),
    )
  );
  if (!existingPage || !Array.isArray(existingPage.value) ||
      existingPage.value.length > 1 || existingPage.nextLink !== undefined) {
    throw new ValidationError("Knowledge-source lookup is malformed, ambiguous or incomplete; no component write was sent.");
  }
  const existing = existingPage.value[0];
  // Microsoft lab: public website grounding with No authentication (sections 2.3–2.6).
  // https://microsoftlearning.github.io/mslearn-copilotstudio/Instructions/Labs/01-create-agents.html
  // SharePoint specifically needs user authentication and site permissions:
  // https://learn.microsoft.com/en-us/microsoft-copilot-studio/nlu-generative-answers-sharepoint-onedrive#advanced-authentication-scenarios
  const authNote = parsed.kind === "publicSite"
    ? "Public website knowledge does not inherently require end-user sign-in; indexing, access policy and runtime retrieval were not verified by authoring."
    : parsed.kind === "azureAISearch"
      ? "Azure AI Search connection/index credentials and runtime retrieval were not verified by authoring."
      : num(agent, "authenticationmode") === 1
        ? "⚠ SharePoint knowledge requires authenticated user access, but this agent is set to No authentication. Verify user authentication and site permissions before runtime testing; this tool does not change them."
        : "SharePoint retrieval depends on supported user authentication and site permissions; neither was verified by authoring.";
  if (existing !== undefined) {
    if (parsed.expectedSourceSha256 === undefined) {
      throw new ValidationError("Replacing a knowledge source requires expectedSourceSha256; inspect the existing full source first. No write sent.");
    }
    const id = existing !== null && typeof existing === "object" && !Array.isArray(existing)
      ? str(existing, "botcomponentid") : undefined;
    if (!id || id.length !== 36 || !GUID_PATTERN.test(id)) {
      throw new ValidationError("Knowledge-source lookup returned an invalid component identity; no write sent.");
    }
    const updateResult = await handleUpsertCopilotAgentComponent({ api }, {
      agentId, componentId: id, componentType: COMPONENT_TYPES.knowledgeSource,
      name: parsed.name, content, expectedSourceSha256: parsed.expectedSourceSha256,
    });
    return [
      updateResult,
      authNote,
      "Publish the agent for this to take effect.",
    ]
      .filter(Boolean)
      .join("\n");
  }
  if (parsed.expectedSourceSha256 !== undefined) {
    throw new ValidationError("expectedSourceSha256 requires an existing knowledge source; no create sent.");
  }
  const created = await withCategory(() =>
    api.createRow("botcomponents", {
      name: parsed.name,
      componenttype: COMPONENT_TYPES.knowledgeSource,
      schemaname: schemaName,
      data: content,
      "parentbotid@odata.bind": `/bots(${agentId})`,
    }),
  );
  return [
    `Added knowledge source "${parsed.name}" (${str(created, "botcomponentid") ?? "?"}) to "${str(agent, "name") ?? agentId}".`,
    `  schema name: ${schemaName}`,
    authNote,
    "Publish the agent for this to take effect.",
  ]
    .filter(Boolean)
    .join("\n");
}

// =============================================================================
// add_copilot_agent_topic
// =============================================================================

const COPILOT_TOPIC_VARIABLE_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;

export const copilotTopicQuestionInputSchema = z.object({
  prompt: z.string().min(1).max(2000),
  variable: z
    .string()
    .regex(COPILOT_TOPIC_VARIABLE_PATTERN, "variable must be a plain identifier, e.g. 'Answer'"),
  choices: z.array(z.string().min(1).max(200)).min(2).max(20).optional(),
});

export const addCopilotAgentTopicInputSchema = z.object({
  agentId: z.string().min(1),
  name: z.string().min(1).max(100),
  expectedSourceSha256: z.string().length(64).regex(SOURCE_SHA256_PATTERN).optional(),
  triggerPhrases: z.array(z.string().min(1).max(300)).min(1).max(50),
  messages: z.array(z.string().min(1).max(4000)).min(1).max(20),
  question: copilotTopicQuestionInputSchema.optional(),
  questions: z.array(copilotTopicQuestionInputSchema).min(1).max(10).optional(),
  endConversation: z.boolean().optional(),
  schemaName: z.string().min(1).max(200).optional(),
}).superRefine((input, context) => {
  if (input.question !== undefined && input.questions !== undefined) {
    context.addIssue({
      code: "custom",
      path: ["questions"],
      message: "Use either question or questions, not both.",
    });
  }
  const variables = new Set<string>();
  for (const [index, question] of (input.questions ?? []).entries()) {
    const key = question.variable.toLowerCase();
    if (variables.has(key)) {
      context.addIssue({
        code: "custom",
        path: ["questions", index, "variable"],
        message: "Question variables must be unique (case-insensitive).",
      });
    }
    variables.add(key);
  }
});

const copilotTopicQuestionJsonSchema = {
  type: "object",
  properties: {
    prompt: { type: "string", minLength: 1, maxLength: 2000 },
    variable: {
      type: "string",
      pattern: COPILOT_TOPIC_VARIABLE_PATTERN.source,
      description: "Plain identifier, e.g. 'Answer' -> Topic.Answer. Must be unique across questions, ignoring case.",
    },
    choices: {
      type: "array",
      minItems: 2,
      maxItems: 20,
      items: { type: "string", minLength: 1, maxLength: 200 },
    },
  },
  required: ["prompt", "variable"],
};

export const addCopilotAgentTopicTool = {
  name: "add_copilot_agent_topic",
  description:
    "Add a topic to a Copilot Studio agent without hand-writing YAML: trigger phrases, an optional legacy question or 1–10 ordered questions, and reply messages. Existing replacement requires expectedSourceSha256 of inspected full source and an exact existing AdaptiveDialog target; ambiguous or incomplete lookups, other source kinds and guarded creation are refused. Replaces the full topic source using a conditional row update, with readback reported separately. Each answer is stored in Topic.<variable>; variable names must be unique ignoring case. All questions precede messages; use question or questions, never both. Individual question shapes have worked in the Studio draft test pane, but saved source does not prove publication or runtime behavior. For richer actions use guarded upsert_copilot_agent_component; local lint is not full service validation.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: { type: "string", description: "The agent's botid (GUID)" },
      name: { type: "string", description: "Topic display name" },
      expectedSourceSha256: {
        type: "string",
        minLength: 64,
        maxLength: 64,
        pattern: SOURCE_SHA256_PATTERN.source,
        description: "Required for existing-topic replacement: exact SHA-256 of full stored UTF-8 AdaptiveDialog source, from get_copilot_agent_component. Mixed-case hexadecimal accepted. Does not permit guarded creation or replace TaskDialog tools.",
      },
      triggerPhrases: {
        type: "array",
        items: { type: "string" },
        description: "Phrases that should start this topic",
      },
      messages: {
        type: "array",
        items: { type: "string" },
        description:
          "What the agent says after all questions, in order. Each becomes one message. Reference any answer as {Topic.<variable>}.",
      },
      question: {
        ...copilotTopicQuestionJsonSchema,
        description:
          "Legacy single question before the messages. With choices, multiple choice; without, free text. Cannot be combined with questions. Existing single-question YAML is preserved.",
      },
      questions: {
        type: "array",
        minItems: 1,
        maxItems: 10,
        items: copilotTopicQuestionJsonSchema,
        description:
          "Ask 1–10 questions in order before all messages. Each variable must be unique ignoring case. Cannot be combined with question. This creates conversation nodes, not external tool calls.",
      },
      endConversation: {
        type: "boolean",
        description: "End the conversation after the last message",
      },
      schemaName: {
        type: "string",
        description: "Override the generated '<agentSchema>.topic.<Name>' schema name",
      },
    },
    required: ["agentId", "name", "triggerPhrases", "messages"],
    not: { required: ["question", "questions"] },
  },
};

function yamlBlockScalar(text: string, indent: string): string {
  return `|-\n${text
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((l) => (l === "" ? "" : indent + l))
    .join("\n")}`;
}

export function buildTopicYaml(args: {
  name: string;
  triggerPhrases: readonly string[];
  messages: readonly string[];
  question?:
    { prompt: string; variable: string; choices?: readonly string[] | undefined } | undefined;
  questions?: readonly {
    prompt: string;
    variable: string;
    choices?: readonly string[] | undefined;
  }[] | undefined;
  endConversation?: boolean;
}): string {
  const lines = [
    "kind: AdaptiveDialog",
    "beginDialog:",
    "  kind: OnRecognizedIntent",
    "  id: main",
    "  intent:",
    `    displayName: ${JSON.stringify(args.name)}`,
    "    triggerQueries:",
  ];
  for (const q of args.triggerPhrases) lines.push(`      - ${JSON.stringify(q)}`);
  lines.push("", "  actions:");
  const questions = args.questions ?? (args.question !== undefined ? [args.question] : []);
  questions.forEach((question, questionIndex) => {
    // Shapes confirmed by publish: a multiple-choice question is an
    // EmbeddedEntity wrapping a ClosedListEntity (a BARE ClosedListEntity is
    // rejected as unknown); free text is the StringPrebuiltEntity.
    lines.push(
      "    - kind: Question",
      `      id: question_${questionIndex + 1}`,
      "      alwaysPrompt: true",
      `      variable: Topic.${question.variable}`,
      `      prompt: ${JSON.stringify(question.prompt)}`,
    );
    if (question.choices !== undefined && question.choices.length > 0) {
      lines.push(
        "      entity:",
        "        kind: EmbeddedEntity",
        "        definition:",
        "          kind: ClosedListEntity",
        "          items:",
      );
      question.choices.forEach((c, i) => {
        // Legacy IDs are unchanged. Multi-question choices have a generated,
        // question-scoped ID; caller-controlled labels never become IDs.
        const choiceId = args.questions === undefined
          ? `choice_${i + 1}`
          : `question_${questionIndex + 1}_choice_${i + 1}`;
        lines.push(
          `            - id: ${choiceId}`,
          `              displayName: ${JSON.stringify(c)}`,
        );
      });
    } else {
      lines.push("      entity:", "        kind: StringPrebuiltEntity");
    }
    lines.push("");
  });
  args.messages.forEach((m, i) => {
    lines.push(
      `    - kind: SendActivity`,
      `      id: sendMessage_${i + 1}`,
      `      activity: ${yamlBlockScalar(m, "        ")}`,
      "",
    );
  });
  if (args.endConversation)
    lines.push("    - kind: EndConversation", "      id: endConversation_1", "");
  return lines.join("\n");
}

export async function handleAddCopilotAgentTopic(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = addCopilotAgentTopicInputSchema.parse(input);
  const api = requireApi(context.api);
  const agentId = assertGuid(parsed.agentId, "agentId");
  const agent = await withCategory(() => api.getRow("bots", agentId, "name,schemaname"));
  const agentSchema = str(agent, "schemaname");
  if (!agentSchema) throw new ValidationError(`Agent ${agentId} has no schema name.`);
  const schemaName = parsed.schemaName ?? `${agentSchema}.topic.${toolSchemaSegment(parsed.name)}`;
  const content = buildTopicYaml({
    name: parsed.name,
    triggerPhrases: parsed.triggerPhrases,
    messages: parsed.messages,
    ...(parsed.question !== undefined ? { question: parsed.question } : {}),
    ...(parsed.questions !== undefined ? { questions: parsed.questions } : {}),
    ...(parsed.endConversation !== undefined ? { endConversation: parsed.endConversation } : {}),
  });
  const problems = lintComponent(COMPONENT_TYPES.topic, content);
  if (problems.length > 0) {
    // The builder produced something its own linter rejects — that is a bug
    // here, not in the caller's input, so say so instead of saving it.
    throw new ValidationError(
      `Internal error: the generated topic failed lint (${problems.join("; ")}).`,
    );
  }
  const existingPage = (
    await withCategory(() =>
      api.queryRows("botcomponents", {
        select: "botcomponentid",
        filter: `_parentbotid_value eq ${agentId} and schemaname eq '${schemaName.replace(/'/g, "''")}'`,
        top: 2,
      }),
    )
  );
  if (!existingPage || !Array.isArray(existingPage.value) ||
      existingPage.value.length > 1 || existingPage.nextLink !== undefined) {
    throw new ValidationError("Topic lookup is malformed, ambiguous or incomplete; no component write was sent.");
  }
  if (existingPage.value.length === 1) {
    const existing = existingPage.value[0];
    const id = existing !== null && typeof existing === "object" && !Array.isArray(existing)
      ? str(existing, "botcomponentid") : undefined;
    if (!id || id.length !== 36 || !GUID_PATTERN.test(id)) {
      throw new ValidationError("Topic lookup returned an invalid component identity; no write sent.");
    }
    if (parsed.expectedSourceSha256 === undefined) {
      throw new ValidationError("Replacing a topic requires expectedSourceSha256; inspect its full source first. No write sent.");
    }
    let current: Row;
    try {
      current = await api.getRow("botcomponents", id, `${GUARDED_COMPONENT_SELECT},schemaname`);
    } catch (error) {
      throw new CopilotComponentUpdateError("preflight", error instanceof ApiError ? error.statusCode ?? 502 : 502);
    }
    const guard = guardedComponentPreflight(current, id, agentId, parsed.expectedSourceSha256, COMPONENT_TYPES.topic);
    let isAdaptiveDialog = false;
    try {
      const document = parseDocument(str(current, "data")!, { uniqueKeys: true, strict: true });
      const sourceKind: unknown = isMap(document.contents) ? document.contents.get("kind", true) : undefined;
      isAdaptiveDialog = document.errors.length === 0 && isScalar(sourceKind) && sourceKind.value === "AdaptiveDialog";
    } catch {
      // An unreadable existing source must not be replaced by this typed builder.
    }
    if (str(current, "schemaname") !== schemaName || !isAdaptiveDialog) {
      throw new ValidationError("Existing topic must match the exact schema name and have valid AdaptiveDialog source. Other components, including TaskDialog tools, are not replaced; no write sent.");
    }
    return guardedComponentWrite(api, id, agentId, COMPONENT_TYPES.topic, { name: parsed.name, data: content }, guard.etag);
  }
  if (parsed.expectedSourceSha256 !== undefined) {
    throw new ValidationError("expectedSourceSha256 requires an existing topic; no create sent.");
  }
  const created = await withCategory(() =>
    api.createRow("botcomponents", {
      name: parsed.name,
      componenttype: COMPONENT_TYPES.topic,
      schemaname: schemaName,
      data: content,
      "parentbotid@odata.bind": `/bots(${agentId})`,
    }),
  );
  return [
    `Added topic "${parsed.name}" (${str(created, "botcomponentid") ?? "?"}) — ${parsed.triggerPhrases.length} trigger phrase(s), ${parsed.messages.length} message(s).`,
    `  schema name: ${schemaName}`,
    "Publish the agent for this to take effect.",
  ].join("\n");
}

// =============================================================================
// add_copilot_agent_test_case
// =============================================================================

export const addCopilotAgentTestCaseInputSchema = z.union([z.object({
  agentId: z.string().min(1),
  input: z.string().min(1).max(2000),
  name: z.string().min(1).max(200).optional(),
  expectedOutput: z.string().max(4000).optional(),
  conversationTurns: z.never().optional(),
  componentId: z.never().optional(),
  confirmCreate: z.never().optional(),
}), conversationTestCaseInputSchema]);

export const addCopilotAgentTestCaseTool = {
  name: "add_copilot_agent_test_case",
  description:
    "EXPERIMENTAL low-level authoring: add one EvaluationData case (a question and optional expected answer), or with conversationTurns a MultiTurnEvaluationCase of 2\u20136 question/reference-answer pairs (supply a stable new componentId and name, then confirmCreate=true to create once and independently verify the readback). Reference answers are expectations, not actual agent responses. This is not a supported test-set create API \u2014 Microsoft documents REST operations for existing test sets and runs, but no create endpoint; source and readback alone are not native-serializer, grading or runtime proof. A case created here belongs to no test set until linked \u2014 group it with add_copilot_agent_evaluation_test_set, which binds its ParentBotComponentId; deleting that set afterwards deletes this case with it.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: { type: "string", description: "The agent's botid (GUID)" },
      input: { type: "string", description: "The message to send the agent" },
      name: { type: "string", description: "Display name; defaults to the input" },
      expectedOutput: {
        type: "string",
        description: "The answer you expect (accepted by publish; scoring unconfirmed)",
      },
      conversationTurns: {
        type: "array", minItems: 2, maxItems: 6,
        description: "Experimental alternative to input: 2–6 literal question/reference-answer pairs. Reference answers are expectations, not observed agent responses. LF is allowed; no braces, expressions or other control/format characters.",
        items: { type: "object", additionalProperties: false, properties: {
          question: { type: "string", minLength: 1, maxLength: 1000 },
          referenceAnswer: { type: "string", minLength: 1, maxLength: 1000 },
        }, required: ["question", "referenceAnswer"] },
      },
      componentId: { type: "string", minLength: 36, maxLength: 36, pattern: "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(?![\\s\\S])", description: "Conversation mode only: stable new component GUID for recovery; never an overwrite or retry permission." },
      confirmCreate: { type: "boolean", default: false, description: "Conversation mode only: false previews locally; true creates the exact new ID after duplicate checks and separately reads it back. No evaluation or publication." },
    },
    required: ["agentId"],
    oneOf: [
      { required: ["input"], not: { anyOf: [{ required: ["conversationTurns"] }, { required: ["componentId"] }, { required: ["confirmCreate"] }] } },
      { required: ["conversationTurns", "componentId", "name"], not: { anyOf: [{ required: ["input"] }, { required: ["expectedOutput"] }] } },
    ],
  },
};

export function buildTestCaseYaml(args: { input: string; expectedOutput?: string }): string {
  const lines = [
    "kind: EvaluationData",
    "rows:",
    "  - source: Manual",
    `    input: ${JSON.stringify(args.input)}`,
  ];
  if (args.expectedOutput !== undefined)
    lines.push(`    expectedOutput: ${JSON.stringify(args.expectedOutput)}`);
  return lines.join("\n") + "\n";
}

/**
 * The grader every observed Copilot Studio evaluation names. Kept as the
 * default because an EvaluationSet with no grader scores nothing, and the
 * validator refuses a body without a `graders:` key.
 */
/**
 * Graders this API version accepts, by the `kind` the service deserializes.
 * All three are confirmed live: each one started a run that scored. They are
 * the documented quality / similarity / text-match methods.
 *
 * Names are NOT derivable from the documented labels — eight plausible ones
 * were tried live and every one resolved to `UnknownGraderBase`:
 * KeywordMatchGrader, ClassificationGrader, CapabilityGrader, CustomGrader,
 * TextMatchGrader, StringMatchGrader, KeywordGrader, MatchGrader. The newer
 * Classification and Capability graders are not reachable through
 * api-version 2024-10-01 in this tenant under any of those spellings.
 *
 * NOTE for refusal/guardrail cases: GeneralQuality grades helpfulness, and it
 * reports `abstention: Yes` with `status: Fail` when an agent correctly
 * declines — verified live on two cases whose required behaviour IS to
 * decline. Score those with a classification-style grader instead.
 */
export const EVALUATION_GRADER_KINDS = [
  "GeneralQualityGrader",
  "CompareMeaningGrader",
  "ExactMatchGrader",
] as const;

export const DEFAULT_EVALUATION_GRADER = "GeneralQualityGrader";

/**
 * Build the `EvaluationSet` YAML that names the graders scoring a set's cases.
 *
 * The body carries graders and nothing else, because membership is NOT in the
 * YAML. Verified live against this tenant on 2026-09-05: the service reads an
 * EvaluationSet component written through Dataverse (it appears in
 * `GET .../makerevaluation/testsets` as Active with real audit info), but it
 * reports `totalTestCases` from the `ParentBotComponentId` lookup on each
 * EvaluationData row — not from any key in this body. A `testCases:` list of
 * schema names and an inline `rows:` block were both written and both left the
 * count at 0; binding one case's `ParentBotComponentId` moved it 0 -> 1 and the
 * second moved it to 2. `description:` is not echoed back by the service
 * either, so it is local documentation only.
 *
 * The maker-evaluation REST API still cannot create a set
 * (`POST .../makerevaluation/testsets` answers `RouteNotFound`, verified live),
 * so Dataverse remains the only authoring path. Callers who need a different
 * body can pass a raw one instead of using this builder.
 */
export function buildEvaluationSetYaml(args: {
  graders: readonly string[];
  description?: string | undefined;
}): string {
  const lines = ["kind: EvaluationSet"];
  if (args.description !== undefined) lines.push(`description: ${JSON.stringify(args.description)}`);
  lines.push("graders:");
  // Graders deserialize polymorphically on `kind`, like every other Copilot
  // Studio YAML node. A bare string lands on the base type and the run fails
  // with 500 "Grader type 'UnknownGraderBase' is not supported" — verified
  // live, and the same body with `kind:` started a run that scored.
  for (const grader of args.graders) lines.push(`  - kind: ${grader}`);
  return lines.join("\n") + "\n";
}

/**
 * The navigation property that links an EvaluationData row to its EvaluationSet.
 * Dataverse rejects the attribute name (`parentbotcomponentid`) as undeclared;
 * the bind needs the relationship's PascalCase navigation property, read from
 * `EntityDefinitions(LogicalName='botcomponent')/ManyToOneRelationships`.
 */
export const EVALUATION_SET_PARENT_NAVIGATION = "ParentBotComponentId";

/** Read only the actual root kind; never resolve aliases or echo source/errors. */
function readEvaluationComponentKind(data: unknown): "EvaluationData" | "EvaluationSet" | "MultiTurnEvaluationCase" | undefined {
  if (typeof data !== "string" || data.length === 0 || data.length > 200_000) return undefined;
  try {
    const document = parseDocument(data, { uniqueKeys: true, strict: true });
    if (document.errors.length > 0 || !isMap(document.contents)) return undefined;
    const kind = document.contents.get("kind", true);
    if (!isScalar(kind)) return undefined;
    const value: unknown = kind.value;
    return value === "EvaluationData" || value === "EvaluationSet" || value === "MultiTurnEvaluationCase" ? value : undefined;
  } catch {
    return undefined;
  }
}

export const addCopilotAgentEvaluationTestSetInputSchema = z.object({
  agentId: z.string().min(1),
  name: z.string().min(1).max(200),
  graders: z.array(z.string().min(1).max(200)).max(32).optional(),
  testCaseSchemaNames: z.array(z.string().min(1).max(300)).max(500).optional(),
  description: z.string().max(4000).optional(),
  data: z.string().min(1).max(200_000).optional(),
  schemaName: z.string().min(1).max(200).optional(),
});

export const addCopilotAgentEvaluationTestSetTool = {
  name: "add_copilot_agent_evaluation_test_set",
  description:
    "EXPERIMENTAL low-level authoring: create one EvaluationSet (componenttype 19) grouping existing test cases and naming graders. Active listing and case counts do not verify grader behavior or execution. A case is an EvaluationData or MultiTurnEvaluationCase component; do not mix single-response and conversation cases in one set (conversation sets allow at most 20 cases and one GeneralQualityGrader). Membership is NOT in the set body \u2014 each case's ParentBotComponentId points at the set, and that is what the service counts as totalTestCases. Unknown, ambiguous, wrong-kind, cross-agent and already-parented cases are refused; this never intentionally moves a case from another parent. Writes are ETag-conditional and read back; the operation is not one transaction, so partial links remain. DELETING a set can CASCADE-DELETE its linked cases \u2014 never delete a partially linked set to roll back. Raw data must contain a root EvaluationSet and cannot be combined with graders; testCaseSchemaNames may accompany either, because membership is stored separately.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: { type: "string", description: "The agent's botid (GUID)" },
      name: { type: "string", description: "Display name for the test set" },
      graders: {
        type: "array",
        items: { type: "string" },
        description:
          `Grader kinds that score the cases. Defaults to ["${DEFAULT_EVALUATION_GRADER}"], which is confirmed live. ` +
          "GeneralQualityGrader grades HELPFULNESS and fails a correct refusal (abstention: Yes) — use a classification-style grader for guardrail cases.",
      },
      testCaseSchemaNames: {
        type: "array",
        items: { type: "string" },
        description:
          "Unique schema names of existing unparented EvaluationData or MultiTurnEvaluationCase components on this agent, with one homogeneous case kind per set. May be supplied with either data or graders. Already-parented cases and EvaluationSet components are refused.",
      },
      description: { type: "string", description: "Optional description" },
      data: {
        type: "string",
        description:
          "Raw EvaluationSet YAML, used verbatim instead of the builder. Use this when you know the real schema.",
      },
      schemaName: { type: "string", description: "Override the generated '<agentSchema>.testset.<Name>' schema name" },
    },
    required: ["agentId", "name"],
  },
};

export async function handleAddCopilotAgentEvaluationTestSet(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = addCopilotAgentEvaluationTestSetInputSchema.parse(input);
  const api = requireApi(context.api);
  const agentId = assertGuid(parsed.agentId, "agentId");
  if (parsed.data !== undefined && parsed.graders !== undefined) {
    throw new ValidationError(
      "Pass either data (the whole body) or graders (the builder), not both. testCaseSchemaNames may accompany either body mode.",
    );
  }
  const agent = await withCategory(() => api.getRow("bots", agentId, "botid,name,schemaname"));
  if (str(agent, "botid")?.toLowerCase() !== agentId.toLowerCase()) {
    throw new ValidationError("The agent response did not match the requested ID. No test set was written.");
  }
  const agentSchema = str(agent, "schemaname");
  if (!agentSchema) throw new ValidationError("The agent has no schema name.");

  const content =
    parsed.data ??
    buildEvaluationSetYaml({
      graders: parsed.graders ?? [DEFAULT_EVALUATION_GRADER],
      description: parsed.description,
    });
  if (readEvaluationComponentKind(content) !== "EvaluationSet") {
    throw new ValidationError("The test-set body must be one valid YAML document with root kind EvaluationSet. No test set was written.");
  }
  const problems = lintComponent(COMPONENT_TYPES.testCase, content);
  if (problems.length > 0) {
    return [
      "Refusing to write an EvaluationSet the validator rejects:",
      ...problems.map((problem) => "  • " + problem),
      "",
      "Fix the body, or pass data with the full YAML you want.",
    ].join("\n");
  }

  const requestedCases = parsed.testCaseSchemaNames ?? [];
  if (new Set(requestedCases.map((schema) => schema.toLowerCase())).size !== requestedCases.length) {
    throw new ValidationError("Test case schema names must be unique ignoring case. No test set was written.");
  }
  const caseRows = new Map<string, Row>();
  const caseIds = new Set<string>();
  let caseKind: "EvaluationData" | "MultiTurnEvaluationCase" | undefined;
  const caseSelect = "botcomponentid,componenttype,schemaname,_parentbotid_value,_parentbotcomponentid_value,data,versionnumber";
  const validateCase = (row: Row, schema: string, expectedId?: string, expectedParent: string | null = null): string => {
    const id = str(row, "botcomponentid");
    if (!id || !GUID_PATTERN.test(id) || (expectedId !== undefined && id.toLowerCase() !== expectedId.toLowerCase())) {
      throw new ValidationError("Test case " + schema + " did not return the expected valid component ID.");
    }
    assertComponentBelongsToAgent(row, id, agentId);
    const kind = readEvaluationComponentKind(row.data);
    if (str(row, "schemaname") !== schema || num(row, "componenttype") !== COMPONENT_TYPES.testCase ||
        (kind !== "EvaluationData" && kind !== "MultiTurnEvaluationCase")) {
      throw new ValidationError("Test case " + schema + " must resolve to exactly one matching EvaluationData or MultiTurnEvaluationCase component on this agent.");
    }
    if (caseKind !== undefined && kind !== caseKind) {
      throw new ValidationError("A test set cannot mix single-response and conversation cases; no mixed-kind membership write was sent.");
    }
    if (kind === "MultiTurnEvaluationCase" && lintConversationTestCaseSource(row.data as string).length > 0) {
      throw new ValidationError("Conversation case structure is incomplete or malformed; this case observation cannot be verified.");
    }
    caseKind = kind;
    const etag = str(row, "@odata.etag");
    if (!etag || !/^W\/"[0-9]{1,20}"$/.test(etag)) {
      throw new ValidationError("Test case " + schema + " did not return a supported version ETag; refusing unconditional membership writes.");
    }
    const parent = row._parentbotcomponentid_value;
    if (expectedParent === null) {
      if (typeof parent === "string" && GUID_PATTERN.test(parent)) {
        throw new ValidationError("Test case " + schema + " already has parent " + parent + "; refusing to move it. No existing membership was intentionally changed.");
      }
      if (parent !== null) {
        throw new ValidationError("Test case " + schema + " did not return an explicit empty parent lookup; refusing to infer that it is unparented.");
      }
    } else if (typeof parent !== "string" || parent.toLowerCase() !== expectedParent.toLowerCase()) {
      throw new ValidationError("Test case " + schema + " readback did not confirm the requested set membership.");
    }
    return id;
  };
  if (requestedCases.length > 0) {
    const found = await withCategory(() =>
      api.queryRows("botcomponents", {
        select: caseSelect,
        filter:
          "_parentbotid_value eq " + agentId + " and componenttype eq " + COMPONENT_TYPES.testCase + " and (" +
          requestedCases.map((schema) => "schemaname eq '" + escapeOData(schema) + "'").join(" or ") +
          ")",
        top: requestedCases.length,
      }),
    );
    if (!Array.isArray(found?.value) || found.nextLink !== undefined || found.value.length > requestedCases.length) {
      throw new ValidationError("Test case lookup was incomplete or malformed. No test set was written.");
    }
    for (const row of found.value) {
      const schema = str(row, "schemaname");
      if (!schema || !requestedCases.includes(schema) || caseRows.has(schema)) {
        throw new ValidationError("Test case lookup returned an unexpected or ambiguous schema name. No test set was written.");
      }
      const id = validateCase(row, schema);
      if (caseIds.has(id.toLowerCase())) {
        throw new ValidationError("More than one requested case resolved to the same component ID. No test set was written.");
      }
      caseIds.add(id.toLowerCase());
      caseRows.set(schema, row);
    }
    const missing = requestedCases.filter((schema) => !caseRows.has(schema));
    if (missing.length > 0) {
      throw new ValidationError(
        "No test case component on this agent has these schema names: " + missing.join(", ") + ". " +
        "List them with list_copilot_agent_components componentType=19, or create them with add_copilot_agent_test_case first. No test set was written.",
      );
    }
  }

  if (caseKind === "MultiTurnEvaluationCase") {
    // Documented conversation-only cap; preserve existing single-response limits.
    // https://learn.microsoft.com/en-us/microsoft-copilot-studio/analytics-agent-evaluation-multi-turn
    if (requestedCases.length > 20) {
      throw new ValidationError("Conversation test sets support at most 20 cases. No test set or membership write was sent.");
    }
    const document = parseDocument(content, { uniqueKeys: true, strict: true });
    const graders = isMap(document.contents) ? document.contents.get("graders", true) : undefined;
    const grader = isSeq(graders) && graders.items.length === 1 ? graders.items[0] : undefined;
    const graderKind = isMap(grader) ? grader.get("kind", true) : undefined;
    if (!isScalar(graderKind) || graderKind.value !== "GeneralQualityGrader") {
      throw new ValidationError("Conversation test sets currently support only one GeneralQualityGrader in this authoring tool. Single-response-only graders are not inferred compatible; no test set was written.");
    }
  }

  const schemaName =
    parsed.schemaName ?? agentSchema + ".testset." + toolSchemaSegment(parsed.name).slice(0, 80);
  const created = await withCategory(() =>
    api.createRow("botcomponents", {
      name: parsed.name,
      componenttype: COMPONENT_TYPES.testCase,
      schemaname: schemaName,
      data: content,
      "parentbotid@odata.bind": "/bots(" + agentId + ")",
    }),
  );
  const setId = str(created, "botcomponentid");
  if (!setId || !GUID_PATTERN.test(setId) || caseIds.has(setId.toLowerCase())) {
    throw new ValidationError("The set creation response did not return a valid new set ID. A set may exist, but no case links were attempted; inspect the agent before retrying.");
  }

  // Each case PATCH is conditional, but the complete set is not atomic. Do
  // not automatically move an occupied case or delete the set as rollback.
  const linked: string[] = [];
  const verified: string[] = [];
  const failed: string[] = [];
  const unverified: string[] = [];
  for (const [schema, original] of caseRows) {
    const caseId = str(original, "botcomponentid")!;
    try {
      const current = await withCategory(() => api.getRow("botcomponents", caseId, caseSelect));
      validateCase(current, schema, caseId);
      if (["data", "versionnumber", "@odata.etag"].some((field) => current[field] !== original[field])) {
        throw new ValidationError("Test case " + schema + " changed since the initial lookup; refusing to link it.");
      }
      await withCategory(() =>
        api.linkCopilotEvaluationCaseIfUnchanged(caseId, setId, str(current, "@odata.etag")!),
      );
      linked.push(schema);
    } catch (error) {
      failed.push(schema + " (" + sanitizeErrorMessage(error instanceof Error ? error.message : "Unclassified case-link failure").slice(0, 1000) + ")");
      continue;
    }
    try {
      const readback = await withCategory(() => api.getRow("botcomponents", caseId, caseSelect));
      validateCase(readback, schema, caseId, setId);
      verified.push(schema);
    } catch (error) {
      unverified.push(schema + " (" + sanitizeErrorMessage(error instanceof Error ? error.message : "Unclassified readback failure").slice(0, 1000) + ")");
    }
  }

  return [
    "Added experimental evaluation test set " + JSON.stringify(parsed.name) + " (" + setId + ") to " + JSON.stringify(str(agent, "name") ?? agentId) + ".",
    "  schema name: " + schemaName,
    parsed.data === undefined ? "  proposed graders: " + (parsed.graders ?? [DEFAULT_EVALUATION_GRADER]).join(", ") : "  graders: supplied in raw body; runtime behavior unverified",
    "  case-link writes accepted: " + linked.length + " of " + requestedCases.length,
    "  case links verified by readback: " + verified.length + " of " + requestedCases.length,
    ...(failed.length > 0
      ? ["", "No successful link-write response for " + failed.length + " case(s); the set exists but may be incomplete. Failed requests can have uncertain outcomes:", ...failed.map((f) => "  • " + f)]
      : []),
    ...(unverified.length > 0
      ? ["", unverified.length + " accepted link-write response(s) were not confirmed by readback:", ...unverified.map((f) => "  • " + f)]
      : []),
    "",
    "Membership is the case's ParentBotComponentId lookup, not a key in the set body. Separately inspect the service's totalTestCases with list_copilot_agent_evaluation_test_sets.",
    "Each case write is conditional on its last-read ETag; the set and its members are not one atomic transaction. Stop concurrent editing and inspect partial outcomes before retrying. Never delete the set as rollback: that can cascade-delete linked cases.",
    "EXPERIMENTAL: Active/listing/count readback is not proof of grader behavior or successful evaluation. No evaluation was started and nothing was published.",
  ].join("\n");
}

export async function handleAddCopilotAgentTestCase(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = addCopilotAgentTestCaseInputSchema.parse(input);
  if (parsed.conversationTurns !== undefined) return handleConversationTestCase(context, input);
  const api = requireApi(context.api);
  const agentId = assertGuid(parsed.agentId, "agentId");
  const agent = await withCategory(() => api.getRow("bots", agentId, "name,schemaname"));
  const agentSchema = str(agent, "schemaname");
  if (!agentSchema) throw new ValidationError(`Agent ${agentId} has no schema name.`);
  const name = parsed.name ?? parsed.input.slice(0, 200);
  const schemaName = `${agentSchema}.testcase.${toolSchemaSegment(name).slice(0, 80)}`;
  const content = buildTestCaseYaml({
    input: parsed.input,
    ...(parsed.expectedOutput !== undefined ? { expectedOutput: parsed.expectedOutput } : {}),
  });
  const created = await withCategory(() =>
    api.createRow("botcomponents", {
      name,
      componenttype: COMPONENT_TYPES.testCase,
      schemaname: schemaName,
      data: content,
      "parentbotid@odata.bind": `/bots(${agentId})`,
    }),
  );
  return [
    `Added test case "${name}" (${str(created, "botcomponentid") ?? "?"}) to "${str(agent, "name") ?? agentId}".`,
    "This wrote a raw EvaluationData component. Verify it in Copilot Studio before relying on it; publish is not required for test data.",
  ].join("\n");
}

// =============================================================================
// get_copilot_agent_transcripts
// =============================================================================

/**
 * Copilot Studio stores conversation history in the Dataverse table
 * `conversationtranscript`, linked to the agent by `bot_conversationtranscriptid`.
 *
 * The column name is not a guess. A filter on `_bot_conversationtranscriptid_value`
 * reaches the AUTHORIZATION check ("Access denied"), while a wrong name is
 * rejected earlier by the parser ("Could not find a property named ..."). So
 * the query is known to be well-formed even where the rows cannot be read.
 */
const TRANSCRIPT_DENIED =
  "Reading conversation transcripts was refused. The table exists and the query is correct, so this is a permission or licence wall, not a mistake in the request: `conversationtranscript` rows need a security role granting Read on that table, and the environment's transcript retention must be on. An account that can author agents is NOT automatically allowed to read what people said to them.";

export const getCopilotAgentTranscriptsInputSchema = z.object({
  agentId: z.string().min(1),
  since: z.string().max(40).optional(),
  top: z.number().int().min(1).max(200).optional(),
  includeContent: z.boolean().optional(),
});

export const getCopilotAgentTranscriptsTool = {
  name: "get_copilot_agent_transcripts",
  description:
    "Read stored conversations for a Copilot Studio agent from the Dataverse `conversationtranscript` table. This is the raw source behind Copilot Studio's analytics — what people actually said to the agent. NOTE: authoring an agent does not grant permission to read its transcripts; the read is commonly refused, and this tool explains that case rather than returning an empty result that would read as 'no conversations'.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: { type: "string", description: "The agent's botid (GUID)" },
      since: {
        type: "string",
        description: "ISO date/time — only conversations started at or after this",
      },
      top: { type: "number", description: "Maximum conversations to return (default 20, max 200)" },
      includeContent: {
        type: "boolean",
        description:
          "Include each transcript's raw `content`. It is a large JSON blob whose exact shape this server has not been able to observe, so it is returned as-is rather than reformatted.",
      },
    },
    required: ["agentId"],
  },
};

export async function handleGetCopilotAgentTranscripts(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = getCopilotAgentTranscriptsInputSchema.parse(input);
  const api = requireApi(context.api);
  const agentId = assertGuid(parsed.agentId, "agentId");

  let filter = `_bot_conversationtranscriptid_value eq ${agentId}`;
  if (parsed.since !== undefined) {
    if (Number.isNaN(Date.parse(parsed.since))) {
      throw new ValidationError(
        `since must be an ISO date/time — got ${JSON.stringify(parsed.since)}`,
      );
    }
    filter += ` and conversationstarttime ge ${new Date(parsed.since).toISOString()}`;
  }

  const select = parsed.includeContent
    ? "conversationtranscriptid,name,conversationstarttime,createdon,metadata,content"
    : "conversationtranscriptid,name,conversationstarttime,createdon,metadata";

  const limit = parsed.top ?? 20;
  let rows: Row[];
  let incomplete: boolean;
  try {
    const page = await withCategory(() =>
      api.queryRows("conversationtranscripts", {
        select,
        filter,
        orderBy: "conversationstarttime desc",
        top: limit,
      }),
    );
    rows = page.value.slice(0, limit);
    incomplete = page.nextLink !== undefined || page.value.length > limit;
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    if (/access denied|permission|license|privilege/i.test(message)) {
      throw new ValidationError(`${TRANSCRIPT_DENIED}\n\nDataverse said: ${message}`);
    }
    throw err;
  }

  if (rows.length === 0) {
    if (incomplete) {
      return "Dataverse returned an empty first transcript page plus a continuation. The conversation listing is incomplete; no conclusion about the absence of stored conversations can be issued.";
    }
    return "No stored conversations for this agent. Transcripts only appear once the environment retains them and someone has actually talked to the published agent — a newly published agent legitimately has none.";
  }

  const lines = [`Conversations (${rows.length}, newest first):`, ""];
  if (incomplete) {
    lines.push(
      `⚠ Conversation listing is incomplete: this report covers only the first page (at most ${limit} conversations). Additional conversations were not inspected; no continuation was followed.`,
      "",
    );
  }
  for (const row of rows) {
    lines.push(
      `  ${str(row, "conversationstarttime") ?? str(row, "createdon") ?? "(no start time)"}`,
    );
    lines.push(`    id: ${str(row, "conversationtranscriptid") ?? "?"}`);
    const name = str(row, "name");
    if (name) lines.push(`    name: ${name}`);
    const metadata = str(row, "metadata");
    if (metadata) lines.push(`    metadata: ${metadata.slice(0, 500)}`);
    if (parsed.includeContent) {
      const content = str(row, "content") ?? "";
      lines.push(
        `    content (${content.length} chars, raw):`,
        ...content
          .slice(0, 4000)
          .split("\n")
          .map((l) => `      ${l}`),
      );
    }
    lines.push("");
  }
  return lines.join("\n").trimEnd();
}

// =============================================================================
// add_copilot_agent_tool
// =============================================================================

/** Strip a display name down to what a schema-name segment allows. */
export function toolSchemaSegment(name: string): string {
  const cleaned = name.replace(/[^A-Za-z0-9]/g, "");
  return cleaned === "" ? "Tool" : cleaned;
}

/**
 * Build the `TaskDialog` YAML that declares an agent tool.
 *
 * The shape is copied from a working agent, not invented: `inputs` entries are
 * `ManualTaskInput` when the author fixes the value and `AutomaticTaskInput`
 * when the model supplies it at runtime, and `modelDescription` is the text the
 * model reads when deciding whether to call the tool.
 */
// Conservative MCP-name subset; preserve exact case and never normalize a name.
const MCP_TOOL_NAME_PATTERN = /^[A-Za-z0-9_.-]{1,128}$(?![\s\S])/;
const mcpToolNamesSchema = z.array(z.string().regex(MCP_TOOL_NAME_PATTERN)).min(1).max(100)
  .refine((names) => new Set(names).size === names.length, "MCP tool names must be unique.");
const MCP_SELECTION_NOTE =
  "Selected MCP tool names were authored in the component source. This does not verify catalog membership, runtime enforcement, granted permissions or end-user confirmation; test the resulting draft separately.";

export function buildToolYaml(args: {
  displayName: string;
  modelDescription?: string;
  action:
    | { kind: "connector"; connectionReference: string; operationId: string; mode: string }
    | { kind: "flow"; flowId: string }
    | { kind: "agent"; gptComponentSchemaName: string }
    | { kind: "mcpServer"; connectionReference: string; operationId: string; mode: string; mcpToolNames?: readonly string[]; mcpKnownToolNames?: readonly string[] };
  inputs: readonly { propertyName: string; value?: string | undefined }[];
  outputs: readonly string[];
}): string {
  if (args.action.kind === "mcpServer" && (args.inputs.length > 0 || args.outputs.length > 0)) {
    throw new ValidationError("MCP inputs/outputs customization is unsupported: outer TaskDialog fields do not establish per-tool defaults or bindings. No source was generated.");
  }
  const selectedMcpTools = args.action.kind === "mcpServer" && args.action.mcpToolNames !== undefined
    ? mcpToolNamesSchema.parse(args.action.mcpToolNames)
    : undefined;
  const knownMcpTools = args.action.kind === "mcpServer" && args.action.mcpKnownToolNames !== undefined
    ? mcpToolNamesSchema.parse(args.action.mcpKnownToolNames)
    : undefined;
  if (knownMcpTools !== undefined &&
      (selectedMcpTools === undefined || knownMcpTools.some((name) => !selectedMcpTools.includes(name)))) {
    throw new ValidationError("mcpKnownToolNames requires mcpToolNames and must be an exact case-sensitive subset.");
  }
  const lines = ["kind: TaskDialog"];

  if (args.inputs.length > 0) {
    lines.push("inputs:");
    for (const input of args.inputs) {
      if (input.value === undefined) {
        lines.push(
          "  - kind: AutomaticTaskInput",
          `    propertyName: ${yamlStringScalar(input.propertyName)}`,
          "",
        );
      } else {
        lines.push(
          "  - kind: ManualTaskInput",
          `    propertyName: ${yamlStringScalar(input.propertyName)}`,
          `    value: ${yamlStringScalar(input.value)}`,
          "",
        );
      }
    }
  }

  lines.push(`modelDisplayName: ${yamlStringScalar(args.displayName)}`);
  if (args.modelDescription !== undefined) {
    lines.push(`modelDescription: ${yamlStringScalar(args.modelDescription)}`);
  }

  if (args.outputs.length > 0) {
    lines.push("outputs:");
    for (const output of args.outputs) lines.push(`  - propertyName: ${yamlStringScalar(output)}`);
  }

  lines.push("", "action:");
  if (args.action.kind === "connector") {
    lines.push(
      "  kind: InvokeConnectorTaskAction",
      `  connectionReference: ${yamlStringScalar(args.action.connectionReference)}`,
      "  connectionProperties:",
      `    mode: ${yamlStringScalar(args.action.mode)}`,
      "",
      `  operationId: ${yamlStringScalar(args.action.operationId)}`,
    );
  } else if (args.action.kind === "flow") {
    lines.push("  kind: InvokeFlowTaskAction", `  flowId: ${yamlStringScalar(args.action.flowId)}`);
  } else if (args.action.kind === "agent") {
    lines.push(
      "  kind: InvokeAgentTaskAction",
      `  gptComponentSchemaName: ${yamlStringScalar(args.action.gptComponentSchemaName)}`,
    );
  } else {
    // Read off a production Work IQ tool: an MCP server is an EXTERNAL AGENT
    // reached over a connection, whose operation is described as MCP metadata.
    lines.push(
      "  kind: InvokeExternalAgentTaskAction",
      `  connectionReference: ${yamlStringScalar(args.action.connectionReference)}`,
      "  connectionProperties:",
      `    mode: ${yamlStringScalar(args.action.mode)}`,
      "",
      "  operationDetails:",
      "    kind: ModelContextProtocolMetadata",
      `    operationId: ${yamlStringScalar(args.action.operationId)}`,
    );
    if (selectedMcpTools !== undefined) {
      // Microsoft experimental authoring schema, pinned 1838950:
      // https://github.com/microsoft/skills-for-copilot-studio/blob/1838950fa95f1d2f8365e826acd8a600ff593bd9/reference/bot.schema.yaml-authoring.json
      // ModelContextProtocolMetadata.tools -> UseSpecificTools.tools:string[].
      lines.push("    tools:", "      kind: UseSpecificTools", "      tools:");
      for (const name of selectedMcpTools) lines.push(`        - ${yamlStringScalar(name)}`);
    }
    if (knownMcpTools !== undefined) {
      // Same pinned experimental schema: McpToolDefinitionNoKind requires name.
      // This is name-only cached metadata, not a discovered schema or timestamp.
      lines.push("    knownTools:");
      for (const name of knownMcpTools) lines.push(`      - name: ${yamlStringScalar(name)}`);
    }
  }

  lines.push("", "outputMode: All", "");
  return lines.join("\n");
}

export const addCopilotAgentToolInputSchema = z.object({
  agentId: z.string().min(1),
  name: z.string().min(1).max(100),
  modelDescription: z.string().min(1).max(2000).optional(),
  connectorId: z.string().min(1).max(200).optional(),
  connectionId: z.string().min(1).max(200).optional(),
  connectionReferenceName: z.string().regex(CONNECTION_REFERENCE_NAME_PATTERN).optional(),
  expectedSourceSha256: z.string().length(64).regex(SOURCE_SHA256_PATTERN).optional(),
  operationId: z.string().min(1).max(200).optional(),
  flowId: z.string().min(1).optional(),
  targetAgentId: z.string().min(1).optional(),
  mcpServer: z.boolean().optional(),
  mcpToolNames: mcpToolNamesSchema.optional(),
  mcpKnownToolNames: mcpToolNamesSchema.optional(),
  connectionMode: z.enum(["Invoker", "Maker"]).optional(),
  inputs: z
    .array(
      z.object({
        propertyName: z.string().min(1).max(100),
        value: z.string().max(2000).optional(),
      }),
    )
    .max(50)
    .optional(),
  outputs: z.array(z.string().min(1).max(100)).max(50).optional(),
  schemaName: z.string().min(1).max(200).optional(),
}).superRefine((value, context) => {
  if (value.mcpServer === true) {
    for (const field of ["inputs", "outputs"] as const) {
      if (value[field] !== undefined) {
        context.addIssue({ code: "custom", path: [field], message: "MCP inputs/outputs customization is unsupported: outer TaskDialog fields do not establish per-tool defaults or bindings. Omit inputs and outputs." });
      }
    }
  }
  if (value.mcpToolNames !== undefined && value.mcpServer !== true) {
    context.addIssue({ code: "custom", path: ["mcpToolNames"], message: "mcpToolNames requires mcpServer=true." });
  }
  if (value.mcpKnownToolNames !== undefined &&
      (value.mcpServer !== true || value.mcpToolNames === undefined ||
        value.mcpKnownToolNames.some((name) => !value.mcpToolNames!.includes(name)))) {
    context.addIssue({ code: "custom", path: ["mcpKnownToolNames"], message: "mcpKnownToolNames requires mcpServer=true and an exact case-sensitive subset of mcpToolNames." });
  }
});

export const addCopilotAgentToolTool = {
  name: "add_copilot_agent_tool",
  description:
    "Add an agent tool \u2014 a connector operation, MCP server, solution-aware cloud flow, or another agent. Every existing replacement requires expectedSourceSha256, a unique complete lookup, exact component identity/schema and a matching action kind; the full source is conditionally updated with its fresh ETag and independently read back. Connector/MCP tools also require connectorId + connectionId + operationId + connectionReferenceName naming an active existing Dataverse reference whose connector and physical connection match (logical-name suffixes are not physical IDs), and a replacement retains that association; no connection is created or rebound. Optional experimental mcpToolNames authors a UseSpecificTools selection and mcpKnownToolNames authors name-only knownTools metadata; omission preserves the prior source, and MCP input/output customization is refused. Readback is separate from acceptance: saved metadata does not prove publication, authentication, discovery or runtime behavior.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: { type: "string", description: "The agent's botid (GUID)" },
      name: { type: "string", description: "Display name, also shown to the model" },
      modelDescription: {
        type: "string",
        description:
          "What the tool does, in the model's words. Required for connector, flow, and agent tools; optional for MCP servers, which describe their own tools.",
      },
      connectorId: {
        type: "string",
        description: "Connector to call, e.g. 'shared_sharepointonline' (connector tools only)",
      },
      connectionId: {
        type: "string",
        description:
          "Id of an existing connection for that connector, from list_connections (connector tools only)",
      },
      connectionReferenceName: {
        type: "string", pattern: CONNECTION_REFERENCE_NAME_PATTERN.source,
        description: "Required for connector/MCP tools: complete persisted Dataverse connectionreferencelogicalname. Never manufacture it from a physical connection ID.",
      },
      expectedSourceSha256: {
        type: "string", minLength: 64, maxLength: 64, pattern: SOURCE_SHA256_PATTERN.source,
        description: "Required when replacing any existing tool; exact full stored source hash from get_copilot_agent_component. The existing TaskDialog action kind must match the requested tool kind. Not valid for creation.",
      },
      operationId: {
        type: "string",
        description: "Connector operation, e.g. 'GetItemAttachments' (connector tools only)",
      },
      flowId: { type: "string", description: "Cloud flow GUID (flow tools only)" },
      targetAgentId: {
        type: "string",
        description:
          "Another agent's botid, to call THAT agent as a tool. Its Custom GPT component is resolved for you. The target must be reachable from this agent — see configure_copilot_agent's connectableByOtherAgents.",
      },
      mcpServer: {
        type: "boolean",
        description:
          "Set true to declare an MCP-server tool: connectorId + connectionId identify the MCP connector's connection, operationId is the MCP operation (e.g. 'mcp_MeServer'). Expressed on the platform as InvokeExternalAgentTaskAction with ModelContextProtocolMetadata — read off a production Work IQ tool. No modelDescription is needed: the server describes its own tools.",
      },
      mcpToolNames: {
        type: "array",
        minItems: 1,
        maxItems: 100,
        uniqueItems: true,
        items: { type: "string", minLength: 1, maxLength: 128, pattern: MCP_TOOL_NAME_PATTERN.source },
        description: "Experimental, only with mcpServer=true: exact case-sensitive MCP tool names from a previously inspected catalog. Authors UseSpecificTools in the Microsoft experimental YAML schema; does not verify catalog membership, runtime enforcement, permissions or end-user confirmation. Omit to retain legacy generated source. This is full tool-source authoring, not a surgical selection-only edit.",
      },
      mcpKnownToolNames: {
        type: "array",
        minItems: 1,
        maxItems: 100,
        uniqueItems: true,
        items: { type: "string", minLength: 1, maxLength: 128, pattern: MCP_TOOL_NAME_PATTERN.source },
        description: "Experimental name-only knownTools cached metadata, requiring mcpServer=true and mcpToolNames. Exact case-sensitive unique subset of the selected names, from an inspected catalog. Does not infer input/output schemas or refresh timestamps, verify runtime/catalog membership, or complete end-user binding review. Omission adds nothing; source replacement may require renewed binding review.",
      },
      connectionMode: {
        type: "string",
        enum: ["Invoker", "Maker"],
        description:
          "Whose connection runs the call. Defaults to Invoker (the signed-in user), which is what a working agent was observed using.",
      },
      inputs: {
        type: "array",
        description:
          "Non-MCP tool inputs: a value authors a fixed design-time input; omission authors a model-filled input. Unsupported with mcpServer=true, including an empty array: this builder cannot establish per-MCP-tool defaults or bindings.",
        items: {
          type: "object",
          properties: {
            propertyName: { type: "string" },
            value: { type: "string" },
          },
          required: ["propertyName"],
        },
      },
      outputs: {
        type: "array",
        description:
          "Non-MCP output property names; defaults to ['Response']. Unsupported with mcpServer=true, including an empty array. Omitted MCP outputs generate no outer output declarations and do not verify a live catalog or response schema.",
        items: { type: "string" },
      },
      schemaName: {
        type: "string",
        description: "Override the generated '<agentSchema>.action.<Name>' schema name",
      },
    },
    required: ["agentId", "name"],
  },
};

export async function handleAddCopilotAgentTool(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = addCopilotAgentToolInputSchema.parse(input);
  const api = requireApi(context.api);
  const agentId = assertGuid(parsed.agentId, "agentId");

  const isFlow = parsed.flowId !== undefined;
  const isAgent = parsed.targetAgentId !== undefined;
  const isMcp = parsed.mcpServer === true;
  if (!isMcp && parsed.modelDescription === undefined) {
    throw new ValidationError(
      "Connector, flow, and agent tools need modelDescription — this is how the model decides when to call them. An MCP server may omit it because the server describes its own tools.",
    );
  }
  const connectorFields = ["connectorId", "connectionId", "operationId", "connectionReferenceName"] as const;
  if (isMcp && (isFlow || isAgent)) {
    throw new ValidationError(
      "mcpServer=true declares a connector-backed MCP tool — it cannot also be a flow or an agent tool.",
    );
  }
  if (isFlow && isAgent) {
    throw new ValidationError(
      "A tool calls a flow or another agent, not both — pass flowId or targetAgentId, not each.",
    );
  }
  if (isAgent) {
    const stray = connectorFields.filter((k) => parsed[k] !== undefined);
    if (stray.length > 0) {
      throw new ValidationError(
        `An agent tool is defined by targetAgentId alone — remove ${stray.join(", ")}.`,
      );
    }
  } else if (isFlow) {
    const stray = connectorFields.filter((k) => parsed[k] !== undefined);
    if (stray.length > 0) {
      throw new ValidationError(
        `A flow tool is defined by flowId alone — remove ${stray.join(", ")}, or drop flowId to declare a connector tool instead.`,
      );
    }
  } else {
    const missing = connectorFields.filter((k) => parsed[k] === undefined);
    if (missing.length > 0) {
      throw new ValidationError(
        `A connector tool needs ${missing.join(", ")}. To point the tool at a Power Automate flow instead, pass flowId.`,
      );
    }
  }

  const agent = await withCategory(() => api.getRow("bots", agentId, "name,schemaname"));
  const agentSchema = str(agent, "schemaname");
  if (agentSchema === undefined || agentSchema === "") {
    throw new ValidationError(
      `Agent ${agentId} has no schema name, so a component schema name cannot be derived.`,
    );
  }

  const schemaName = parsed.schemaName ?? `${agentSchema}.action.${toolSchemaSegment(parsed.name)}`;
  let connectionReference: Extract<ResolvedToolConnection, { state: "resolved" }> | undefined;
  if (!isFlow && !isAgent) {
    const resolved = await resolveToolConnectionReference(api, parsed.connectionReferenceName ?? "");
    const connectorId = toolConnectorId(parsed.connectorId);
    const connectionId = connectorId === undefined ? undefined : toolPhysicalConnectionId(parsed.connectionId, connectorId);
    if (resolved.state !== "resolved" || !connectorId || !connectionId ||
        resolved.connectorId.toLowerCase() !== connectorId.toLowerCase() ||
        resolved.connectionId?.toLowerCase() !== connectionId.toLowerCase()) {
      throw new ValidationError("The exact persisted connection reference, connector and physical connection could not be verified. No component or connection-reference write was sent.");
    }
    connectionReference = resolved;
  }

  let agentGptSchemaName: string | undefined;
  if (isAgent) {
    const targetId = assertGuid(parsed.targetAgentId ?? "", "targetAgentId");
    // Resolve the target's Custom GPT component rather than assuming the
    // conventional "<schema>.gpt.default" — the action must name a component
    // that really exists or publish rejects it.
    // The reference is resolved against PUBLISHED content, so an unpublished
    // target fails publish with "Gpt with id '<name>' not found" — which reads
    // as a missing component even though the component plainly exists.
    // Verified: a reference to a published agent publishes; the identical
    // reference to a never-published one does not.
    const targetBot = await withCategory(() => api.getRow("bots", targetId, "name,publishedon"));
    if (str(targetBot, "publishedon") === undefined) {
      throw new ValidationError(
        `Agent "${str(targetBot, "name") ?? targetId}" has never been published, so another agent cannot call it: the reference resolves against published content, and publish would fail with "Gpt with id ... not found" even though the component exists. Publish that agent first.`,
      );
    }

    const gptComponents = (
      await withCategory(() =>
        api.queryRows("botcomponents", {
          select: "schemaname",
          filter: `_parentbotid_value eq ${targetId} and componenttype eq ${COMPONENT_TYPES.customGpt}`,
          top: 1,
        }),
      )
    ).value;
    const found = gptComponents[0];
    agentGptSchemaName = found === undefined ? undefined : str(found, "schemaname");
    if (agentGptSchemaName === undefined || agentGptSchemaName === "") {
      throw new ValidationError(
        `Agent ${targetId} has no instructions component, so there is nothing for another agent to call. Give it instructions first with set_copilot_agent_instructions.`,
      );
    }
  }

  const content = buildToolYaml({
    displayName: parsed.name,
    modelDescription: parsed.modelDescription,
    action: isAgent
      ? { kind: "agent", gptComponentSchemaName: agentGptSchemaName ?? "" }
      : isFlow
        ? { kind: "flow", flowId: assertGuid(parsed.flowId ?? "", "flowId") }
        : isMcp
          ? {
              kind: "mcpServer",
              connectionReference: connectionReference?.referenceName ?? "",
              operationId: parsed.operationId ?? "",
              mode: parsed.connectionMode ?? "Invoker",
              mcpToolNames: parsed.mcpToolNames,
              mcpKnownToolNames: parsed.mcpKnownToolNames,
            }
          : {
              kind: "connector",
              connectionReference: connectionReference?.referenceName ?? "",
              operationId: parsed.operationId ?? "",
              mode: parsed.connectionMode ?? "Invoker",
            },
    inputs: parsed.inputs ?? [],
    outputs: parsed.outputs ?? (isMcp ? [] : ["Response"]),
  });

  // Upsert by schema name so re-running does not leave a duplicate tool behind.
  const existingPage = (
    await withCategory(() =>
      api.queryRows("botcomponents", {
        select: "botcomponentid",
        filter: `_parentbotid_value eq ${agentId} and schemaname eq '${schemaName.replace(/'/g, "''")}'`,
        top: 2,
      }),
    )
  );
  if (existingPage === null || typeof existingPage !== "object" || Array.isArray(existingPage) ||
      !Array.isArray(existingPage.value) || existingPage.value.length > 1 || existingPage.nextLink !== undefined) {
    throw new ValidationError("The existing tool lookup was ambiguous or incomplete; no write sent.");
  }
  const existing = existingPage.value;

  const notes = [
    "",
    "Publish the agent to make the tool live — publish is also the only real validator here, and it names the component if the tool is malformed.",
  ];

  if (existing.length === 1) {
    const previous = existing[0];
    const id = previous !== null && typeof previous === "object" && !Array.isArray(previous)
      ? str(previous, "botcomponentid") : undefined;
    if (id === undefined || id.length !== 36 || !GUID_PATTERN.test(id)) {
      throw new ValidationError("The existing tool ID could not be verified; no write sent.");
    }
    if (parsed.expectedSourceSha256 === undefined) {
      throw new ValidationError("Replacing any existing tool requires expectedSourceSha256; inspect the existing component first. No write sent.");
    }
    let current: Row;
    try {
      current = await api.getRow("botcomponents", id, `${GUARDED_COMPONENT_SELECT},schemaname`,
        connectionReference === undefined ? undefined : "botcomponent_connectionreference($select=connectionreferenceid;$top=2)");
    } catch (error) {
      throw new CopilotComponentUpdateError("preflight", error instanceof ApiError ? error.statusCode ?? 502 : 502);
    }
    const guard = guardedComponentPreflight(current, id, agentId, parsed.expectedSourceSha256, COMPONENT_TYPES.topic);
    if (str(current, "schemaname") !== schemaName) {
      throw new ValidationError("The existing tool schema name does not match the requested target; no write sent.");
    }
    // Inspect exact structural nodes, not naming conventions or nested kind text.
    // This is a replacement-kind guard, not full platform schema validation.
    const document = parseDocument(str(current, "data") ?? "", { uniqueKeys: true, strict: true });
    const root = document.contents;
    const rootKind: unknown = isMap(root) ? root.get("kind", true) : undefined;
    const action: unknown = isMap(root) ? root.get("action", true) : undefined;
    const actionKind: unknown = isMap(action) ? action.get("kind", true) : undefined;
    const expectedActionKind = isFlow ? "InvokeFlowTaskAction" : isAgent ? "InvokeAgentTaskAction" :
      isMcp ? "InvokeExternalAgentTaskAction" : "InvokeConnectorTaskAction";
    const operationDetails: unknown = isMap(action) ? action.get("operationDetails", true) : undefined;
    const metadataKind: unknown = isMap(operationDetails) ? operationDetails.get("kind", true) : undefined;
    if (document.errors.length > 0 || !isScalar(rootKind) || rootKind.value !== "TaskDialog" ||
        !isScalar(actionKind) || actionKind.value !== expectedActionKind ||
        (isMcp && (!isScalar(metadataKind) || metadataKind.value !== "ModelContextProtocolMetadata"))) {
      throw new ValidationError("The existing source must be a TaskDialog with the requested action kind; no write sent.");
    }
    if (connectionReference !== undefined) {
      const linked = current.botcomponent_connectionreference;
      const referenceNode = isMap(action) ? action.get("connectionReference", true) : undefined;
      if (!isScalar(referenceNode) || typeof referenceNode.value !== "string" ||
          referenceNode.value.toLowerCase() !== connectionReference.referenceName.toLowerCase() ||
          current["botcomponent_connectionreference@odata.nextLink"] !== undefined ||
          !Array.isArray(linked) || linked.length !== 1 ||
          linked[0] === null || typeof linked[0] !== "object" || Array.isArray(linked[0]) ||
          str(linked[0] as Row, "connectionreferenceid")?.toLowerCase() !== connectionReference.referenceId.toLowerCase()) {
        throw new ValidationError("The existing tool does not have the exact logical reference and reference association; this tool does not rebind it. No write sent.");
      }
    }
    const updateResult = await guardedComponentWrite(api, id, agentId, COMPONENT_TYPES.topic, { name: parsed.name, data: content }, guard.etag);
    return updateResult +
      (connectionReference === undefined ? "" : "\nThe source-conditional update does not atomically guard connection-reference metadata or associations, or verify runtime binding. No association repair was attempted.") +
      (isMcp && str(current, "data") !== content ? `\n${MCP_BINDING_REVIEW_NOTE}` : "") +
      (parsed.mcpToolNames === undefined ? "" : `\n${MCP_SELECTION_NOTE}`);
  }

  if (parsed.expectedSourceSha256 !== undefined) {
    throw new ValidationError("expectedSourceSha256 requires an existing tool component; no create sent.");
  }
  const created = await withCategory(() =>
    api.createRow("botcomponents", {
      name: parsed.name,
      componenttype: COMPONENT_TYPES.topic,
      schemaname: schemaName,
      data: content,
      "parentbotid@odata.bind": `/bots(${agentId})`,
      // Documented collection-valued association on create; only an existing row.
      ...(connectionReference === undefined ? {} : {
        "botcomponent_connectionreference@odata.bind": [`/connectionreferences(${connectionReference.referenceId})`],
      }),
    }),
  );

  if (connectionReference !== undefined) {
    const createdId = str(created, "botcomponentid");
    let verified = false;
    if (createdId !== undefined && GUID_PATTERN.test(createdId)) {
      try {
        const after = await api.getRow("botcomponents", createdId,
          "botcomponentid,_parentbotid_value,componenttype,schemaname,name,data",
          "botcomponent_connectionreference($select=connectionreferenceid;$top=2)");
        const linked = after.botcomponent_connectionreference;
        verified = str(after, "botcomponentid")?.toLowerCase() === createdId.toLowerCase() &&
          str(after, "_parentbotid_value")?.toLowerCase() === agentId.toLowerCase() &&
          num(after, "componenttype") === COMPONENT_TYPES.topic && after.data === content &&
          after.schemaname === schemaName && after.name === parsed.name &&
          after["botcomponent_connectionreference@odata.nextLink"] === undefined &&
          Array.isArray(linked) && linked.length === 1 &&
          linked[0] !== null && typeof linked[0] === "object" && !Array.isArray(linked[0]) &&
          str(linked[0] as Row, "connectionreferenceid")?.toLowerCase() === connectionReference.referenceId.toLowerCase();
      } catch {
        // An accepted create must not be repeated just because readback failed.
      }
    }
    return [
      `Tool create accepted (${createdId !== undefined && GUID_PATTERN.test(createdId) ? createdId : "ID unavailable"}).`,
      verified ? "Readback verified: exact component source and existing-reference association matched at observation time." :
        "Readback not verified: inspect the component before retrying; do not automatically repeat the create.",
      "This does not prove publication, connection authorization, tool discovery or runtime behavior.",
      ...(isMcp ? [MCP_BINDING_REVIEW_NOTE] : []),
      ...(parsed.mcpToolNames === undefined ? [] : [MCP_SELECTION_NOTE]),
    ].join("\n");
  }
  return [
    `Added tool "${parsed.name}" (${str(created, "botcomponentid") ?? "?"}) to "${str(agent, "name") ?? agentId}".`,
    `  schema name: ${schemaName}`,
    ...notes,
  ].join("\n");
}

// =============================================================================
// publish_copilot_agent
// =============================================================================

const MAX_PUBLISH_DIAGNOSTICS = 20;

function safePublishText(value: unknown, maxLength: number): string | undefined {
  if (typeof value !== "string") return undefined;
  // Bound the untrusted Dataverse field before running several global
  // sanitization expressions over it. The extra window lets a token or path
  // that begins near the output boundary be fully redacted before slicing.
  const work = value.slice(0, maxLength + 4_096);
  const safe = Array.from(sanitizeErrorMessage(work), (character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 0x1f || codePoint === 0x7f ? " " : character;
  })
    .join("")
    .replace(/\s+/g, " ")
    .trim();
  return safe ? safe.slice(0, maxLength) : undefined;
}

/**
 * Read the real outcome of the last publish out of `bot.synchronizationstatus`.
 *
 * The column is JSON (sometimes delivered as a string). Only
 * `lastFinishedPublishOperation` says whether publishing actually worked;
 * the HTTP status of PvaPublish does not. Diagnostics are flattened into
 * readable lines so the caller learns WHY it failed, not just that it did.
 */
export function readPublishOutcome(raw: unknown): {
  status?: string;
  operationEnd?: string;
  syncState?: string;
  problems: string[];
} {
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { problems: [] };
    }
  }
  if (parsed === null || typeof parsed !== "object") return { problems: [] };

  const state = (parsed as Record<string, unknown>)["currentSynchronizationState"];
  const syncState =
    state !== null &&
    typeof state === "object" &&
    typeof (state as Record<string, unknown>)["state"] === "string"
      ? safePublishText((state as Record<string, unknown>)["state"], 100)
      : undefined;

  const op = (parsed as Record<string, unknown>)["lastFinishedPublishOperation"];
  if (op === null || op === undefined || typeof op !== "object") {
    return syncState !== undefined ? { syncState, problems: [] } : { problems: [] };
  }

  const opRecord = op as Record<string, unknown>;
  const status = safePublishText(opRecord["status"], 100);
  const operationEnd = safePublishText(opRecord["operationEnd"], 100);

  const problems: string[] = [];
  let diagnosticsTruncated = false;
  const details = opRecord["diagnosticDetails"];
  if (Array.isArray(details)) {
    const detailGroups = details.slice(0, MAX_PUBLISH_DIAGNOSTICS);
    diagnosticsTruncated ||= details.length > detailGroups.length;
    for (const detail of detailGroups) {
      if (detail === null || typeof detail !== "object") continue;
      const list = (detail as Record<string, unknown>)["diagnosticList"];
      if (!Array.isArray(list)) continue;
      const remaining = MAX_PUBLISH_DIAGNOSTICS - problems.length;
      if (remaining === 0) {
        diagnosticsTruncated = true;
        break;
      }
      const items = list.slice(0, remaining);
      diagnosticsTruncated ||= list.length > items.length;
      for (const item of items) {
        if (item === null || typeof item !== "object") continue;
        const rec = item as Record<string, unknown>;
        const message = safePublishText(rec["errorMessage"], 500);
        const code = safePublishText(rec["errorCode"], 100);
        if (message !== undefined)
          problems.push(code !== undefined ? `${code}: ${message}` : message);
      }
    }
  }
  if (diagnosticsTruncated) {
    const notice =
      "Additional Copilot Studio diagnostics were omitted to keep the response bounded.";
    if (problems.length >= MAX_PUBLISH_DIAGNOSTICS) {
      problems[MAX_PUBLISH_DIAGNOSTICS - 1] = notice;
    } else {
      problems.push(notice);
    }
  }
  return {
    ...(status !== undefined ? { status } : {}),
    ...(operationEnd !== undefined ? { operationEnd } : {}),
    ...(syncState !== undefined ? { syncState } : {}),
    problems,
  };
}

export const publishCopilotAgentInputSchema = z.object({
  agentId: z.string().min(1),
  waitSeconds: z.number().int().min(3).max(180).optional().default(60),
  allowUnauthenticatedPublish: z.boolean().optional().default(false),
});

export const publishCopilotAgentTool = {
  name: "publish_copilot_agent",
  description:
    "Publish a Copilot Studio agent — the same action as the designer's Publish button (Dataverse bound action PvaPublish). Publishing is what makes authoring changes reach users and channels. The current authentication mode must be readable and explicit. No-authentication agents require allowUnauthenticatedPublish=true because publishing can expose an anonymous endpoint; unspecified or unknown authentication is refused. This tool never changes authentication or access policy.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: { type: "string", description: "The agent's botid (GUID)" },
      waitSeconds: {
        type: "integer",
        minimum: 3,
        maximum: 180,
        default: 60,
        description:
          "How long to poll for this publish's final status after sending one publish request (default 60 seconds)",
      },
      allowUnauthenticatedPublish: {
        type: "boolean",
        default: false,
        description:
          "Explicitly accept publishing an agent whose authentication mode is None, which can make it reachable without signing in. Does not bypass an unreadable, unspecified or unknown authentication mode, and does not change security settings.",
      },
    },
    required: ["agentId"],
  },
};

export async function handlePublishCopilotAgent(
  context: { api: DataverseApi | null; sleep?: (milliseconds: number) => Promise<void> },
  input: unknown,
): Promise<string> {
  const parsed = publishCopilotAgentInputSchema.parse(input);
  const api = requireApi(context.api);
  const agentId = assertGuid(parsed.agentId, "agentId");
  logger.info({ agentId }, "Executing publish_copilot_agent");

  // Mandatory, fresh preflight. Bare-created agents can default to no auth;
  // a permission/network failure must not turn this check into fail-open publish.
  // Dataverse bot_authenticationmode: 0 Unspecified, 1 None, 2 Integrated,
  // 3 Custom Azure Active Directory, 4 Generic OAuth2 (Microsoft table reference).
  const before = await withCategory(() =>
    api.getRow("bots", agentId, "authenticationmode,synchronizationstatus"),
  );
  const authenticationMode = num(before, "authenticationmode");
  if (authenticationMode === undefined || ![1, 2, 3, 4].includes(authenticationMode)) {
    throw new ValidationError(
      "Not published: the agent's authentication mode is unspecified, missing or unknown. Review and explicitly configure authentication in Copilot Studio, then read it back before publishing. No security settings were changed.",
    );
  }
  if (authenticationMode === 1 && !parsed.allowUnauthenticatedPublish) {
    throw new ValidationError(
      "Not published: this agent has No authentication. Publishing can expose an endpoint that people can use without signing in. Configure authentication in Copilot Studio, or pass allowUnauthenticatedPublish=true only if anonymous publication is explicitly intended. No security settings were changed.",
    );
  }
  const priorOperationEnd = readPublishOutcome(before["synchronizationstatus"]).operationEnd;

  // Namespace-qualified: the unqualified name is not resolved for this action.
  await withCategory(() =>
    api.executeBoundAction("bots", agentId, "Microsoft.Dynamics.CRM.PvaPublish", {}),
  );

  // PvaPublish returns HTTP 200 even when the publish itself FAILS — the real
  // outcome lands in synchronizationstatus.lastFinishedPublishOperation.
  // Verified live: a topic referencing a cloud flow that is not in a solution
  // published "successfully" (200) while the operation recorded
  // InvalidReferenceError / CloudFlow not found. Reporting the 200 alone would
  // be a failure that looks like a success.
  //
  // That record is the LAST FINISHED operation, though, and publishing is
  // asynchronous — so reading it straight after publishing returns the
  // PREVIOUS run's verdict. Observed live: a publish reported FAILED citing a
  // component that had already been deleted, because the record belonged to
  // an earlier attempt. `priorOperationEnd` (captured before publishing) is
  // what distinguishes this run's outcome from the last one's.
  const sleep =
    context.sleep ??
    ((milliseconds: number) => new Promise<void>((resolve) => setTimeout(resolve, milliseconds)));
  const pollIntervalMilliseconds = 3_000;
  const waitMilliseconds = parsed.waitSeconds * 1_000;
  const deadline = Date.now() + waitMilliseconds;
  let remainingSleepBudget = waitMilliseconds;
  let firstPoll = true;
  let bot: Row = {};
  let outcome: ReturnType<typeof readPublishOutcome> = { problems: [] };
  let hasFreshFinishedOperation = false;

  while (remainingSleepBudget > 0 && Date.now() < deadline) {
    const wallClockRemaining = deadline - Date.now();
    const delay = Math.min(
      firstPoll ? 1_000 : pollIntervalMilliseconds,
      remainingSleepBudget,
      wallClockRemaining,
    );
    if (delay <= 0) break;
    await sleep(delay);
    remainingSleepBudget -= delay;
    firstPoll = false;
    bot = await withCategory(() =>
      api.getRow("bots", agentId, "name,publishedon,statuscode,synchronizationstatus"),
    );
    outcome = readPublishOutcome(bot["synchronizationstatus"]);
    hasFreshFinishedOperation =
      outcome.operationEnd !== undefined && outcome.operationEnd !== priorOperationEnd;
    if (
      hasFreshFinishedOperation &&
      (outcome.status === "Succeeded" || outcome.status === "Failed")
    ) {
      break;
    }
  }

  const name = str(bot, "name") ?? agentId;

  if (!hasFreshFinishedOperation) {
    const lines = [
      `Publish started for "${name}" — the platform accepted it, but the result is not in yet.`,
    ];
    if (priorOperationEnd !== undefined && outcome.operationEnd === priorOperationEnd) {
      lines.push(
        "",
        `The last recorded publish (${priorOperationEnd}) belongs to an earlier attempt, so reporting it now would describe the wrong run.`,
      );
    } else {
      lines.push(
        "",
        "Copilot Studio has not recorded a new finished publish operation that can be tied to this request yet.",
      );
    }
    if (outcome.syncState === "Synchronizing") {
      // Observed live: after a failed publish an agent can sit in
      // "Synchronizing" indefinitely, accepting further publishes (HTTP 200)
      // without ever recording an outcome. Saying "wait a minute" there sends
      // people into a loop, so name the state instead.
      lines.push(
        "",
        "The agent is currently in the 'Synchronizing' state. If it stays there and no new result appears, the agent is stuck mid-sync — a previous failed publish can leave it that way, and further publishes are accepted without completing. Open it in Copilot Studio to clear the condition.",
      );
    } else {
      lines.push(
        "Read the agent with get_copilot_agent to see the eventual outcome; do not publish again merely to check status.",
      );
    }
    return lines.join("\n");
  }

  if (outcome.status === "Failed") {
    const lines = [`Publish FAILED for "${name}" — the agent was NOT published.`, ""];
    if (outcome.problems.length > 0) {
      lines.push("Problems reported by Copilot Studio:");
      outcome.problems.forEach((p) => lines.push(`  • ${p}`));
      lines.push("");
    }
    lines.push(
      "A common cause: a topic references a cloud flow that the agent cannot resolve — an agent-callable flow must live in a SOLUTION and use the 'When an agent calls the flow' trigger.",
    );
    return lines.join("\n");
  }

  if (outcome.status !== "Succeeded") {
    return [
      `Publish started for "${name}" — Copilot Studio has not reported a successful final outcome yet.`,
      outcome.status !== undefined ? `  current operation status: ${outcome.status}` : "",
      "",
      "Read the agent with get_copilot_agent to confirm the eventual outcome; do not publish again merely to check status.",
    ]
      .filter((line) => line !== "")
      .join("\n");
  }

  const lines = [`Published "${name}".`];
  const status = str(bot, "statuscode@OData.Community.Display.V1.FormattedValue");
  if (status) lines.push(`  provisioning: ${status}`);
  if (outcome.status) lines.push(`  last publish operation: ${outcome.status}`);
  const published = str(bot, "publishedon");
  if (published) lines.push(`  published: ${published}`);
  return lines.join("\n");
}

// =============================================================================
// get_copilot_agent_endpoint
// =============================================================================

export const getCopilotAgentEndpointInputSchema = z.object({
  agentId: z.string().min(1),
});

export const getCopilotAgentEndpointTool = {
  name: "get_copilot_agent_endpoint",
  description:
    "Get the Direct Line endpoint for a Copilot Studio agent — the address a chat client uses to hold a conversation with it. Requires a Copilot Studio licence on the signed-in user (an unlicensed account is refused with 'User Viral license is expired') and an agent that has been published.",
  inputSchema: {
    type: "object" as const,
    properties: { agentId: { type: "string", description: "The agent's botid (GUID)" } },
    required: ["agentId"],
  },
};

export async function handleGetCopilotAgentEndpoint(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = getCopilotAgentEndpointInputSchema.parse(input);
  const api = requireApi(context.api);
  const agentId = assertGuid(parsed.agentId, "agentId");
  logger.info({ agentId }, "Executing get_copilot_agent_endpoint");

  const result = await withCategory(() =>
    api.executeBoundAction<Record<string, unknown>>(
      "bots",
      agentId,
      "Microsoft.Dynamics.CRM.PvaGetDirectLineEndpoint",
      {},
    ),
  );

  const endpoint = typeof result["Endpoint"] === "string" ? result["Endpoint"] : undefined;
  if (endpoint === undefined || endpoint.trim() === "") {
    throw new ValidationError(
      "Copilot Studio returned no Direct Line endpoint. Publish the agent first and confirm the signed-in user has a Copilot Studio license.",
    );
  }
  assertAllowedEndpointHost(endpoint);
  return [
    "Direct Line endpoint:",
    `  ${endpoint}`,
    "",
    "Conversations run over this endpoint with Direct Line, or through the Microsoft 365 Agents SDK CopilotStudio client. Both need the agent to be published.",
  ].join("\n");
}

// =============================================================================
// audit_copilot_agent_access
// =============================================================================

/** `bot.authenticationmode` — who the agent makes sign in. */
const AUTH_MODE_LABELS: Record<number, string> = {
  0: "Unspecified",
  1: "No authentication",
  2: "Integrated (Microsoft Entra)",
  3: "Custom Entra ID",
  4: "Generic OAuth2",
};

/** `bot.accesscontrolpolicy` — who is allowed to use it. */
const ACCESS_POLICY_LABELS: Record<number, string> = {
  0: "Anyone",
  1: "Agent readers",
  2: "Group membership",
  3: "Anyone, including outside this tenant",
};

const AUTH_TRIGGER_LABELS: Record<number, string> = { 0: "As needed", 1: "Always" };

/**
 * Access findings for one agent, worst first.
 *
 * The combination that matters is authentication × published: an agent with
 * no authentication that has been published can be talked to by anyone who
 * can reach its Direct Line endpoint — verified live, a plain unauthenticated
 * GET returned a conversation token for exactly such an agent. Unpublished
 * agents carry the same setting but no runtime, so they are noted rather than
 * flagged.
 */
export function auditAgentAccess(row: {
  authenticationmode?: number;
  accesscontrolpolicy?: number;
  authorizedsecuritygroupids?: string;
  published: boolean;
}): string[] {
  const findings: string[] = [];
  const mode = row.authenticationmode;
  const policy = row.accesscontrolpolicy;
  const groups = (row.authorizedsecuritygroupids ?? "").trim();

  if (mode === 1) {
    findings.push(
      row.published
        ? "PUBLISHED WITH NO AUTHENTICATION — anyone who can reach its endpoint can hold a conversation with it, without signing in."
        : "No authentication configured. Not reachable yet because it is unpublished, but publishing it would open it to anyone with the endpoint.",
    );
  }
  if (policy === 3) {
    findings.push("Access policy allows people OUTSIDE this tenant.");
  }
  if (policy === 0 && mode !== 1) {
    findings.push("Access policy is 'Anyone' — any signed-in user may use it.");
  }
  if (policy === 2 && groups === "") {
    findings.push(
      "Access policy is 'Group membership' but no security groups are listed — check in Copilot Studio which groups actually apply.",
    );
  }
  if (mode === 0) {
    findings.push(
      "Authentication mode is 'Unspecified' — the effective behaviour is whatever the environment defaults to.",
    );
  }
  return findings;
}

export const auditCopilotAgentAccessInputSchema = z.object({
  agentId: z.string().optional(),
});

export const auditCopilotAgentAccessTool = {
  name: "audit_copilot_agent_access",
  description:
    "Report who can talk to your Copilot Studio agents — authentication mode, access-control policy, authorized groups and published state — flagging the combinations that expose an agent (published with no authentication, access allowed outside the tenant). Pass an agentId for one agent, or omit it to audit the first page of visible agents (at most 500). Reports incomplete results without following continuation pages. Read-only.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: {
        type: "string",
        description: "Audit one agent (GUID). Omit to audit the first page of visible agents (at most 500); incomplete results are reported.",
      },
    },
  },
};

export async function handleAuditCopilotAgentAccess(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = auditCopilotAgentAccessInputSchema.parse(input);
  const api = requireApi(context.api);
  const select =
    "botid,name,authenticationmode,accesscontrolpolicy,authenticationtrigger,authorizedsecuritygroupids,publishedon";

  let rows: Row[];
  let incomplete = false;
  if (parsed.agentId !== undefined) {
    const agentId = assertGuid(parsed.agentId, "agentId");
    rows = [await withCategory(() => api.getRow("bots", agentId, select))];
  } else {
    const page = await withCategory(() =>
      api.queryRows("bots", { select, orderBy: "name asc", top: 500 }),
    );
    rows = page.value.slice(0, 500);
    incomplete = page.nextLink !== undefined || page.value.length > 500;
  }

  if (rows.length === 0) {
    if (incomplete) {
      return "Dataverse returned an empty first agent page plus a continuation. Access audit is incomplete; no conclusion about the absence of visible agents can be issued.";
    }
    return "No Copilot Studio agents are visible to you in this environment.";
  }

  const lines: string[] = [];
  let exposed = 0;
  for (const row of rows) {
    const published = str(row, "publishedon") !== undefined;
    const mode = num(row, "authenticationmode");
    const policy = num(row, "accesscontrolpolicy");
    const groups = str(row, "authorizedsecuritygroupids") ?? "";
    const findings = auditAgentAccess({
      ...(mode !== undefined ? { authenticationmode: mode } : {}),
      ...(policy !== undefined ? { accesscontrolpolicy: policy } : {}),
      authorizedsecuritygroupids: groups,
      published,
    });
    if (findings.length > 0) exposed += 1;

    lines.push(`${str(row, "name") ?? "(unnamed)"}  [${str(row, "botid") ?? "?"}]`);
    lines.push(`  authentication: ${AUTH_MODE_LABELS[mode ?? -1] ?? `mode ${mode}`}`);
    lines.push(`  who may use it: ${ACCESS_POLICY_LABELS[policy ?? -1] ?? `policy ${policy}`}`);
    const trigger = num(row, "authenticationtrigger");
    if (trigger !== undefined)
      lines.push(`  sign-in prompt: ${AUTH_TRIGGER_LABELS[trigger] ?? trigger}`);
    if (groups !== "") lines.push(`  authorized groups: ${groups}`);
    lines.push(`  published: ${published ? str(row, "publishedon") : "never"}`);
    findings.forEach((f) => lines.push(`  ⚠ ${f}`));
    lines.push("");
  }

  const header =
    incomplete
      ? [`Audited the first ${rows.length} visible agents — ${exposed} with something worth looking at.`, ""]
      : rows.length === 1
      ? []
      : [`Audited ${rows.length} agents — ${exposed} with something worth looking at.`, ""];
  return [
    ...header,
    ...lines,
    ...(incomplete
      ? ["⚠ Access audit is incomplete: this report covers only the first page (at most 500 agents). Additional visible agents were not inspected.", ""]
      : []),
    "This reflects only the agents Dataverse lets you see: agents owned by other people are invisible unless shared with you, so this is not a tenant-wide audit.",
  ].join("\n");
}

// =============================================================================
// chat_with_copilot_agent
// =============================================================================

/** Hosts this tool is allowed to talk to. The endpoint host is returned by
 * Dataverse (a Power Platform gateway) and the conversation host is Direct
 * Line itself — anything else means the platform handed us something
 * unexpected, and we stop rather than post a token at it. */
const DIRECTLINE_HOST = "directline.botframework.com";
const ALLOWED_ENDPOINT_HOST_SUFFIXES = [
  ".powerapps.com",
  ".powerva.microsoft.com",
  ".microsoft.com",
] as const;

function assertAllowedEndpointHost(endpoint: string): URL {
  let url: URL;
  try {
    url = new URL(endpoint);
  } catch {
    throw new ValidationError(
      `Copilot Studio returned an endpoint that is not a URL: ${endpoint.slice(0, 80)}`,
    );
  }
  if (url.protocol !== "https:") {
    throw new ValidationError(`Refusing a non-HTTPS agent endpoint: ${url.protocol}//${url.host}`);
  }
  const host = url.hostname.toLowerCase();
  const allowed = ALLOWED_ENDPOINT_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix));
  if (!allowed) {
    throw new ValidationError(
      `Refusing to send a conversation token to an unexpected host: ${host}. Expected a Microsoft Power Platform gateway.`,
    );
  }
  return url;
}

/**
 * Turn the two runtime error codes this channel actually produces into plain
 * English. Both were reproduced live, and together they describe a real
 * either/or that the error codes alone do not:
 *
 *   authentication None      → Direct Line chat works, but generative answers
 *                              over knowledge fail (AuthenticationNotConfigured)
 *   authentication Integrated → knowledge can work, but Direct Line refuses the
 *                              conversation entirely
 *                              (IntegratedAuthenticationNotSupportedInChannel)
 *
 * These are Direct Line limitations, not limitations of every MCP execution
 * surface. Integrated agents can use execute_copilot_agent after consent.
 */
export function explainAgentRuntimeError(replyText: string): string | undefined {
  if (replyText.includes("AuthenticationNotConfigured")) {
    return [
      "What that means: the agent has no authentication configured, and answering from knowledge sources requires it.",
      "Integrated (Microsoft Entra) authentication is not supported by this Direct Line channel. Use execute_copilot_agent with delegated CopilotStudio.Copilots.Invoke consent, or Copilot Studio's own test pane, to test an Integrated-auth agent.",
    ].join("\n");
  }
  if (replyText.includes("IntegratedAuthenticationNotSupportedInChannel")) {
    return [
      "What that means: the agent uses Integrated (Microsoft Entra) authentication, which this conversation channel does not support — it carries no signed-in user.",
      "Use execute_copilot_agent with delegated CopilotStudio.Copilots.Invoke consent, or Copilot Studio's test pane or another channel that carries user identity. Agent execution may invoke tools with external side effects.",
    ].join("\n");
  }
  if (replyText.includes("LatestPublishedVersionNotFound")) {
    return [
      "What that means: this agent has never been published, so there is no version for the channel to run. Nothing is wrong with its content.",
      "Call publish_copilot_agent and wait for it to finish — publishing takes a minute or two — then try again. An agent restored from a solution import is unpublished even though its components are all present.",
    ].join("\n");
  }
  return undefined;
}

export const chatWithCopilotAgentInputSchema = z
  .object({
    agentId: z.string().min(1),
    message: z.string().min(1).max(4000).optional(),
    messages: z.array(z.string().min(1).max(4000)).min(1).max(20).optional(),
    waitSeconds: z.number().int().min(1).max(60).optional().default(15),
  })
  .refine((v) => (v.message === undefined) !== (v.messages === undefined), {
    message: "Pass exactly one of message (a single turn) or messages (a scripted conversation).",
  });

export const chatWithCopilotAgentTool = {
  name: "chat_with_copilot_agent",
  description:
    "Talk to a published Copilot Studio agent through Direct Line and return its replies. Integrated/Microsoft Entra agents must use execute_copilot_agent instead. Pass `message` for a single turn, or `messages` for a scripted conversation where every turn runs in the SAME Direct Line conversation. Each call is a fresh conversation; use execute_copilot_agent and its conversationId for authenticated continuation across calls. Requires a Copilot Studio licence on the signed-in user. Agent execution may invoke tools with external side effects.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: { type: "string", description: "The agent's botid (GUID)" },
      message: { type: "string", description: "A single message to send (use this OR messages)" },
      messages: {
        type: "array",
        items: { type: "string" },
        description:
          "A scripted conversation: each entry is one user turn, sent in order in one conversation, waiting for the agent between turns",
      },
      waitSeconds: {
        type: "number",
        description: "How long to wait for replies before returning (default 15, max 60)",
      },
    },
    required: ["agentId"],
    oneOf: [
      { required: ["message"], not: { required: ["messages"] } },
      { required: ["messages"], not: { required: ["message"] } },
    ],
  },
};

interface DirectLineActivity {
  id?: string;
  type?: string;
  text?: string;
  from?: { id?: string };
  suggestedActions?: { actions?: { title?: string }[] };
}

export async function handleChatWithCopilotAgent(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = chatWithCopilotAgentInputSchema.parse(input);
  const api = requireApi(context.api);
  const agentId = assertGuid(parsed.agentId, "agentId");
  logger.info({ agentId }, "Executing chat_with_copilot_agent");

  // Direct Line carries no signed-in user, so an agent set to Integrated
  // (Entra) authentication refuses every conversation with
  // `IntegratedAuthenticationNotSupportedInChannel`. That is worth knowing
  // BEFORE opening a conversation and waiting on it. Deliberately narrow:
  // only Integrated is refused here, because that is the mode whose failure
  // was observed live — any other mode is left to the runtime to answer.
  // Fail-soft: this check exists only to save a doomed round trip, so if the
  // read itself does not work the real attempt still goes ahead. A pre-check
  // that cannot read must never be the thing that blocks the call.
  const authRow = await (async () => {
    try {
      return await api.getRow("bots", agentId, "authenticationmode,schemaname");
    } catch {
      return undefined;
    }
  })();
  if (authRow !== undefined && num(authRow, "authenticationmode") === 2) {
    const schemaName = str(authRow, "schemaname") ?? "<agentSchemaName>";
    return (
      "This agent uses Integrated (Microsoft Entra) authentication, which this Direct Line tool cannot test.\n\n" +
      "Direct Line — the channel this tool uses — carries no signed-in user, so the service refuses the conversation with " +
      "`IntegratedAuthenticationNotSupportedInChannel`. Verified live: the refusal is identical with and without an Entra bearer " +
      "token on the token request, so it is a property of the channel, not something a better token fixes.\n\n" +
      "Use execute_copilot_agent with agentSchemaName " +
      JSON.stringify(schemaName) +
      " and a message to run a published turn as the signed-in user. Supply the agent's environmentId if it differs from the current environment. " +
      "This is a separate explicit action: execution may invoke connectors, flows, or MCP tools with external side effects. " +
      "Nothing was executed by this refused Direct Line call.\n\n" +
      "The authenticated tool uses the direct-to-engine endpoint:\n" +
      `  POST https://{environment}.environment.api.powerplatform.com/copilotstudio/dataverse-backed/authenticated/bots/${schemaName}/conversations\n` +
      "That route needs a delegated `CopilotStudio.Copilots.Invoke` token — an app registration carrying that permission, with admin " +
      "consent, followed by a fresh signed-in session. `CopilotStudio.Copilots.Test` is not sufficient for that route. " +
      "You can also use Copilot Studio's test pane or a configured identity-aware channel such as Teams.\n\n" +
      "Tools configured as `Invoker` use the signed-in user's own connection; list_copilot_agent_tools reports the tool's connection mode."
    );
  }

  // 1. Ask Dataverse where this agent's conversation endpoint is.
  const endpointResult = await withCategory(() =>
    api.executeBoundAction<Record<string, unknown>>(
      "bots",
      agentId,
      "Microsoft.Dynamics.CRM.PvaGetDirectLineEndpoint",
      {},
    ),
  );
  const endpoint =
    typeof endpointResult["Endpoint"] === "string" ? endpointResult["Endpoint"] : undefined;
  if (endpoint === undefined) {
    throw new ValidationError(
      "Copilot Studio did not return a Direct Line endpoint for this agent. Publish the agent first (publish_copilot_agent).",
    );
  }
  const endpointUrl = assertAllowedEndpointHost(endpoint);

  // 2. Exchange it for a Direct Line token.
  const tokenResponse = await fetch(endpointUrl, { method: "GET" });
  if (!tokenResponse.ok) {
    throw new ValidationError(
      `Could not get a conversation token (HTTP ${tokenResponse.status}). An agent that has never been published cannot be talked to.`,
    );
  }
  const tokenBody = (await tokenResponse.json()) as { token?: string };
  const token = tokenBody.token;
  if (typeof token !== "string" || token === "") {
    throw new ValidationError("The conversation-token endpoint returned no token.");
  }
  const auth = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  // 3. Start a conversation and say the thing.
  const convResponse = await fetch(`https://${DIRECTLINE_HOST}/v3/directline/conversations`, {
    method: "POST",
    headers: auth,
  });
  if (!convResponse.ok) {
    throw new ValidationError(
      `Direct Line refused to start a conversation (HTTP ${convResponse.status}).`,
    );
  }
  const conversation = (await convResponse.json()) as { conversationId?: string };
  const conversationId = conversation.conversationId;
  if (typeof conversationId !== "string" || conversationId === "") {
    throw new ValidationError("Direct Line did not return a conversation id.");
  }
  const activitiesUrl = `https://${DIRECTLINE_HOST}/v3/directline/conversations/${encodeURIComponent(conversationId)}/activities`;

  const turns = parsed.messages ?? [parsed.message ?? ""];

  // Direct Line hands back a `watermark` with every activity read; passing it
  // on the next read returns only what arrived since. That is what makes a
  // multi-turn script possible — without it every turn re-reads the whole
  // conversation and the agent's earlier replies masquerade as new ones.
  let watermark: string | undefined;
  const transcript: string[] = [];
  const allReplies: string[] = [];

  for (const text of turns) {
    const sendResponse = await fetch(activitiesUrl, {
      method: "POST",
      headers: auth,
      body: JSON.stringify({ type: "message", from: { id: "mcp-user" }, text }),
    });
    if (!sendResponse.ok) {
      throw new ValidationError(`Direct Line refused the message (HTTP ${sendResponse.status}).`);
    }
    // Direct Line echoes our own message back in the activity list and does NOT
    // preserve the `from.id` we sent, so filtering by sender reports the user's
    // own words as the agent's reply (observed live). The returned activity id
    // is the reliable way to exclude it.
    const sentActivity = (await sendResponse.json()) as { id?: string };
    const sentActivityId = typeof sentActivity.id === "string" ? sentActivity.id : undefined;

    const deadline = Date.now() + parsed.waitSeconds * 1000;
    let replies: string[] = [];
    let suggestions: string[] = [];
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      const url =
        watermark === undefined
          ? activitiesUrl
          : `${activitiesUrl}?watermark=${encodeURIComponent(watermark)}`;
      const readResponse = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!readResponse.ok) continue;
      const payload = (await readResponse.json()) as {
        activities?: DirectLineActivity[];
        watermark?: string;
      };
      const activities = payload.activities ?? [];
      const fresh = activities.filter(
        (a) =>
          a.id !== sentActivityId &&
          a.from?.id !== "mcp-user" &&
          typeof a.text === "string" &&
          a.text.trim() !== "" &&
          a.text.trim() !== text.trim(),
      );
      if (fresh.length > 0) {
        replies = fresh.map((a) => (a.text ?? "").trim());
        suggestions = activities
          .flatMap((a) => a.suggestedActions?.actions ?? [])
          .map((a) => a.title ?? "")
          .filter((t) => t !== "");
        if (typeof payload.watermark === "string") watermark = payload.watermark;
        break;
      }
      if (typeof payload.watermark === "string") watermark = payload.watermark;
    }

    transcript.push(`You: ${text}`);
    if (replies.length === 0) {
      transcript.push(`Agent: (nothing within ${parsed.waitSeconds}s)`);
    } else {
      replies.forEach((reply) => transcript.push(`Agent: ${reply}`));
      if (suggestions.length > 0)
        transcript.push(`  suggested replies: ${suggestions.join(" | ")}`);
    }
    transcript.push("");
    allReplies.push(...replies);
  }

  const lines = [...transcript];
  const explanation = explainAgentRuntimeError(allReplies.join(" "));
  if (allReplies.length === 0) {
    lines.push(
      `The agent sent nothing back within ${parsed.waitSeconds}s.`,
      "",
      "That usually means the agent is published but has no topic matching this message and no generative answers configured — or it is still warming up. Try again, or ask something its topics cover.",
    );
  } else if (explanation !== undefined) {
    lines.push(explanation, "");
  }
  lines.push(
    turns.length > 1
      ? `(${turns.length} turns in one conversation. Each call starts a fresh conversation, so the first turn is a cold start.)`
      : "(Each call starts a fresh conversation — this tests a cold start, not a continuing thread.)",
  );
  return lines.join("\n");
}

// =============================================================================
// share_copilot_agent
// =============================================================================

export const shareCopilotAgentInputSchema = z.object({
  agentId: z.string().min(1),
  userId: z
    .string()
    .min(1)
    .describe("A Dataverse systemuserid, an Entra object id, or the user's email address"),
  accessLevel: z.enum(["read", "edit"]).optional().default("read"),
});

export const shareCopilotAgentTool = {
  name: "share_copilot_agent",
  description:
    "Grant a user direct Dataverse read access to a Copilot Studio agent and every one of its components. This does not assign the environment-level Agent Viewer/ChatBot Reader role that the Copilot Studio UI may require. edit is retained as an input for compatibility but is rejected: UI-equivalent coauthor access also requires Environment Maker and cannot be safely represented by row grants alone.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: { type: "string", description: "The agent's botid (GUID)" },
      userId: {
        type: "string",
        description:
          "Who to share with: a Dataverse systemuserid, an Entra object id, or the user's email address",
      },
      accessLevel: {
        type: "string",
        enum: ["read", "edit"],
        description:
          "read = Dataverse row read grant (default); edit = rejected because UI-equivalent coauthor access also requires Environment Maker",
      },
    },
    required: ["agentId", "userId"],
  },
};

/**
 * GrantAccess wants a Dataverse `systemuserid`, but people have Entra object
 * ids and email addresses — and the two GUIDs are different values for the
 * same person. Seen live: sharing with the owner's Entra object id failed with
 * "Entity 'systemuser' With Id = … Does Not Exist". Accept any of the three.
 */
export async function resolveSystemUser(
  api: DataverseApi,
  userRef: string,
): Promise<{
  systemuserid: string;
  label: string;
  via: "systemuserid" | "entraObjectId" | "email";
}> {
  const ref = userRef.trim();
  const isGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(ref);
  const isEmail = /^[^\s@']+@[^\s@']+\.[^\s@']+$/.test(ref);
  if (!isGuid && !isEmail) {
    throw new ValidationError(
      "userId must be a Dataverse systemuserid, an Entra object id (GUID), or the user's email address.",
    );
  }
  const filter = isGuid
    ? `systemuserid eq ${ref} or azureactivedirectoryobjectid eq ${ref}`
    : `internalemailaddress eq '${ref}' or domainname eq '${ref}'`;
  let rows: Row[];
  try {
    rows = (
      await api.queryRows("systemusers", {
        select: "systemuserid,fullname,internalemailaddress,azureactivedirectoryobjectid",
        filter,
        top: 2,
      })
    ).value;
  } catch (err) {
    // No permission to read systemuser (or a stub that does not model it): a GUID
    // can still be tried as-is, which is what this tool always did.
    if (isGuid) return { systemuserid: ref, label: ref, via: "systemuserid" };
    throw err;
  }
  const row = rows[0];
  if (row === undefined) {
    if (isGuid) return { systemuserid: ref, label: ref, via: "systemuserid" };
    throw new ValidationError(
      `No Dataverse user has the email address ${ref} in this environment. Users appear here only after signing in to this environment at least once (or being assigned a licence and security role).`,
    );
  }
  if (rows.length > 1) {
    throw new ValidationError(
      `The user reference ${ref} matched multiple Dataverse users. Sharing was refused before any grant; use a unique email address or GUID and verify the intended recipient.`,
    );
  }
  const systemuserid = str(row, "systemuserid") ?? ref;
  const label = str(row, "fullname") ?? str(row, "internalemailaddress") ?? systemuserid;
  const via: "systemuserid" | "entraObjectId" | "email" = isEmail
    ? "email"
    : systemuserid.toLowerCase() === ref.toLowerCase()
      ? "systemuserid"
      : "entraObjectId";
  return { systemuserid, label, via };
}

export async function handleShareCopilotAgent(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = shareCopilotAgentInputSchema.parse(input);
  if (parsed.accessLevel === "edit") {
    throw new ValidationError(
      "Copilot Studio edit sharing is not a simple Dataverse row grant: a coauthor also needs the Environment Maker role. This tool will not silently grant incomplete or environment-wide access; use Copilot Studio sharing until role-aware coauthor provisioning is implemented.",
    );
  }
  const api = requireApi(context.api);
  const agentId = assertGuid(parsed.agentId, "agentId");
  const resolved = await resolveSystemUser(api, parsed.userId);
  const userId = resolved.systemuserid;
  const accessMask = "ReadAccess";
  logger.info(
    { agentId, userId, via: resolved.via, accessLevel: parsed.accessLevel },
    "Executing share_copilot_agent",
  );
  const recipient = resolved.label === userId ? userId : `${resolved.label} (${userId})`;

  // Sharing the bot row first and discovering afterwards that its component
  // query failed (or was paged) leaves a recipient with a shell agent and no
  // topics. Establish that the complete fan-out set fits in this bounded
  // operation before granting anything.
  const componentPage = await withCategory(() =>
    api.queryRows("botcomponents", {
      select: "botcomponentid",
      filter: `_parentbotid_value eq ${agentId}`,
      top: 5000,
    }),
  );
  if (componentPage.nextLink !== undefined) {
    throw new ValidationError(
      "This agent has more than 5,000 components. Sharing was refused before any grant because a one-page fan-out would leave the recipient with an incomplete agent.",
    );
  }
  const components = componentPage.value;

  const grant = async (entity: "bot" | "botcomponent", id: string): Promise<void> => {
    const idField = entity === "bot" ? "botid" : "botcomponentid";
    await api.executeUnboundAction("GrantAccess", {
      Target: { [idField]: id, "@odata.type": `Microsoft.Dynamics.CRM.${entity}` },
      PrincipalAccess: {
        Principal: { systemuserid: userId, "@odata.type": "Microsoft.Dynamics.CRM.systemuser" },
        AccessMask: accessMask,
      },
    });
  };

  await withCategory(() => grant("bot", agentId));

  let shared = 0;
  const failures: string[] = [];
  for (const component of components) {
    const id = str(component, "botcomponentid");
    if (!id) continue;
    try {
      await grant("botcomponent", id);
      shared += 1;
    } catch (err) {
      const reason =
        safePublishText(err instanceof Error ? err.message : String(err), 120) ?? "unknown error";
      failures.push(`${id}: ${reason}`);
    }
  }

  const lines = [
    `Granted Dataverse read access to the agent and ${shared} of ${components.length} components for ${recipient}.`,
    "The recipient must also have the environment-level Agent Viewer/ChatBot Reader role to use Copilot Studio; this tool does not assign environment roles.",
  ];
  if (failures.length > 0) {
    lines.push(
      "",
      `⚠ ${failures.length} component grants failed — the recipient will see an incomplete agent:`,
    );
    failures.slice(0, 5).forEach((f) => lines.push(`  • ${f.slice(0, 160)}`));
    if (failures.length > 5) lines.push(`  … and ${failures.length - 5} more`);
  }
  return lines.join("\n");
}

// =============================================================================
// delete_copilot_agent
// =============================================================================

export const deleteCopilotAgentInputSchema = z.object({
  agentId: z.string().min(1),
  confirm: z.boolean().optional(),
});

export const deleteCopilotAgentTool = {
  name: "delete_copilot_agent",
  description:
    "Delete a Copilot Studio agent and all of its components. Dataverse refuses to delete an agent that still has components (foreign key botcomponent_parent_bot), so this removes them first. Destructive and irreversible — requires confirm.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: { type: "string", description: "The agent's botid (GUID)" },
      confirm: { type: "boolean", description: "Must be true to actually delete" },
    },
    required: ["agentId"],
  },
};

export async function handleDeleteCopilotAgent(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = deleteCopilotAgentInputSchema.parse(input);
  const api = requireApi(context.api);
  const agentId = assertGuid(parsed.agentId, "agentId");

  const bot = await withCategory(() => api.getRow("bots", agentId, "name"));
  const componentPage = await withCategory(() =>
    api.queryRows("botcomponents", {
      select: "botcomponentid",
      filter: `_parentbotid_value eq ${agentId}`,
      top: 5000,
    }),
  );
  const components = componentPage.value;
  const componentCount = `${components.length}${componentPage.nextLink === undefined ? "" : "+"}`;

  if (!parsed.confirm) {
    throw new ValidationError(
      `Not confirmed. This deletes the agent "${str(bot, "name") ?? agentId}" (${agentId}) and its ${componentCount} components permanently${componentPage.nextLink === undefined ? "" : " (the exact count exceeds one Dataverse page)"}. Call again with confirm: true.`,
    );
  }

  logger.info({ agentId, components: components.length }, "Executing delete_copilot_agent");

  // Prefer the platform's own delete: PvaDeleteBot removes the agent AND
  // cascades to its components in one call (verified live — an agent with 15
  // components left nothing behind). The manual path below is the fallback,
  // and it must go bottom-up because the botcomponent_parent_bot foreign key
  // refuses to delete an agent that still has components.
  try {
    await api.executeBoundAction("bots", agentId, "Microsoft.Dynamics.CRM.PvaDeleteBot", {});
    return `Deleted agent "${str(bot, "name") ?? agentId}" and its ${componentCount} components.`;
  } catch (err) {
    logger.warn(
      { agentId, err: err instanceof Error ? err.message : String(err) },
      "PvaDeleteBot failed",
    );
    if (componentPage.nextLink !== undefined) {
      const reason = sanitizeErrorMessage(err instanceof Error ? err.message : String(err))
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 500);
      throw new ValidationError(
        `Copilot Studio's cascade delete failed${reason ? ` (${reason})` : ""}. The agent has more than 5,000 components, so the component-by-component fallback was refused before deleting any rows; an incomplete first-page fallback would partially destroy the agent.`,
      );
    }
  }

  let removed = 0;
  for (const component of components) {
    const id = str(component, "botcomponentid");
    if (!id) continue;
    await withCategory(() => api.deleteRow("botcomponents", id));
    removed += 1;
  }
  await withCategory(() => api.deleteRow("bots", agentId));

  return `Deleted agent "${str(bot, "name") ?? agentId}" and its ${removed} components (component-by-component fallback).`;
}

// =============================================================================
// validate_copilot_agent
// =============================================================================

export const validateCopilotAgentInputSchema = z.object({
  agentId: z.string().min(1),
});

export const listCopilotAgentToolsInputSchema = z.object({
  agentId: z.string().describe("The agent's botid (GUID)"),
  checkConnections: z
    .boolean()
    .optional()
    .default(true)
    .describe(
      "Resolve up to 50 distinct complete logical references in Dataverse, then inspect physical connection status metadata (default true). Does not verify component associations, Invoker bindings or runtime access.",
    ),
});

export type ListCopilotAgentToolsInput = z.infer<typeof listCopilotAgentToolsInputSchema>;

export const listCopilotAgentToolsTool = {
  name: "list_copilot_agent_tools",
  description:
    "List what a Copilot Studio agent can actually DO — its tools (MCP servers, connector operations, cloud flows, other agents) rather than its conversational topics. " +
    "Tools are botcomponent rows of the same type as topics, so a plain component list buries them; this reports each tool's model-facing description (the text the orchestrator " +
    "matches on), the operation it calls, credential mode, the complete logical reference and resolved physical connection metadata. Never infers a connection ID from a logical-name suffix. Reads at most 50 distinct references; status metadata is not component-association, Invoker-binding or runtime verification.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: { type: "string", description: "The agent's botid (GUID)" },
      checkConnections: {
        type: "boolean",
        description:
          "Resolve up to 50 complete logical references and inspect physical status metadata (default true), without runtime or association verification.",
      },
    },
    required: ["agentId"],
  },
};

export async function handleListCopilotAgentTools(
  context: { api: DataverseApi | null; connectorApi?: ConnectorMetadataApi | null },
  input: unknown,
): Promise<string> {
  const parsed = listCopilotAgentToolsInputSchema.parse(input);
  const api = requireApi(context.api);
  const agentId = assertGuid(parsed.agentId, "agentId");

  const componentPage = await withCategory(() =>
    api.queryRows("botcomponents", {
      select: "botcomponentid,name,componenttype,schemaname,modifiedon,data",
      filter: `_parentbotid_value eq ${agentId}`,
      top: 5000,
    }),
  );
  const components = componentPage.value;
  const componentsIncomplete = componentPage.nextLink !== undefined;

  // Latest tool changes reach published channels only after agent publication.
  // modifiedon cannot distinguish a newly added tool from an edit to an
  // existing published tool; componentstate describes the solution layer.
  const publishedOn = await (async () => {
    try {
      const row = await api.getRow("bots", agentId, "publishedon");
      return str(row, "publishedon");
    } catch {
      return undefined;
    }
  })();

  const tools = components.filter((row) =>
    isAgentToolComponent({
      componentType: num(row, "componenttype"),
      data: str(row, "data"),
      schemaName: str(row, "schemaname"),
    }),
  );

  if (tools.length === 0) {
    if (components.length === 0) await assertAgentExists(api, agentId);
    if (componentsIncomplete) {
      return (
        `No tools were found in the first ${components.length} components, but Dataverse returned a continuation, so this is not a complete-agent result.\n\n` +
        "Inspect a known component with get_copilot_agent_component or narrow the agent before concluding it has no tools."
      );
    }
    return (
      `This agent has no tools — ${components.length} component(s), all topics/knowledge/settings.\n\n` +
      "Give it one with add_copilot_agent_tool (a connector operation, a cloud flow, an MCP server, or another agent). " +
      "Note that a tool only fires when generative orchestration is on — check configure_copilot_agent."
    );
  }

  const referenceNames = tools.flatMap((row) => extractConnectionReferences(str(row, "data")).map((ref) => ref.referenceName));
  const connectionDetails = await toolConnectionDiagnosticLines(api, context.connectorApi, referenceNames, parsed.checkConnections);

  const lines = [
    componentsIncomplete
      ? `Tools found in the first ${components.length} components (${tools.length}; more components were not inspected):`
      : `Tools on this agent (${tools.length}):`,
    "",
  ];
  const problems: string[] = [];

  for (const row of tools) {
    const name = str(row, "name") ?? "(unnamed)";
    const data = str(row, "data");
    const summary = summarizeAgentTool(data);
    const kind = describeComponentKind({
      componentType: num(row, "componenttype"),
      data,
      schemaName: str(row, "schemaname"),
    });

    lines.push(`  ${name}  — ${kind}`);
    lines.push(`    id: ${str(row, "botcomponentid") ?? "?"}`);
    if (summary.modelDisplayName !== undefined && summary.modelDisplayName !== name) {
      lines.push(`    the model sees it as: ${summary.modelDisplayName}`);
    }
    if (summary.modelDescription !== undefined) {
      lines.push(`    model description: ${summary.modelDescription}`);
    } else if (summary.isMcp) {
      lines.push("    model description: supplied dynamically by the MCP server");
    } else {
      lines.push("    model description: (none)");
      problems.push(
        `${name} has no modelDescription — that text is how the model decides to call a tool, so it is unlikely to ever fire.`,
      );
    }
    if (summary.actionKind !== undefined) lines.push(`    action kind: ${summary.actionKind}`);
    if (summary.operationId !== undefined) lines.push(`    operation: ${summary.operationId}`);
    if (summary.flowId !== undefined) lines.push(`    flow: ${summary.flowId}`);
    if (summary.connectionMode !== undefined) {
      lines.push(
        `    runs as: ${summary.connectionMode}${summary.connectionMode === "Invoker" ? " (the signed-in user's connection)" : ""}`,
      );
    }

    if (summary.connectionReferenceName !== undefined) {
      for (const line of connectionDetails.get(summary.connectionReferenceName.toLowerCase()) ?? []) {
        lines.push(`    ${line}`);
      }
    }
    const modified = str(row, "modifiedon");
    if (publishedOn !== undefined && modified !== undefined && modified > publishedOn) {
      lines.push(`    ⚠ changed ${modified}, after the agent was last published (${publishedOn})`);
      problems.push(
        `${name}'s latest draft changes are not published. An earlier published version of this tool may still be available in Teams and the web channel. Review and test the draft changes before publishing with publish_copilot_agent.`,
      );
    }
    lines.push("");
  }

  if (problems.length > 0) {
    lines.push("Problems:");
    for (const problem of problems) lines.push(`  • ${problem}`);
    lines.push("");
  }
  if (componentsIncomplete) {
    lines.push(
      "⚠ Dataverse returned a component continuation. This report covers only the first 5,000 components; tool counts and problem checks are incomplete.",
      "",
    );
  }
  lines.push(
    "A tool only fires when the agent uses generative orchestration and the model description matches the user's intent. If a tool here looks right but never runs, check configure_copilot_agent.",
  );

  return lines.join("\n");
}

export const validateCopilotAgentTool = {
  name: "validate_copilot_agent",
  description:
    "Lint an agent's components for the problems that are checkable outside the designer: missing or wrong 'kind:' headers, empty content, tabs in YAML, missing instructions. This is NOT a full validation — Dataverse stores component content as an opaque string and only the Copilot Studio designer and publish know the complete schema.",
  inputSchema: {
    type: "object" as const,
    properties: { agentId: { type: "string", description: "The agent's botid (GUID)" } },
    required: ["agentId"],
  },
};

export async function handleValidateCopilotAgent(
  context: { api: DataverseApi | null; connectorApi?: ConnectorMetadataApi | null },
  input: unknown,
): Promise<string> {
  const parsed = validateCopilotAgentInputSchema.parse(input);
  const api = requireApi(context.api);
  const agentId = assertGuid(parsed.agentId, "agentId");
  const botRow = await withCategory(() => api.getRow("bots", agentId, "authenticationmode"));
  // 1 = None. Anything else (Integrated, Manual) means OAuthInput can publish.
  const authMode = num(botRow, "authenticationmode");
  const agentHasAuthentication = authMode !== undefined && authMode !== 1;

  const componentPage = await withCategory(() =>
    api.queryRows("botcomponents", {
      select: "botcomponentid,name,componenttype,schemaname,data",
      filter: `_parentbotid_value eq ${agentId}`,
      top: 5000,
    }),
  );
  const components = componentPage.value;
  const componentsIncomplete = componentPage.nextLink !== undefined;

  if (components.length === 0) {
    if (componentsIncomplete) {
      return "Dataverse returned an empty first component page plus a continuation. Validation is incomplete; no clean verdict can be issued.";
    }
    return "This agent has no components. If it was just created, provisioning is asynchronous — try again in a minute.";
  }

  const findings: string[] = [];
  let fileAttachmentsNotValidated = 0;
  let yamlLintedComponents = 0;
  for (const component of components) {
    const type = num(component, "componenttype");
    const data = str(component, "data") ?? "";
    // File attachments store their bytes in filedata, not YAML in data.
    // This metadata-only query does not validate the attachment or ingestion.
    if (type === COMPONENT_TYPES.fileAttachment) {
      fileAttachmentsNotValidated += 1;
      continue;
    }
    if (type === undefined || !Number.isInteger(type) || type < 0) {
      findings.push(
        `  ${str(component, "name") ?? "(unnamed)"} — component type unavailable or invalid [${str(component, "botcomponentid") ?? "?"}]`,
        "    • source was not linted because the component type could not be established",
      );
      continue;
    }
    yamlLintedComponents += 1;
    let problems = lintComponent(type, data);
    // The content lint cannot know the agent's authentication mode, so it
    // notes that OAuthInput needs one. Here the agent IS known: drop the note
    // when authentication is configured, so a real agent lints clean.
    // The platform's own provisioned "Sign in" topic (…topic.Signin) carries an
    // OAuthInput too, and it publishes fine on an agent without authentication —
    // verified live — so the note is a false positive there.
    const isPlatformSignin = (str(component, "schemaname") ?? "").endsWith(".topic.Signin");
    if (agentHasAuthentication || isPlatformSignin) {
      problems = problems.filter((p) => !p.includes("AuthenticationInputNotEnabled"));
    }
    if (problems.length > 0) {
      findings.push(
        `  ${str(component, "name") ?? "(unnamed)"} — ${componentTypeLabel(type)} [${str(component, "botcomponentid") ?? "?"}]`,
      );
      problems.forEach((p) => findings.push(`    • ${p}`));
    }
  }

  // Flow references are the #1 cause of a failed publish, and the failure is
  // invisible until you publish: an agent-callable flow must exist in the
  // environment as a real cloud flow. Verified live — a topic pointing at a
  // non-solution flow (no Dataverse workflow row at all) and a topic pointing
  // at a hand-created workflow row (a row the Power Automate service does not
  // know about) BOTH fail publish with `NotFound: CloudFlow … not found`.
  const flowRefs = new Map<string, string[]>(); // flowId -> component names
  // Logical names are stable identities; their suffixes are never physical IDs.
  const connectionRefs = new Map<string, { referenceName: string; usedBy: string[] }>();
  for (const component of components) {
    if (num(component, "componenttype") === COMPONENT_TYPES.fileAttachment) continue;
    const name = str(component, "name") ?? "(unnamed)";
    for (const flowId of extractFlowReferences(str(component, "data"))) {
      flowRefs.set(flowId, [...(flowRefs.get(flowId) ?? []), name]);
    }
    for (const reference of extractConnectionReferences(str(component, "data"))) {
      const key = reference.referenceName.toLowerCase();
      const existing = connectionRefs.get(key);
      connectionRefs.set(key, { referenceName: reference.referenceName, usedBy: [...(existing?.usedBy ?? []), name] });
    }
  }

  const unresolvable: string[] = [];
  const unverified: string[] = [];
  let confirmedPresent = 0;
  if (flowRefs.size > 0) {
    for (const [flowId, usedBy] of flowRefs) {
      try {
        const page = await api.queryRows("workflows", {
          select: "workflowid,name,statecode",
          filter: `workflowid eq ${flowId}`,
          top: 1,
        });
        const found = page.value;
        if (Array.isArray(found) && found.length === 1 && found[0] !== null &&
            typeof found[0] === "object" && !Array.isArray(found[0]) &&
            str(found[0], "workflowid")?.toLowerCase() === flowId.toLowerCase()) {
          // An exact visible row proves presence even when a continuation
          // exists; an empty page cannot prove absence until it is complete.
          confirmedPresent += 1;
        } else if (Array.isArray(found) && found.length === 0 && page.nextLink === undefined) {
          unresolvable.push(`${flowId} — referenced by ${usedBy.join(", ")}`);
        } else {
          const reason = Array.isArray(found) && found.length === 0
            ? "empty lookup page has a continuation"
            : "lookup did not return one matching workflow identity";
          unverified.push(`${flowId} — ${reason}; referenced by ${usedBy.join(", ")}`);
        }
      } catch (error) {
        // Failed reads are unknown, never evidence of presence or absence.
        // Only typed status codes are safe to expose; not raw messages/bodies.
        const status = error instanceof PowerAutomateError ? error.statusCode : undefined;
        const reason = status === 401 ? "HTTP 401: authentication failed"
          : status === 403 ? "HTTP 403: access denied"
            : status === 429 ? "HTTP 429: throttled"
              : status !== undefined && Number.isInteger(status) && status >= 400 && status <= 599
                ? `HTTP ${status}` : "status unavailable";
        unverified.push(`${flowId} — lookup failed (${reason}); referenced by ${usedBy.join(", ")}`);
      }
    }
  }

  const connectionDetails = await toolConnectionDiagnosticLines(api, context.connectorApi,
    [...connectionRefs.values()].map((ref) => ref.referenceName), true);

  const hasInstructions = components.some(
    (c) => num(c, "componenttype") === COMPONENT_TYPES.customGpt,
  );
  const lines = [
    componentsIncomplete
      ? `Checked the first ${components.length} components; more were not inspected.`
      : `Checked ${components.length} components.`,
    "",
  ];
  if (fileAttachmentsNotValidated > 0) {
    lines.push(
      `YAML-linted components: ${yamlLintedComponents}.`,
      `File attachments not validated: ${fileAttachmentsNotValidated} (type 14; not YAML source).`,
      "  Binary content, stored hash, ingestion, search indexing and readiness were not checked.",
      "",
    );
  }
  if (findings.length === 0) {
    lines.push(
      yamlLintedComponents === 0
        ? "No YAML components were linted; no structural clean verdict can be issued."
        : fileAttachmentsNotValidated > 0
          ? `No structural problems found in the ${yamlLintedComponents} YAML-linted components; file attachments remain unvalidated${componentsIncomplete ? " and additional components were not inspected" : ""}.`
          : componentsIncomplete
            ? "No structural problems were found in the inspected page; this is not a complete-agent verdict."
            : "No structural problems found.",
    );
  } else {
    lines.push("Problems:", ...findings);
  }
  if (flowRefs.size > 0) {
    lines.push(
      "",
      confirmedPresent === flowRefs.size
        ? `Flow references: ${flowRefs.size}, each present in Dataverse.`
        : `Flow references: ${flowRefs.size}; confirmed present in Dataverse: ${confirmedPresent}; not returned by a completed lookup: ${unresolvable.length}; unverified: ${unverified.length}.`,
      "  (Dataverse presence is necessary, not sufficient: a flow the Power Automate service does not serve still fails publish. Confirm the flow opens in Power Automate.)",
    );
  }
  if (unresolvable.length > 0) {
    lines.push(
      "",
      "⚠ Flow references not returned by completed Dataverse lookups (may FAIL the next publish):",
      ...unresolvable.map((u) => `  • ${u}`),
      "",
      "An agent-callable flow must exist as a real cloud flow in this environment, created through Power Automate (in a solution, with the 'When an agent calls the flow' trigger). A flow that is not in a solution has no Dataverse row for the agent to resolve.",
      "Dataverse visibility is caller-scoped; not returned is not proof of deletion. Verify access and the flow in Power Automate before publishing.",
    );
  }
  if (unverified.length > 0) {
    lines.push(
      "",
      "⚠ Flow-reference validation is incomplete. These references were not verified:",
      ...unverified.map((u) => `  • ${u}`),
      "No presence, absence, or publish-readiness conclusion can be drawn for these references. No alternate identity or authentication fallback was attempted.",
    );
  }
  if (connectionRefs.size > 0) {
    lines.push("", `Logical connection references: ${connectionRefs.size} (metadata only; no runtime or user-binding verification).`);
    for (const [key, reference] of connectionRefs) {
      for (const line of connectionDetails.get(key) ?? []) lines.push(`  ${line}`);
      lines.push(`    used by: ${reference.usedBy.join(", ")}`);
    }
  }
  if (!hasInstructions && !componentsIncomplete) {
    lines.push(
      "",
      "Note: this agent has no instructions component (set_copilot_agent_instructions).",
    );
  } else if (!hasInstructions) {
    lines.push(
      "",
      "No instructions component was found in the inspected page; additional components were not inspected.",
    );
  }
  if (componentsIncomplete) {
    lines.push(
      "",
      "⚠ Dataverse returned a component continuation. Lint, flow references, connection references, and instruction discovery above cover only the first 5,000 components.",
    );
  }
  lines.push(
    "",
    "This lint only covers what is checkable outside Copilot Studio. The designer and independent runtime checks provide separate evidence; no publication or runtime validation was performed.",
  );
  return lines.join("\n");
}
